import { useEffect, useMemo, useRef, useState } from 'react'
import './App.css'
import { installDomTranslator } from './i18nRuntime'
import { DE_UI_LITERALS, DE_UI_TEMPLATES } from './i18n/de'
import { EN_UI_LITERALS, EN_UI_TEMPLATES } from './i18n/en'
import { normalizeApiError } from './errorHandling'

const NAV_ITEMS = [
  { label: 'Prüfexport' },
  { label: 'Web-Upload' },
  { label: 'API-Keys' },
  { label: 'Administration' }
]

const TENANT_USER_RIGHTS_DEFAULT = {
  can_manage_companies: false,
  can_manage_sites: false,
  can_manage_pos: false,
  can_run_exports: false,
  can_manage_api_keys: false,
  can_manage_users: false,
  can_manage_impersonation: false,
}

const TENANT_USER_RIGHTS_OPTIONS = [
  { key: 'can_manage_companies', label: 'Unternehmen verwalten' },
  { key: 'can_manage_sites', label: 'Betriebsstätten verwalten' },
  { key: 'can_manage_pos', label: 'Kassen/POS verwalten' },
  { key: 'can_run_exports', label: 'Export durchführen' },
  { key: 'can_manage_api_keys', label: 'API-Keys verwalten' },
  { key: 'can_manage_users', label: 'Nutzerverwaltung' },
  { key: 'can_manage_impersonation', label: 'Impersonisation verwalten' }
]
const UI_SEARCH_THRESHOLD = 8
const UI_MULTISELECT_VISIBLE_ROWS = 3
const PASSKEY_DEFAULT_NAME = 'iShield Key 2 Pro'
const UPLOAD_FILE_LIMIT_BYTES = 2_000_000_000
const UPLOAD_FILE_LIMIT_LABEL = '2 GB'

const DE_ARTIFACT_TYPE_LABELS = {
  dsfinvk: 'DSFinV-K',
  tse_tar: 'TSE-TAR',
  procedure_doc: 'Verfahrensdoku'
}

const POS_TSE_TYPE_OPTIONS = [
  { value: 'swissbit_cloud_tse', label: 'Swissbit Cloud TSE' },
  { value: 'dn_tse_cloud', label: 'DN TSE Cloud' },
  { value: 'swissbit_hw_tse', label: 'Swissbit HW TSE' },
  { value: 'fiskaly_cloud_tse', label: 'fiskaly Cloud TSE' },
  { value: 'other', label: 'Andere' }
]
const detectBrowserUiLang = () => ((typeof navigator !== 'undefined' && String(navigator.language || '').toLowerCase().startsWith('de')) ? 'de' : 'en')
let CURRENT_UI_LITERALS = detectBrowserUiLang() === 'de' ? DE_UI_LITERALS : EN_UI_LITERALS
let CURRENT_UI_TEMPLATES = detectBrowserUiLang() === 'de' ? DE_UI_TEMPLATES : EN_UI_TEMPLATES
const setCurrentUiLang = (lang) => {
  const safe = String(lang || '').toLowerCase() === 'de' ? 'de' : 'en'
  CURRENT_UI_LITERALS = safe === 'de' ? DE_UI_LITERALS : EN_UI_LITERALS
  CURRENT_UI_TEMPLATES = safe === 'de' ? DE_UI_TEMPLATES : EN_UI_TEMPLATES
}

const ceilTwoDecimalsStrict = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return 0
  const scaled = num * 100
  const floored = Math.floor(scaled)
  // Keep exact two-decimal values stable; otherwise round strictly up.
  if (Math.abs(scaled - floored) < 1e-9) return floored / 100
  return (floored + 1) / 100
}

const formatDisplaySizeFromMb = (valueMb) => {
  const rawMb = Number(valueMb)
  if (!Number.isFinite(rawMb) || rawMb <= 0) {
    return { text: '0.00 MB' }
  }
  const useGb = rawMb >= 1024
  const rawValue = useGb ? (rawMb / 1024) : rawMb
  const rounded = ceilTwoDecimalsStrict(rawValue)
  return {
    text: `${rounded.toFixed(2)} ${useGb ? 'GB' : 'MB'}`
  }
}

const formatDisplaySizeFromBytes = (valueBytes) => {
  const rawBytes = Number(valueBytes)
  if (!Number.isFinite(rawBytes) || rawBytes < 0) return ''
  return formatDisplaySizeFromMb(rawBytes / 1000000).text
}

const uploadBlobWithProgress = ({ url, method = 'PUT', headers = {}, body, onProgress }) => (
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url, true)
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null && value !== '') {
        xhr.setRequestHeader(key, String(value))
      }
    })
    xhr.upload.onprogress = (event) => {
      if (typeof onProgress !== 'function') return
      if (event.lengthComputable && event.total > 0) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
        })
        return
      }
      onProgress({
        loaded: event.loaded,
        total: 0,
        percent: null
      })
    }
    xhr.onload = () => {
      let data = null
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        data = null
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status, data })
        return
      }
      reject(new Error(normalizeApiError(xhr.status, data, { uploadFileLimitLabel: UPLOAD_FILE_LIMIT_LABEL })))
    }
    xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload.'))
    xhr.onabort = () => reject(new Error('Upload abgebrochen.'))
    xhr.send(body)
  })
)

const sha256DigestHex = async (file) => {
  if (!globalThis.crypto?.subtle) {
    throw new Error('SHA-256 wird von diesem Browser nicht unterstützt.')
  }
  const buffer = await file.arrayBuffer()
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

const normalizePosTseType = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (POS_TSE_TYPE_OPTIONS.some((option) => option.value === normalized)) return normalized
  return 'swissbit_cloud_tse'
}

const getPosTseTypeLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  return POS_TSE_TYPE_OPTIONS.find((option) => option.value === normalized)?.label || String(value || '').trim() || '-'
}

const isSwissbitCloudPosTseType = (value) => ['swissbit_cloud_tse', 'dn_tse_cloud'].includes(normalizePosTseType(value))

const isFiskalyCloudPosTseType = (value) => normalizePosTseType(value) === 'fiskaly_cloud_tse'

const getArtifactTypeLabel = (artifactType, country) => {
  if (!artifactType) return '-'
  const normalizedCountry = String(country || '').trim().toUpperCase()
  if (normalizedCountry === 'DE') return DE_ARTIFACT_TYPE_LABELS[artifactType] || artifactType
  return artifactType
}

const uiLiteral = (label) => CURRENT_UI_LITERALS[label] || label

const formatUiTemplate = (key, vars = {}) => {
  const template = CURRENT_UI_TEMPLATES[key] || ''
  if (!template) return ''
  return String(template).replace(/\{\{(\w+)\}\}/g, (_, name) => String(vars?.[name] ?? ''))
}

const getDefaultUploadRange = () => {
  const now = new Date()
  const today = toIsoDate(now)
  return {
    from: today,
    to: today
  }
}

const formatDateDisplay = (value) => {
  if (!value) return '-'
  if (/^-?\d+$/.test(String(value).trim())) {
    const d = toDateTimeValue(value)
    if (!d || Number.isNaN(d.getTime())) return String(value)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}.${mm}.${yyyy}`
  }
  const raw = String(value)
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[3]}.${m[2]}.${m[1]}`
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

const toDateTimeValue = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value < 1000000000000 ? value * 1000 : value)
  }
  const raw = String(value).trim()
  if (/^-?\d+$/.test(raw)) {
    const numeric = Number(raw)
    return new Date(numeric < 1000000000000 ? numeric * 1000 : numeric)
  }
  return new Date(raw)
}

const formatDateTimeDisplay = (value) => {
  const d = toDateTimeValue(value)
  if (!d) return '-'
  if (Number.isNaN(d.getTime())) return String(value)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`
}

const toIsoDate = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const monthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1)

const buildCalendarCells = (monthDate) => {
  const first = monthStart(monthDate)
  const firstWeekday = (first.getDay() + 6) % 7
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(toIsoDate(new Date(first.getFullYear(), first.getMonth(), day)))
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

const LegalBottomBar = ({ showTerms = false, appVersion = 'dev' }) => (
  <footer className="legal-bottom-bar" aria-label="Rechtliche Links">
    <a className="legal-bottom-link sb-link" href="https://www.swissbit.com/de/impressum/" target="_blank" rel="noreferrer">
      Impressum
    </a>
    {showTerms ? (
      <a className="legal-bottom-link sb-link legal-bottom-link-center" href="/Geschaeftsbedingungen.pdf" target="_blank" rel="noreferrer">
        Geschäftsbedingungen
      </a>
    ) : (
      <span className="legal-bottom-link-spacer" aria-hidden="true" />
    )}
    <span className="legal-bottom-right">
      <a className="legal-bottom-link sb-link" href="/Datenschutzerklaerung.pdf" target="_blank" rel="noreferrer">
        Datenschutz
      </a>
      <span className="legal-bottom-version">Version {appVersion}</span>
    </span>
  </footer>
)

const highlightSearchSubstring = (value, query) => {
  const text = value == null || value === '' ? '-' : String(value)
  const needle = String(query || '').trim()
  if (!needle) return text
  const haystack = text.toLowerCase()
  const lowerNeedle = needle.toLowerCase()
  if (!lowerNeedle) return text
  let from = 0
  let hit = haystack.indexOf(lowerNeedle, from)
  if (hit === -1) return text
  const nodes = []
  let idx = 0
  while (hit !== -1) {
    if (hit > from) nodes.push(text.slice(from, hit))
    nodes.push(
      <mark className="search-substring-mark" key={`m-${idx}`}>
        {text.slice(hit, hit + needle.length)}
      </mark>
    )
    idx += 1
    from = hit + needle.length
    hit = haystack.indexOf(lowerNeedle, from)
  }
  if (from < text.length) nodes.push(text.slice(from))
  return nodes
}

const decodeBase64UrlToArrayBuffer = (value) => {
  const input = String(value || '')
  const padded = input + '='.repeat((4 - (input.length % 4)) % 4)
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const encodeArrayBufferToBase64Url = (value) => {
  if (!value) return ''
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : new Uint8Array(value.buffer || value)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const toCredentialCreateOptions = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {}
  const options = { ...source }
  options.challenge = decodeBase64UrlToArrayBuffer(source.challenge)
  if (source.user && source.user.id) {
    options.user = { ...source.user, id: decodeBase64UrlToArrayBuffer(source.user.id) }
  }
  options.excludeCredentials = (source.excludeCredentials || []).map((item) => ({
    ...item,
    id: decodeBase64UrlToArrayBuffer(item.id)
  }))
  return options
}

const toCredentialRequestOptions = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {}
  const options = { ...source }
  options.challenge = decodeBase64UrlToArrayBuffer(source.challenge)
  options.allowCredentials = Array.isArray(source.allowCredentials)
    ? source.allowCredentials.map((item) => ({ ...item, id: decodeBase64UrlToArrayBuffer(item.id) }))
    : []
  return options
}

const serializePasskeyRegistrationCredential = (credential) => {
  const response = credential?.response || {}
  return {
    id: credential?.id || '',
    rawId: encodeArrayBufferToBase64Url(credential?.rawId),
    type: credential?.type || 'public-key',
    response: {
      clientDataJSON: encodeArrayBufferToBase64Url(response.clientDataJSON),
      attestationObject: encodeArrayBufferToBase64Url(response.attestationObject),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : []
    }
  }
}

const serializePasskeyAuthenticationCredential = (credential) => {
  const response = credential?.response || {}
  return {
    id: credential?.id || '',
    rawId: encodeArrayBufferToBase64Url(credential?.rawId),
    type: credential?.type || 'public-key',
    response: {
      clientDataJSON: encodeArrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: encodeArrayBufferToBase64Url(response.authenticatorData),
      signature: encodeArrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle ? encodeArrayBufferToBase64Url(response.userHandle) : ''
    }
  }
}

const CheckboxMultiSelect = ({
  options,
  value,
  onChange,
  disabled = false,
  emptyLabel = 'Keine Daten',
  searchable = false,
  searchThreshold = UI_SEARCH_THRESHOLD,
  visibleRows = null
}) => {
  const selected = Array.isArray(value) ? value : []
  const selectedSet = new Set(selected)
  const [query, setQuery] = useState('')

  const showSearch = searchable && options.length >= searchThreshold
  const normalizedQuery = (showSearch ? query : '').trim().toLowerCase()
  const filteredOptions = useMemo(() => {
    if (!showSearch || !normalizedQuery) return options
    return options.filter((option) => {
      const label = String(option?.label || '').toLowerCase()
      return label.includes(normalizedQuery)
    })
  }, [normalizedQuery, options, showSearch])

  const toggle = (optionValue) => {
    if (disabled) return
    if (selectedSet.has(optionValue)) {
      onChange(selected.filter((item) => item !== optionValue))
      return
    }
    onChange([...selected, optionValue])
  }

  const shouldScroll = Number.isInteger(visibleRows) && visibleRows > 0 && filteredOptions.length > visibleRows
  const listStyle = shouldScroll ? { '--multi-select-max-rows': String(visibleRows) } : undefined

  return (
    <div className="multi-select-wrap">
      {showSearch && (
        <input
          type="search"
          className="multi-select-search"
          placeholder="Filter..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
      )}
      <div className={`multi-select${disabled ? ' is-disabled' : ''}${shouldScroll ? ' is-scrollable' : ''}`} aria-disabled={disabled} style={listStyle}>
        {!filteredOptions.length && <div className="multi-select-empty">{emptyLabel}</div>}
        {filteredOptions.map((option) => (
          <label className="multi-select-option" key={option.value}>
            <input
              type="checkbox"
              checked={selectedSet.has(option.value)}
              onChange={() => toggle(option.value)}
              disabled={disabled}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

const normalizeTenantUserRights = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {}
  const rights = {
    can_manage_companies: Boolean(source.can_manage_companies),
    can_manage_sites: Boolean(source.can_manage_sites),
    can_manage_pos: Boolean(source.can_manage_pos),
    can_run_exports: Boolean(source.can_run_exports),
    can_manage_api_keys: Boolean(source.can_manage_api_keys),
    can_manage_users: Boolean(source.can_manage_users),
    can_manage_impersonation: Boolean(source.can_manage_impersonation),
  }
  // Hierarchical implication: higher scope includes lower scope rights.
  if (rights.can_manage_companies) {
    rights.can_manage_sites = true
    rights.can_manage_pos = true
  } else if (rights.can_manage_sites) {
    rights.can_manage_pos = true
  }
  return rights
}

const normalizeAuthUser = (raw) => {
  const source = raw && typeof raw === 'object' && raw.user && typeof raw.user === 'object'
    ? raw.user
    : (raw && typeof raw === 'object' ? raw : {})
  return {
    ...source,
    role: typeof source.role === 'string' ? source.role : '',
    preferred_lang: String(source.preferred_lang || '').toLowerCase() === 'de' ? 'de' : (String(source.preferred_lang || '').toLowerCase() === 'en' ? 'en' : ''),
    rights: normalizeTenantUserRights(source.rights),
    impersonation: source.impersonation || { active: false }
  }
}

function App() {
  const TIME_RANGE_REQUIRED_ERROR = 'Bitte Zeitraum vollständig wählen (Von/Bis).'
  const browserDefaultLang = useMemo(() => detectBrowserUiLang(), [])
  const [uiLang, setUiLang] = useState(browserDefaultLang)
  const defaultUploadRange = useMemo(() => getDefaultUploadRange(), [])
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('fiscalarchive_token') || '')
  const [authUser, setAuthUser] = useState(null)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState(import.meta.env.VITE_LOGIN_PASSWORD || '')
  const [loginError, setLoginError] = useState('')
  const [loginInfo, setLoginInfo] = useState('')
  const [loginBusy, setLoginBusy] = useState(false)
  const [forgotPasswordBusy, setForgotPasswordBusy] = useState(false)
  const [passkeyLoginBusy, setPasskeyLoginBusy] = useState(false)
  const [resetPasswordNew, setResetPasswordNew] = useState('')
  const [resetPasswordRepeat, setResetPasswordRepeat] = useState('')
  const [resetPasswordBusy, setResetPasswordBusy] = useState(false)
  const [resetPasswordError, setResetPasswordError] = useState('')
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState('')
  const [resetTokenValidating, setResetTokenValidating] = useState(false)
  const [resetTokenValid, setResetTokenValid] = useState(null)
  const [active, setActive] = useState('Administration')
  const [profiles, setProfiles] = useState([])
  const [apiKeys, setApiKeys] = useState([])
  const [selectedApiKeyIds, setSelectedApiKeyIds] = useState([])
  const [bulkApiKeyApprovalBusy, setBulkApiKeyApprovalBusy] = useState(false)
  const [bulkApiKeyApprovalStatus, setBulkApiKeyApprovalStatus] = useState('')
  const [archiveItems, setArchiveItems] = useState([])
  const [archiveItemsWithDeleted, setArchiveItemsWithDeleted] = useState([])
  const [exports, setExports] = useState([])
  const [companies, setCompanies] = useState([])
  const [companiesWithDeleted, setCompaniesWithDeleted] = useState([])
  const [sites, setSites] = useState([])
  const [posList, setPosList] = useState([])
  const [posListWithDeleted, setPosListWithDeleted] = useState([])
  const [companyName, setCompanyName] = useState('')
  const [companyCountry, setCompanyCountry] = useState('DE')
  const [siteName, setSiteName] = useState('')
  const [selectedCompany, setSelectedCompany] = useState('')
  const [createPosDialog, setCreatePosDialog] = useState({
    siteId: '',
    posId: '',
    posLabel: '',
    tseType: 'swissbit_cloud_tse',
    tseSerialNumber: '',
    status: ''
  })
  const [showCompanyCreate, setShowCompanyCreate] = useState(false)
  const [expandedCompanies, setExpandedCompanies] = useState({})
  const [expandedSites, setExpandedSites] = useState({})
  const [editCompany, setEditCompany] = useState({ id: '', name: '' })
  const [editSite, setEditSite] = useState({ id: '', name: '' })
  const [editPos, setEditPos] = useState({
    id: '',
    posSerial: '',
    posLabel: '',
    tseType: 'swissbit_cloud_tse',
    tseSerialNumber: '',
    status: ''
  })
  const [editTenant, setEditTenant] = useState(false)
  const [tenantNameEdit, setTenantNameEdit] = useState('')
  const [uploadCompanyId, setUploadCompanyId] = useState('')
  const [uploadCompanySearchTerm, setUploadCompanySearchTerm] = useState('')
  const [tenantAdminOrgName, setTenantAdminOrgName] = useState('')
  const [tenantAdminName, setTenantAdminName] = useState('')
  const [tenantAdminEmail, setTenantAdminEmail] = useState('')
  const [showTenantCreate, setShowTenantCreate] = useState(false)
  const [editTenantAdmin, setEditTenantAdmin] = useState({ userId: '', organizationName: '', name: '', email: '' })
  const [expandedPlatformOrgId, setExpandedPlatformOrgId] = useState('')
  const [expandedPlatformMobileRowId, setExpandedPlatformMobileRowId] = useState('')
  const [platformOrgSearchTerm, setPlatformOrgSearchTerm] = useState('')
  const [tenantAdmins, setTenantAdmins] = useState([])
  const [resellerAdmins, setResellerAdmins] = useState([])
  const [resellerAdminName, setResellerAdminName] = useState('')
  const [resellerAdminContactName, setResellerAdminContactName] = useState('')
  const [resellerAdminEmail, setResellerAdminEmail] = useState('')
  const [showResellerCreate, setShowResellerCreate] = useState(false)
  const [resellerSearchTerm, setResellerSearchTerm] = useState('')
  const [editResellerAdmin, setEditResellerAdmin] = useState({ userId: '', name: '', contactName: '', email: '' })
  const [expandedPlatformResellerId, setExpandedPlatformResellerId] = useState('')
  const [platformOrgExpandedResellerIds, setPlatformOrgExpandedResellerIds] = useState({})
  const [resellerTenantOrgName, setResellerTenantOrgName] = useState('')
  const [resellerTenantName, setResellerTenantName] = useState('')
  const [resellerTenantEmail, setResellerTenantEmail] = useState('')
  const [resellerTenantRequestImpersonation, setResellerTenantRequestImpersonation] = useState(true)
  const [resellerSubRequestImpersonation, setResellerSubRequestImpersonation] = useState(true)
  const [showResellerTenantCreate, setShowResellerTenantCreate] = useState(false)
  const [showResellerSubCreate, setShowResellerSubCreate] = useState(false)
  const [editResellerTenantAdmin, setEditResellerTenantAdmin] = useState({ userId: '', tenantId: '', organizationName: '', name: '', email: '' })
  const [expandedResellerTenantId, setExpandedResellerTenantId] = useState('')
  const [expandedResellerMobileRowId, setExpandedResellerMobileRowId] = useState('')
  const [resellerOrgExpandedResellerIds, setResellerOrgExpandedResellerIds] = useState({})
  const [resellerTenantAdmins, setResellerTenantAdmins] = useState([])
  const [resellerImpersonationPermissions, setResellerImpersonationPermissions] = useState([])
  const [resellerImpersonationSearchTerm, setResellerImpersonationSearchTerm] = useState('')
  const [platformError, setPlatformError] = useState('')
  const [tenantUsers, setTenantUsers] = useState([])
  const [tenantImpersonationPermissions, setTenantImpersonationPermissions] = useState([])
  const [tenantImpersonationSearchTerm, setTenantImpersonationSearchTerm] = useState('')
  const [tenantImpersonationEmail, setTenantImpersonationEmail] = useState('')
  const [tenantImpersonationError, setTenantImpersonationError] = useState('')
  const [showTenantImpersonationCreate, setShowTenantImpersonationCreate] = useState(false)
  const [expandedTenantImpersonationPermissionId, setExpandedTenantImpersonationPermissionId] = useState('')
  const [tenantUserName, setTenantUserName] = useState('')
  const [tenantUserEmail, setTenantUserEmail] = useState('')
  const [tenantUserRights, setTenantUserRights] = useState(TENANT_USER_RIGHTS_DEFAULT)
  const [showTenantUserCreate, setShowTenantUserCreate] = useState(false)
  const [expandedTenantUserId, setExpandedTenantUserId] = useState('')
  const [editTenantUser, setEditTenantUser] = useState({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })
  const [tenantUserError, setTenantUserError] = useState('')
  const [tenantUserSuccess, setTenantUserSuccess] = useState('')
  const [rightsDialog, setRightsDialog] = useState({ open: false, mode: 'create', userId: '', label: '' })
  const [rightsDraft, setRightsDraft] = useState(TENANT_USER_RIGHTS_DEFAULT)
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [showUserSettings, setShowUserSettings] = useState(false)
  const [showTenantOrganizationPanel, setShowTenantOrganizationPanel] = useState(false)
  const [showTenantUsersPanel, setShowTenantUsersPanel] = useState(false)
  const [showTenantFullAccessPanel, setShowTenantFullAccessPanel] = useState(false)
  const [preferredLanguageDraft, setPreferredLanguageDraft] = useState(browserDefaultLang)
  const [settingsBusy, setSettingsBusy] = useState(false)
  const [settingsError, setSettingsError] = useState('')
  const [settingsSuccess, setSettingsSuccess] = useState('')
  const [passwordNew, setPasswordNew] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [passwordBusy, setPasswordBusy] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passkeys, setPasskeys] = useState([])
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const [passkeyError, setPasskeyError] = useState('')
  const [passkeySuccess, setPasskeySuccess] = useState('')
  const [showPasskeyPanel, setShowPasskeyPanel] = useState(false)
  const [showPasskeyCreate, setShowPasskeyCreate] = useState(false)
  const [passkeyName, setPasskeyName] = useState(PASSKEY_DEFAULT_NAME)
  const [editPasskey, setEditPasskey] = useState({ passkeyId: '', name: '' })
  const [uploadArtifactType, setUploadArtifactType] = useState('tse_tar')
  const [uploadFreeArtifactLabel, setUploadFreeArtifactLabel] = useState('')
  const [uploadFrom, setUploadFrom] = useState(defaultUploadRange.from)
  const [uploadTo, setUploadTo] = useState(defaultUploadRange.to)
  const [uploadPosRefs, setUploadPosRefs] = useState([])
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadBundledFiles, setUploadBundledFiles] = useState([])
  const [, setUploadLastAddedFile] = useState('')
  const [uploadBusy, setUploadBusy] = useState(false)
  const [uploadTransferProgress, setUploadTransferProgress] = useState({ active: false, loaded: 0, total: 0, percent: null })
  const [uploadError, setUploadError] = useState('')
  const [, setUploadSuccess] = useState('')
  const [uploadPickerOpen, setUploadPickerOpen] = useState(false)
  const [uploadPickerMonth, setUploadPickerMonth] = useState(() => monthStart(new Date()))
  const uploadFileInputRef = useRef(null)
  const elefantAudioRef = useRef(null)
  const [apiKeysSearchTerm, setApiKeysSearchTerm] = useState('')
  const [expandedApiKeyMobileId, setExpandedApiKeyMobileId] = useState('')
  const [apiKeyActionDialog, setApiKeyActionDialog] = useState({
    open: false,
    mode: 'approve',
    apiKey: '',
    posId: '',
    posSerial: '',
    companyId: '',
    siteId: '',
    createCompany: false,
    createSite: false,
    newCompanyName: '',
    newCompanyCountry: 'DE',
    newSiteName: '',
    reportedCompanyName: '',
    reportedCompanyCountry: '',
    reportedSiteName: '',
    reportedPosSerial: '',
    reportedPosLabel: '',
    status: '',
    busy: false
  })
  const [apiKeyOnboardingCode, setApiKeyOnboardingCode] = useState({
    code: '',
    busy: false,
    error: '',
    copied: false
  })
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('')
  const [tenantUsersSearchTerm, setTenantUsersSearchTerm] = useState('')
  const [exportCompanyIds, setExportCompanyIds] = useState([])
  const [exportPosIds, setExportPosIds] = useState([])
  const [exportRangeStart, setExportRangeStart] = useState('')
  const [exportRangeEnd, setExportRangeEnd] = useState('')
  const [exportIncludeDeleted, setExportIncludeDeleted] = useState(false)
  const [exportPreview, setExportPreview] = useState(null)
  const [exportSelectedArtifactTypes, setExportSelectedArtifactTypes] = useState([])
  const [exportFilterApplied, setExportFilterApplied] = useState(false)
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState('')
  const [, setExportSuccess] = useState('')
  const [exportAuditorEmail, setExportAuditorEmail] = useState('')
  const [exportPickerOpen, setExportPickerOpen] = useState(false)
  const [exportPickerMonth, setExportPickerMonth] = useState(() => monthStart(new Date()))
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const i18nLiteralsRef = useRef({})
  const [i18nVersion, setI18nVersion] = useState(0)

  const apiBase = import.meta.env.VITE_API_BASE || '/api/v1'
  const appVersion = import.meta.env.VITE_APP_VERSION || 'dev'
  const isActivationRoute = typeof window !== 'undefined' && window.location.pathname === '/activate-account'
  const isPasswordTokenRoute = (typeof window !== 'undefined' && window.location.pathname === '/reset-password') || isActivationRoute
  const resetPasswordToken = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('token') || ''
  }, [])
  const [resetTokenPurpose, setResetTokenPurpose] = useState(isActivationRoute ? 'onboarding' : 'password_reset')
  const tenantId = authUser?.tenant_id || import.meta.env.VITE_TENANT_ID || 't-1'
  const fallbackRole = authToken ? '' : (import.meta.env.VITE_USER_ROLE || '')
  const userRole = authUser?.role || fallbackRole || ''
  const isTenantAdmin = userRole === 'tenant_admin'
  const isTenantUser = userRole === 'tenant_user'
  const isResellerAdmin = userRole === 'reseller_admin'
  const sessionRights = normalizeTenantUserRights(authUser?.rights)
  const effectiveRights = isTenantAdmin
    ? {
        can_manage_companies: true,
        can_manage_sites: true,
        can_manage_pos: true,
        can_run_exports: true,
        can_manage_api_keys: true,
        can_manage_users: true,
        can_manage_impersonation: true,
      }
    : (isTenantUser ? sessionRights : TENANT_USER_RIGHTS_DEFAULT)
  const canRenameOrganization = isTenantAdmin
  const canEditCompanies = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_companies))
  const canEditSites = canEditCompanies || (isTenantUser && Boolean(effectiveRights.can_manage_sites))
  const canEditPos = canEditSites || (isTenantUser && Boolean(effectiveRights.can_manage_pos))
  const canRunExports = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_run_exports))
  const canManageApiKeys = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_api_keys))
  const canManageUsers = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_users))
  const canManageImpersonation = isTenantAdmin || (isTenantUser && Boolean(effectiveRights.can_manage_impersonation))
  const isStructureNavigation = active === 'Unternehmensstruktur'
  const showTopbarOrg = isTenantAdmin || isTenantUser
  const roleDisplayLabel = userRole === 'platform_admin'
    ? uiLiteral('Platform Admin')
    : (userRole === 'reseller_admin'
        ? uiLiteral('Reseller Admin')
        : (userRole === 'tenant_admin'
            ? uiLiteral('Tenant Admin')
            : (userRole === 'tenant_user' ? uiLiteral('Tenant User') : uiLiteral('User'))))
  const topbarUserLabel = roleDisplayLabel
  const userDisplayName = authUser?.name || authUser?.full_name || authUser?.username || ''
  const showTopbarUserName = !isResellerAdmin && userRole !== 'platform_admin'
  const showTopbarRole = false
  const passkeySupported = typeof window !== 'undefined' && Boolean(window.PublicKeyCredential)
  const navItems = useMemo(
    () => {
      if (userRole === 'platform_admin') return [{ label: 'Administration' }]
      if (userRole === 'reseller_admin') return [{ label: 'Administration' }]
      const items = NAV_ITEMS.filter((item) => (item.label === 'Prüfexport' ? canRunExports : true))
      if (isTenantAdmin || isTenantUser) {
        return items.flatMap((item) => (item.label === 'Administration' ? [{ label: 'Unternehmensstruktur' }, item] : [item]))
      }
      return items
    },
    [userRole, canRunExports, isTenantAdmin, isTenantUser]
  )
  const hashTarget = useMemo(() => {
    if (typeof window === 'undefined') return ''
    const raw = String(window.location.hash || '').trim().toLowerCase().replace(/^#/, '')
    if (!raw) return ''
    const map = {
      'web-upload': 'Web-Upload',
      'pruefexport': 'Prüfexport',
      'api-keys': 'API-Keys',
      'administration': 'Administration',
      'unternehmensstruktur': 'Unternehmensstruktur'
    }
    return map[raw] || ''
  }, [])

  const authHeaders = useMemo(
    () => ({
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      ...(userRole ? { 'X-User-Role': userRole } : {})
    }),
    [authToken, tenantId, userRole]
  )
  const isOnboardingPasswordFlow = resetTokenPurpose === 'onboarding' || isActivationRoute
  const resetRouteTitle = isOnboardingPasswordFlow ? 'Konto aktivieren' : 'Passwort zurücksetzen'
  const resetRouteSubmitIdle = isOnboardingPasswordFlow ? 'Passwort setzen' : 'Passwort zurücksetzen'
  const resetRouteSubmitBusy = isOnboardingPasswordFlow ? 'Konto wird aktiviert…' : 'Speichern…'
  const resetRouteValidating = isOnboardingPasswordFlow ? 'Prüfe Aktivierungslink…' : 'Prüfe Link…'
  const resetRouteMissingMessage = isOnboardingPasswordFlow ? 'Aktivierungslink fehlt oder ist ungültig.' : 'Reset-Link fehlt oder ist ungültig.'
  const resetRouteInvalidMessage = isOnboardingPasswordFlow ? 'Aktivierungslink ist ungültig oder abgelaufen.' : 'Reset-Link ist ungültig oder abgelaufen.'
  const resetRouteValidationFailedMessage = isOnboardingPasswordFlow ? 'Aktivierungslink konnte nicht geprüft werden.' : 'Reset-Link konnte nicht geprüft werden.'
  const resetRouteSuccessMessage = isOnboardingPasswordFlow ? 'Konto aktiviert. Sie können sich jetzt einloggen.' : 'Passwort zurückgesetzt. Sie können sich jetzt einloggen.'

  useEffect(() => {
    if (authUser) {
      const preferred = String(authUser?.preferred_lang || '').toLowerCase()
      const effective = preferred === 'de' || preferred === 'en' ? preferred : 'en'
      setUiLang((prev) => (prev === effective ? prev : effective))
      setPreferredLanguageDraft(effective)
      return
    }
    setUiLang((prev) => (prev === browserDefaultLang ? prev : browserDefaultLang))
    setPreferredLanguageDraft(browserDefaultLang)
  }, [authUser, authUser?.preferred_lang, browserDefaultLang])

  useEffect(() => {
    setCurrentUiLang(uiLang)
    i18nLiteralsRef.current = CURRENT_UI_LITERALS
    setI18nVersion((value) => value + 1)
  }, [uiLang])

  useEffect(() => {
    const root = document.body
    const uninstall = installDomTranslator(root, i18nLiteralsRef.current)
    return uninstall
  }, [i18nVersion, archiveItems, exports, apiKeys, companies, sites, posList, active, authUser, loginError, loginInfo, uploadError, exportError, tenantUserError, tenantUserSuccess, tenantImpersonationError, showTenantOrganizationPanel, showTenantUsersPanel, showTenantFullAccessPanel])

  const clearAuth = () => {
    setAuthToken('')
    setAuthUser(null)
    setPasskeys([])
    resetApiKeyOnboardingCode()
    localStorage.removeItem('fiscalarchive_token')
  }

  const applyAuthSession = (payload) => {
    setAuthToken(payload.token)
    setAuthUser(normalizeAuthUser(payload.user))
    localStorage.setItem('fiscalarchive_token', payload.token)
  }

  const parseError = async (res) => {
    try {
      const data = await res.json()
      return normalizeApiError(res.status, data, { uploadFileLimitLabel: UPLOAD_FILE_LIMIT_LABEL })
    } catch {
      return normalizeApiError(res.status, null, { uploadFileLimitLabel: UPLOAD_FILE_LIMIT_LABEL })
    }
  }

  const handleLogin = async (event) => {
    event.preventDefault()
    setLoginError('')
    setLoginInfo('')
    setLoginBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      const data = await res.json()
      applyAuthSession({
        token: data.token,
        user: data.user
      })
    } catch (err) {
      setLoginError(err?.message || 'Login fehlgeschlagen.')
    } finally {
      setLoginBusy(false)
    }
  }

  const handleForgotPassword = async () => {
    setLoginError('')
    setLoginInfo('')
    const email = loginEmail.trim().toLowerCase()
    if (!email) {
      setLoginError('Bitte E-Mail eingeben.')
      return
    }
    setForgotPasswordBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/password/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setLoginInfo('Wenn die E-Mail-Adresse bekannt ist, wurde ein Reset-Link versendet.')
    } catch (err) {
      setLoginError(err?.message || 'Passwort vergessen fehlgeschlagen.')
    } finally {
      setForgotPasswordBusy(false)
    }
  }

  const handleResetPassword = async (event) => {
    event.preventDefault()
    setResetPasswordError('')
    setResetPasswordSuccess('')
    if (resetTokenValid === false) {
      setResetPasswordError(resetRouteInvalidMessage)
      return
    }
    if (!resetPasswordToken) {
      setResetPasswordError('Reset-Link fehlt oder ist ungültig.')
      return
    }
    if (!resetPasswordNew || !resetPasswordRepeat) {
      setResetPasswordError('Bitte alle Passwortfelder ausfüllen.')
      return
    }
    if (resetPasswordNew.length < 8) {
      setResetPasswordError('Neues Passwort muss mindestens 8 Zeichen haben.')
      return
    }
    if (resetPasswordNew !== resetPasswordRepeat) {
      setResetPasswordError('Neues Passwort und Wiederholung stimmen nicht überein.')
      return
    }
    setResetPasswordBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetPasswordToken, new_password: resetPasswordNew })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setResetPasswordSuccess(resetRouteSuccessMessage)
      setResetPasswordNew('')
      setResetPasswordRepeat('')
    } catch (err) {
      setResetPasswordError(err?.message || 'Passwort zurücksetzen fehlgeschlagen.')
    } finally {
      setResetPasswordBusy(false)
    }
  }

  useEffect(() => {
    if (!isPasswordTokenRoute) return
    if (!resetPasswordToken) {
      setResetTokenValid(false)
      setResetPasswordError(resetRouteMissingMessage)
      return
    }
    let cancelled = false
    const validateToken = async () => {
      setResetTokenValidating(true)
      try {
        const res = await fetch(`${apiBase}/auth/password/reset/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetPasswordToken })
        })
        if (!res.ok) {
          if (!cancelled) {
            setResetTokenValid(false)
            setResetPasswordError(resetRouteInvalidMessage)
          }
          return
        }
        const data = await res.json()
        if (cancelled) return
        if (data?.valid) {
          setResetTokenValid(true)
          setResetTokenPurpose(data?.purpose === 'onboarding' ? 'onboarding' : 'password_reset')
          setResetPasswordError('')
        } else {
          setResetTokenValid(false)
          setResetPasswordError(resetRouteInvalidMessage)
        }
      } catch {
        if (!cancelled) {
          setResetTokenValid(false)
          setResetPasswordError(resetRouteValidationFailedMessage)
        }
      } finally {
        if (!cancelled) setResetTokenValidating(false)
      }
    }
    validateToken()
    return () => {
      cancelled = true
    }
  }, [apiBase, isPasswordTokenRoute, resetPasswordToken, resetRouteInvalidMessage, resetRouteMissingMessage, resetRouteValidationFailedMessage])

  const handleLogout = async () => {
    try {
      if (authToken) {
        await fetch(`${apiBase}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` }
        })
      }
    } catch {
      // ignore
    } finally {
      clearAuth()
    }
  }

  const refreshTenantAdmins = async () => {
    const res = await fetch(`${apiBase}/platform/tenant-admins`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setTenantAdmins(Array.isArray(data) ? data : [])
    }
  }

  const refreshResellerAdmins = async () => {
    const endpoint = authUser?.role === 'reseller_admin'
      ? `${apiBase}/reseller/reseller-admins`
      : `${apiBase}/platform/reseller-admins`
    const res = await fetch(endpoint, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setResellerAdmins(Array.isArray(data) ? data : [])
    }
  }

  const refreshResellerTenantAdmins = async () => {
    const res = await fetch(`${apiBase}/reseller/tenant-admins`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setResellerTenantAdmins(Array.isArray(data) ? data : [])
    }
  }

  const refreshResellerImpersonationPermissions = async () => {
    const res = await fetch(`${apiBase}/reseller/impersonation-permissions`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setResellerImpersonationPermissions(Array.isArray(data) ? data : [])
      return
    }
    setResellerImpersonationPermissions([])
  }

  const refreshTenantImpersonationPermissions = async () => {
    const res = await fetch(`${apiBase}/tenant/impersonation-permissions`, { headers: authHeaders })
    if (res.ok) {
      const data = await res.json()
      setTenantImpersonationPermissions(Array.isArray(data) ? data : [])
    }
  }

  const refreshTenantUsers = async () => {
    const res = await fetch(`${apiBase}/tenant/users?ts=${Date.now()}`, {
      headers: authHeaders,
      cache: 'no-store'
    })
    if (res.ok) {
      const data = await res.json()
      const normalized = Array.isArray(data)
        ? data.map((item) => ({ ...item, rights: normalizeTenantUserRights(item?.rights) }))
        : []
      setTenantUsers(normalized)
    }
  }

  const refreshPasskeys = async () => {
    if (!authToken || !authUser) return
    const res = await fetch(`${apiBase}/auth/passkeys`, { headers: authHeaders })
    if (!res.ok) {
      setPasskeys([])
      return
    }
    const data = await res.json()
    setPasskeys(Array.isArray(data) ? data : [])
  }

  const handleCreateTenantAdmin = async () => {
    setPlatformError('')
    if (!tenantAdminOrgName.trim() || !tenantAdminName.trim() || !tenantAdminEmail.trim()) return
    const res = await fetch(`${apiBase}/platform/tenant-admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: tenantAdminOrgName.trim(),
        name: tenantAdminName.trim(),
        email: tenantAdminEmail.trim()
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setTenantAdminOrgName('')
    setTenantAdminName('')
    setTenantAdminEmail('')
    await refreshTenantAdmins()
  }

  const handleCreateResellerAdmin = async () => {
    setPlatformError('')
    if (!resellerAdminEmail.trim()) return
    const payload = { email: resellerAdminEmail.trim() }
    if (resellerAdminName.trim()) payload.name = resellerAdminName.trim()
    if (resellerAdminContactName.trim()) payload.contact_name = resellerAdminContactName.trim()
    if (authUser?.role === 'reseller_admin') {
      payload.request_impersonation = resellerSubRequestImpersonation
    }
    const endpoint = authUser?.role === 'reseller_admin'
      ? `${apiBase}/reseller/reseller-admins`
      : `${apiBase}/platform/reseller-admins`
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setResellerAdminName('')
    setResellerAdminContactName('')
    setResellerAdminEmail('')
    setResellerSubRequestImpersonation(true)
    setShowResellerCreate(false)
    setShowResellerSubCreate(false)
    await refreshResellerAdmins()
  }

  const handleSaveResellerAdminEdit = async () => {
    setPlatformError('')
    if (!editResellerAdmin.userId || !editResellerAdmin.name.trim() || !editResellerAdmin.email.trim()) return
    const res = await fetch(`${apiBase}/platform/reseller-admins/${editResellerAdmin.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: editResellerAdmin.name.trim(),
        contact_name: String(editResellerAdmin.contactName || '').trim(),
        email: editResellerAdmin.email.trim()
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })
    await refreshResellerAdmins()
  }

  const handleDeleteResellerAdmin = async (userId, label) => {
    setPlatformError('')
    const meta = resellerAdmins.find((item) => item.user_id === userId)
    const currentStatus = String(meta?.status || '').toLowerCase()
    const nextStatus = currentStatus === 'active' ? 'disabled' : 'active'
    const actionLabel = nextStatus === 'disabled' ? 'deaktivieren' : 'aktivieren'
    if (!window.confirm(formatUiTemplate('confirm.toggle.generic', { label, action: actionLabel }))) return
    const res = await fetch(`${apiBase}/platform/reseller-admins/${userId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: nextStatus })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerAdmins()
  }

  const handleUpdateResellerImpersonationPermissionStatus = async (permissionId, status) => {
    setPlatformError('')
    const res = await fetch(`${apiBase}/reseller/impersonation-permissions/${permissionId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerImpersonationPermissions()
    await refreshResellerAdmins()
  }

  const handleCreateResellerTenantAdmin = async () => {
    setPlatformError('')
    if (!resellerTenantOrgName.trim() || !resellerTenantName.trim() || !resellerTenantEmail.trim()) return
    const res = await fetch(`${apiBase}/reseller/tenant-admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: resellerTenantOrgName.trim(),
        name: resellerTenantName.trim(),
        email: resellerTenantEmail.trim(),
        request_impersonation: resellerTenantRequestImpersonation
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setResellerTenantOrgName('')
    setResellerTenantName('')
    setResellerTenantEmail('')
    setResellerTenantRequestImpersonation(true)
    setShowResellerTenantCreate(false)
    await refreshResellerTenantAdmins()
  }

  const handleSaveResellerTenantAdminEdit = async () => {
    setPlatformError('')
    if (!editResellerTenantAdmin.userId || !editResellerTenantAdmin.organizationName.trim() || !editResellerTenantAdmin.name.trim() || !editResellerTenantAdmin.email.trim()) return
    const res = await fetch(`${apiBase}/reseller/tenant-admins/${editResellerTenantAdmin.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: editResellerTenantAdmin.organizationName.trim(),
        name: editResellerTenantAdmin.name.trim(),
        email: editResellerTenantAdmin.email.trim()
      })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setEditResellerTenantAdmin({ userId: '', tenantId: '', organizationName: '', name: '', email: '' })
    await refreshResellerTenantAdmins()
  }

  const handleToggleResellerTenantStatus = async (item) => {
    setPlatformError('')
    const currentStatus = (item?.status || '').toLowerCase()
    const targetStatus = currentStatus === 'active' ? 'disabled' : 'active'
    const res = await fetch(`${apiBase}/reseller/tenants/${item.tenant_id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: targetStatus })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerTenantAdmins()
  }

  const handleRequestResellerTenantImpersonation = async (tenantId) => {
    setPlatformError('')
    const res = await fetch(apiBase + '/reseller/tenants/' + tenantId + '/impersonation-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders }
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerTenantAdmins()
  }

  const handleWithdrawResellerTenantImpersonation = async (tenantId, status, label = '') => {
    setPlatformError('')
    if (String(status || '').toLowerCase() === 'approved') {
      const displayLabel = label || uiLiteral('Organisation')
      if (!window.confirm(formatUiTemplate('confirm.withdraw.impersonation_access', { label: displayLabel }))) return
    }
    const res = await fetch(apiBase + '/reseller/tenants/' + tenantId + '/impersonation-withdraw', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders }
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshResellerTenantAdmins()
  }

  const handleImpersonateTenant = async (tenantId) => {
    setPlatformError('')
    const res = await fetch(`${apiBase}/impersonations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleCreateTenantImpersonationPermission = async () => {
    setTenantImpersonationError('')
    if (!tenantImpersonationEmail.trim()) return
    const res = await fetch(`${apiBase}/tenant/impersonation-permissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ email: tenantImpersonationEmail.trim() })
    })
    if (!res.ok) {
      setTenantImpersonationError(await parseError(res))
      return
    }
    setTenantImpersonationEmail('')
    setShowTenantImpersonationCreate(false)
    await refreshTenantImpersonationPermissions()
  }

  const handleUpdateTenantImpersonationPermissionStatus = async (permissionId, status, label = '') => {
    setTenantImpersonationError('')
    if (String(status || '').toLowerCase() === 'revoked') {
      const displayLabel = label || uiLiteral('Helfer')
      if (!window.confirm(formatUiTemplate('confirm.revoke.impersonation_access', { label: displayLabel }))) return
    }
    const res = await fetch(`${apiBase}/tenant/impersonation-permissions/${permissionId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status })
    })
    if (!res.ok) {
      setTenantImpersonationError(await parseError(res))
      return
    }
    await refreshTenantImpersonationPermissions()
  }

  const handleImpersonateUser = async (userId) => {
    setPlatformError('')
    const res = await fetch(`${apiBase}/platform/impersonations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ user_id: userId })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleImpersonateResellerUser = async (userId) => {
    setPlatformError('')
    const res = await fetch(`${apiBase}/reseller/impersonations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ user_id: userId })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleTogglePlatformTenantAdminStatus = async (item) => {
    setPlatformError('')
    const currentStatus = (item?.status || '').toLowerCase()
    const targetStatus = currentStatus === 'active' ? 'disabled' : 'active'
    const res = await fetch(`${apiBase}/platform/tenant-admins/${item.user_id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: targetStatus })
    })
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    await refreshTenantAdmins()
  }

  const handleSaveTenantAdminEdit = async () => {
    setPlatformError('')
    if (!editTenantAdmin.userId || !editTenantAdmin.organizationName.trim() || !editTenantAdmin.name.trim() || !editTenantAdmin.email.trim()) return
    const orgName = editTenantAdmin.organizationName.trim()
    const contactName = editTenantAdmin.name.trim()
    const email = editTenantAdmin.email.trim()
    let res = await fetch(`${apiBase}/platform/tenant-admins/${editTenantAdmin.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        organization_name: orgName,
        name: contactName,
        email
      })
    })
    if (res.status === 404) {
      // Backward-compat fallback for older backend versions that only support email patch.
      res = await fetch(`${apiBase}/platform/tenant-admins/${editTenantAdmin.userId}/email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ email })
      })
      if (!res.ok) {
        setPlatformError(await parseError(res))
        return
      }
      setPlatformError('Organisation/Kontakt konnten nicht vollständig aktualisiert werden (Backend-Endpoint fehlt). Backend neu starten.')
      setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })
      await refreshTenantAdmins()
      return
    }
    if (!res.ok) {
      setPlatformError(await parseError(res))
      return
    }
    setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })
    await refreshTenantAdmins()
  }

  const handleStopImpersonation = async () => {
    const res = await fetch(`${apiBase}/platform/impersonations/stop`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) return
    const data = await res.json()
    applyAuthSession(data)
  }

  const handleCreateTenantUser = async () => {
    setTenantUserError('')
    setTenantUserSuccess('')
    if (!tenantUserName.trim() || !tenantUserEmail.trim()) return
    const res = await fetch(`${apiBase}/tenant/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: tenantUserName.trim(),
        email: tenantUserEmail.trim(),
        rights: tenantUserRights
      })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    await res.json()
    setTenantUserName('')
    setTenantUserEmail('')
    setTenantUserRights(TENANT_USER_RIGHTS_DEFAULT)
    setShowTenantUserCreate(false)
    setTenantUserSuccess('Nutzer angelegt.')
    await refreshTenantUsers()
  }

  const handleLoginWithPasskey = async () => {
    setLoginError('')
    setLoginInfo('')
    if (!passkeySupported) {
      setLoginError('Passkeys werden von diesem Browser nicht unterstützt.')
      return
    }
    setPasskeyLoginBusy(true)
    try {
      const beginRes = await fetch(`${apiBase}/auth/passkeys/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      if (!beginRes.ok) {
        throw new Error(await parseError(beginRes))
      }
      const beginData = await beginRes.json()
      const publicKey = toCredentialRequestOptions(beginData.public_key)
      const credential = await navigator.credentials.get({
        publicKey
      })
      if (!credential) {
        throw new Error('Passkey-Anmeldung abgebrochen.')
      }
      const verifyRes = await fetch(`${apiBase}/auth/passkeys/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challenge_id: beginData.challenge_id,
          credential: serializePasskeyAuthenticationCredential(credential)
        })
      })
      if (!verifyRes.ok) {
        throw new Error(await parseError(verifyRes))
      }
      const data = await verifyRes.json()
      applyAuthSession({
        token: data.token,
        user: data.user
      })
    } catch (err) {
      const message = String(err?.message || '')
      if (message.toLowerCase().includes('passkey not available')) {
        setLoginError('Passkey-Backend nicht verfügbar (WebAuthn-Modul fehlt oder Server nicht neu gestartet).')
      } else {
        setLoginError(message || 'Passkey-Anmeldung fehlgeschlagen.')
      }
    } finally {
      setPasskeyLoginBusy(false)
    }
  }

  const handleCreatePasskey = async () => {
    setPasskeyError('')
    setPasskeySuccess('')
    if (!passkeySupported) {
      setPasskeyError('Passkeys werden von diesem Browser nicht unterstützt.')
      return
    }
    if (!passkeyName.trim()) {
      setPasskeyError('Bitte einen Namen für den Passkey angeben.')
      return
    }
    setPasskeyBusy(true)
    try {
      const beginRes = await fetch(`${apiBase}/auth/passkeys/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders }
      })
      if (!beginRes.ok) {
        throw new Error(await parseError(beginRes))
      }
      const beginData = await beginRes.json()
      const publicKey = toCredentialCreateOptions(beginData.public_key)
      const credential = await navigator.credentials.create({ publicKey })
      if (!credential) {
        throw new Error('Passkey-Registrierung abgebrochen.')
      }
      const verifyRes = await fetch(`${apiBase}/auth/passkeys/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          challenge_id: beginData.challenge_id,
          name: passkeyName.trim(),
          credential: serializePasskeyRegistrationCredential(credential)
        })
      })
      if (!verifyRes.ok) {
        throw new Error(await parseError(verifyRes))
      }
      setPasskeySuccess('Passkey hinzugefügt.')
      setPasskeyName(PASSKEY_DEFAULT_NAME)
      setShowPasskeyCreate(false)
      await refreshPasskeys()
    } catch (err) {
      const message = String(err?.message || '')
      if (message.toLowerCase().includes('passkey not available')) {
        setPasskeyError('Passkey-Backend nicht verfügbar (WebAuthn-Modul fehlt oder Server nicht neu gestartet).')
      } else {
        setPasskeyError(message || 'Passkey konnte nicht erstellt werden.')
      }
    } finally {
      setPasskeyBusy(false)
    }
  }

  const handleSavePasskeyName = async () => {
    setPasskeyError('')
    setPasskeySuccess('')
    if (!editPasskey.passkeyId || !editPasskey.name.trim()) return
    const res = await fetch(`${apiBase}/auth/passkeys/${editPasskey.passkeyId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: editPasskey.name.trim() })
    })
    if (!res.ok) {
      setPasskeyError(await parseError(res))
      return
    }
    setEditPasskey({ passkeyId: '', name: '' })
    setPasskeySuccess('Passkey aktualisiert.')
    await refreshPasskeys()
  }

  const handleDeletePasskey = async (passkeyId, label) => {
    setPasskeyError('')
    setPasskeySuccess('')
    if (!window.confirm(formatUiTemplate('confirm.delete.passkey', { label }))) return
    const res = await fetch(`${apiBase}/auth/passkeys/${passkeyId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) {
      setPasskeyError(await parseError(res))
      return
    }
    setPasskeySuccess('Passkey gelöscht.')
    await refreshPasskeys()
  }

  const handleSaveTenantUserEdit = async () => {
    setTenantUserError('')
    setTenantUserSuccess('')
    if (!editTenantUser.userId || !editTenantUser.name.trim()) return
    const res = await fetch(`${apiBase}/tenant/users/${editTenantUser.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: editTenantUser.name.trim()
      })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    setEditTenantUser({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })
    setTenantUserSuccess('Nutzer aktualisiert.')
    await refreshTenantUsers()
  }

  const handleDeleteTenantUser = async (userId, label) => {
    setTenantUserError('')
    setTenantUserSuccess('')
    if (!window.confirm(formatUiTemplate('confirm.delete.user', { label }))) return
    const res = await fetch(`${apiBase}/tenant/users/${userId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    setTenantUserSuccess('Nutzer gelöscht.')
    await refreshTenantUsers()
  }

  const openTenantUserRightsDialog = (mode, payload = {}) => {
    const initialRights = normalizeTenantUserRights(payload.rights || (mode === 'create' ? tenantUserRights : TENANT_USER_RIGHTS_DEFAULT))
    setRightsDraft(initialRights)
    setRightsDialog({
      open: true,
      mode,
      userId: payload.userId || '',
      label: payload.label || ''
    })
  }

  const closeTenantUserRightsDialog = () => {
    setRightsDialog({ open: false, mode: 'create', userId: '', label: '' })
    setRightsDraft(TENANT_USER_RIGHTS_DEFAULT)
  }

  const toggleTenantUserRight = (key) => {
    setRightsDraft((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const saveTenantUserRightsDialog = async () => {
    if (rightsDialog.mode === 'create') {
      setTenantUserRights(normalizeTenantUserRights(rightsDraft))
      setTenantUserSuccess('Rechte für Neuanlage übernommen.')
      closeTenantUserRightsDialog()
      return
    }

    if (!rightsDialog.userId) return
    setTenantUserError('')
    setTenantUserSuccess('')
    const normalizedDraft = normalizeTenantUserRights(rightsDraft)
    const res = await fetch(`${apiBase}/tenant/users/${rightsDialog.userId}/rights`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ rights: normalizedDraft })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    let savedRights = normalizedDraft
    try {
      const data = await res.json()
      savedRights = normalizeTenantUserRights(data?.rights || normalizedDraft)
    } catch {
      // ignore parse fallback
    }
    const targetUserId = String(rightsDialog.userId || '')
    setTenantUsers((prev) => prev.map((item) => (
      String(item.user_id || '') === targetUserId ? { ...item, rights: savedRights } : item
    )))
    setAuthUser((prev) => {
      if (!prev) return prev
      if (String(prev.user_id || '') !== targetUserId) return prev
      return { ...prev, rights: savedRights }
    })
    setTenantUserSuccess('Rechte gespeichert.')
    closeTenantUserRightsDialog()
    await refreshTenantUsers()
  }

  const handleToggleTenantUserLock = async (userId, label, isLocked) => {
    setTenantUserError('')
    setTenantUserSuccess('')
    const actionLabel = isLocked ? 'entsperren' : 'sperren'
    if (!window.confirm(formatUiTemplate('confirm.toggle.user', { label, action: actionLabel }))) return
    const res = await fetch(`${apiBase}/tenant/users/${userId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ status: isLocked ? 'active' : 'locked' })
    })
    if (!res.ok) {
      setTenantUserError(await parseError(res))
      return
    }
    setTenantUserSuccess(isLocked ? 'Nutzer wurde entsperrt.' : 'Nutzer wurde gesperrt.')
    await refreshTenantUsers()
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')
    if (!passwordNew || !passwordRepeat) {
      setPasswordError('Bitte alle Passwortfelder ausfüllen.')
      return
    }
    if (passwordNew !== passwordRepeat) {
      setPasswordError('Neues Passwort und Wiederholung stimmen nicht überein.')
      return
    }
    if (passwordNew.length < 8) {
      setPasswordError('Neues Passwort muss mindestens 8 Zeichen haben.')
      return
    }
    setPasswordBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          new_password: passwordNew
        })
      })
      if (!res.ok) {
        setPasswordError(await parseError(res))
        return
      }
      setPasswordNew('')
      setPasswordRepeat('')
      setPasswordSuccess('Passwort wurde geändert.')
    } finally {
      setPasswordBusy(false)
    }
  }

  const handleSaveUserSettings = async () => {
    const nextLang = preferredLanguageDraft === 'de' ? 'de' : 'en'
    setSettingsError('')
    setSettingsSuccess('')
    setSettingsBusy(true)
    try {
      const res = await fetch(`${apiBase}/auth/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ preferred_lang: nextLang })
      })
      if (!res.ok) throw new Error(await parseError(res))
      const data = await res.json()
      const savedLang = String(data?.preferred_lang || nextLang).toLowerCase() === 'de' ? 'de' : 'en'
      setAuthUser((prev) => (prev ? { ...prev, preferred_lang: savedLang } : prev))
      setUiLang(savedLang)
      setPreferredLanguageDraft(savedLang)
      setSettingsSuccess('Einstellungen gespeichert.')
      if (typeof window !== 'undefined') {
        window.setTimeout(() => {
          window.location.reload()
        }, 120)
      }
    } catch (err) {
      setSettingsError(err?.message || 'Einstellungen konnten nicht gespeichert werden.')
    } finally {
      setSettingsBusy(false)
    }
  }

  useEffect(() => {
    if (!authToken) return
    const fetchMe = async () => {
      try {
        const res = await fetch(`${apiBase}/auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
        if (!res.ok) {
          clearAuth()
          return
        }
        const data = await res.json()
        setAuthUser(normalizeAuthUser(data))
      } catch {
        clearAuth()
      }
    }
    fetchMe()
  }, [apiBase, authToken])

  useEffect(() => {
    if (!authToken || !authUser) return
    const fetchProfiles = async () => {
      try {
        const res = await fetch(`${apiBase}/profiles`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setProfiles(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchApiKeys = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/api-keys`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setApiKeys(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchArchive = async () => {
      try {
        const res = await fetch(`${apiBase}/web/ingest/files`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setArchiveItems(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchExports = async () => {
      try {
        const res = await fetch(`${apiBase}/exports`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setExports(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setCompanies(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchSites = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setSites(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchPos = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}`, { headers: authHeaders })
        if (!res.ok) return
        const data = await res.json()
        setPosList(Array.isArray(data) ? data : [])
      } catch {
        // ignore for demo
      }
    }

    const fetchTenantUsers = async () => {
      if (!canManageUsers) {
        setTenantUsers([])
        return
      }
      try {
        const res = await fetch(`${apiBase}/tenant/users?ts=${Date.now()}`, {
          headers: authHeaders,
          cache: 'no-store'
        })
        if (!res.ok) return
        const data = await res.json()
        const normalized = Array.isArray(data)
          ? data.map((item) => ({ ...item, rights: normalizeTenantUserRights(item?.rights) }))
          : []
        setTenantUsers(normalized)
      } catch {
        // ignore for demo
      }
    }

    const fetchPasskeys = async () => {
      try {
        const res = await fetch(`${apiBase}/auth/passkeys`, { headers: authHeaders })
        if (!res.ok) {
          setPasskeys([])
          return
        }
        const data = await res.json()
        setPasskeys(Array.isArray(data) ? data : [])
      } catch {
        setPasskeys([])
      }
    }

    fetchProfiles()
    fetchApiKeys()
    fetchArchive()
    fetchExports()
    fetchCompanies()
    fetchSites()
    fetchPos()
    fetchTenantUsers()
    fetchPasskeys()
    if (authUser?.role === 'platform_admin') {
      refreshTenantAdmins()
      refreshResellerAdmins()
    } else if (authUser?.role === 'reseller_admin') {
      refreshResellerAdmins()
      refreshResellerTenantAdmins()
      refreshResellerImpersonationPermissions()
    } else if (authUser?.role === 'tenant_admin') {
      refreshTenantImpersonationPermissions()
    }
  }, [apiBase, tenantId, authHeaders, authToken, authUser, canManageUsers])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!exportIncludeDeleted) {
      setArchiveItemsWithDeleted([])
      return
    }
    let cancelled = false
    const fetchArchiveWithDeleted = async () => {
      try {
        const res = await fetch(`${apiBase}/web/ingest/files?include_deleted=1`, { headers: authHeaders })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) {
          setArchiveItemsWithDeleted(Array.isArray(data) ? data : [])
        }
      } catch {
        if (!cancelled) {
          setArchiveItemsWithDeleted([])
        }
      }
    }
    fetchArchiveWithDeleted()
    return () => { cancelled = true }
  }, [apiBase, authHeaders, authToken, authUser, exportIncludeDeleted])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!exportIncludeDeleted) {
      setCompaniesWithDeleted([])
      return
    }
    let cancelled = false
    const fetchCompaniesWithDeleted = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}&include_deleted=1`, { headers: authHeaders })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) {
          setCompaniesWithDeleted(Array.isArray(data) ? data : [])
        }
      } catch {
        if (!cancelled) setCompaniesWithDeleted([])
      }
    }
    fetchCompaniesWithDeleted()
    return () => { cancelled = true }
  }, [apiBase, authHeaders, authToken, authUser, exportIncludeDeleted, tenantId])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!exportIncludeDeleted) {
      setPosListWithDeleted([])
      return
    }
    let cancelled = false
    const fetchPosWithDeleted = async () => {
      try {
        const res = await fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}&include_deleted=1`, { headers: authHeaders })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) {
          setPosListWithDeleted(Array.isArray(data) ? data : [])
        }
      } catch {
        if (!cancelled) setPosListWithDeleted([])
      }
    }
    fetchPosWithDeleted()
    return () => { cancelled = true }
  }, [apiBase, authHeaders, authToken, authUser, exportIncludeDeleted, tenantId])

  useEffect(() => {
    if (!navItems.length) return
    if (navItems.some((item) => item.label === active)) return
    setActive(navItems[0].label)
  }, [navItems, active])

  useEffect(() => {
    if (!authToken || !authUser) return
    if (!hashTarget) return
    if (!navItems.some((item) => item.label === hashTarget)) return
    setActive(hashTarget)
  }, [authToken, authUser, hashTarget, navItems])

  const prevRoleRef = useRef(userRole)
  const prevImpersonationRef = useRef(Boolean(authUser?.impersonation?.active))

  useEffect(() => {
    const isImpersonating = Boolean(authUser?.impersonation?.active)
    const roleChanged = prevRoleRef.current !== userRole
    const impersonationChanged = prevImpersonationRef.current !== isImpersonating

    if (roleChanged || impersonationChanged) {
      setActive('Administration')
    }

    prevRoleRef.current = userRole
    prevImpersonationRef.current = isImpersonating
  }, [userRole, authUser?.impersonation?.active])

  const activeProfilesByCountry = useMemo(() => {
    const map = {}
    for (const p of profiles) {
      if (p?.status !== 'active' || !p?.country) continue
      if (!map[p.country]) map[p.country] = p
    }
    return map
  }, [profiles])

  const selectableCountries = useMemo(
    () => Object.keys(activeProfilesByCountry).sort(),
    [activeProfilesByCountry]
  )

  const uploadCompany = useMemo(
    () => companies.find((c) => c.company_id === uploadCompanyId) || null,
    [companies, uploadCompanyId]
  )

  const uploadCompanySelectOptions = useMemo(() => {
    const query = uploadCompanySearchTerm.trim().toLowerCase()
    if (!query) return companies
    return companies.filter((company) =>
      [company.name || '', company.country || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [companies, uploadCompanySearchTerm])

  const uploadCompanySelectionVisible = useMemo(
    () => uploadCompanySelectOptions.some((company) => company.company_id === uploadCompanyId),
    [uploadCompanyId, uploadCompanySelectOptions]
  )

  const uploadResolvedProfile = useMemo(() => {
    if (!uploadCompany?.country) return null
    return activeProfilesByCountry[uploadCompany.country] || null
  }, [activeProfilesByCountry, uploadCompany])

  const uploadPosOptions = useMemo(() => {
    if (!uploadCompanyId) return posList
    const siteIds = new Set(
      sites.filter((s) => s.company_id === uploadCompanyId).map((s) => s.site_id)
    )
    return posList.filter((p) => siteIds.has(p.site_id))
  }, [posList, sites, uploadCompanyId])

  const uploadContextReady = useMemo(() => {
    return Boolean(uploadCompanyId && uploadResolvedProfile)
  }, [uploadCompanyId, uploadResolvedProfile])
  const uploadDateLocked = uploadBundledFiles.length > 0
  const uploadDialogBlocked = uploadBusy || uploadTransferProgress.active
  const uploadTransferPercent = Number.isFinite(uploadTransferProgress.percent) ? uploadTransferProgress.percent : null
  const uploadTransferActive = uploadTransferProgress.active
  const uploadTransferStatusText = uploadTransferPercent == null
    ? 'Upload läuft. Der Dialog ist gesperrt, bis die Datei vollständig übertragen wurde.'
    : `Upload läuft (${uploadTransferPercent} %). Der Dialog ist gesperrt, bis die Datei vollständig übertragen wurde.`
  const uploadCompanyMissingError = (!uploadCompanyId && !uploadContextReady) || uploadError === 'Bitte ein Unternehmen wählen.'
  const uploadRangeMissingError = uploadError === TIME_RANGE_REQUIRED_ERROR
  const exportRangeMissingError = exportError === TIME_RANGE_REQUIRED_ERROR

  const tenantDisplayName = useMemo(() => {
    return authUser?.tenant_name || companies[0]?.name || authUser?.tenant_id || '-'
  }, [authUser, companies])

  useEffect(() => {
    setTenantNameEdit(tenantDisplayName === '-' ? '' : tenantDisplayName)
  }, [tenantDisplayName])

  const siteNameById = useMemo(
    () => Object.fromEntries(sites.map((s) => [s.site_id, s.name])),
    [sites]
  )

  const companyNameById = useMemo(
    () => Object.fromEntries(companies.map((c) => [c.company_id, c.name])),
    [companies]
  )

  const companyCountryById = useMemo(
    () => {
      const pairs = companies.map((c) => [c.company_id, c.country || ''])
      if (exportIncludeDeleted) {
        for (const c of companiesWithDeleted) {
          pairs.push([c.company_id, c.country || ''])
        }
      }
      return Object.fromEntries(pairs)
    },
    [companies, companiesWithDeleted, exportIncludeDeleted]
  )

  const posNameById = useMemo(() => {
    const siteById = new Map(sites.map((site) => [site.site_id, site.name]))
    const formatExportPosName = (pos) => {
      const posLabel = pos.pos_label || pos.pos_serial
      const siteName = pos.site_name || siteById.get(pos.site_id)
      const baseName = siteName ? `${posLabel} (${siteName})` : posLabel
      return pos?.is_deleted
        ? formatUiTemplate('entity.deleted_at', {
          name: baseName,
          date: formatDateDisplay(pos.deleted_at)
        })
        : baseName
    }
    const entries = posList.map((pos) => [pos.pos_id, formatExportPosName(pos)])
    if (exportIncludeDeleted) {
      for (const p of posListWithDeleted) {
        entries.push([p.pos_id, formatExportPosName(p)])
      }
    }
    return Object.fromEntries(entries)
  }, [posList, posListWithDeleted, sites, exportIncludeDeleted])

  const resolveExportCountry = (item) => {
    const ids = Array.isArray(item?.company_ids) ? item.company_ids : []
    if (!ids.length) return ''
    const countries = new Set(ids.map((id) => companyCountryById[id]).filter(Boolean))
    if (countries.size !== 1) return ''
    return Array.from(countries)[0]
  }

  const filteredApiKeys = useMemo(() => {
    const query = apiKeysSearchTerm.trim().toLowerCase()
    if (!query) return apiKeys
    return apiKeys.filter((item) => {
      const haystack = [
        item.pos_serial || '',
        item.company_name || '',
        item.site_name || '',
        item.reported_company_name || '',
        item.reported_site_name || '',
        item.status || '',
        item.config_status || ''
      ].join(' ').toLowerCase()
      return haystack.includes(query)
    })
  }, [apiKeys, apiKeysSearchTerm])

  const pendingApiKeys = useMemo(
    () => filteredApiKeys.filter((item) => String(item.status || '').toLowerCase() === 'requested'),
    [filteredApiKeys]
  )

  const selectedPendingApiKeyIds = useMemo(
    () => new Set(selectedApiKeyIds),
    [selectedApiKeyIds]
  )

  const filteredOrganizationCompanies = useMemo(() => {
    const query = organizationSearchTerm.trim().toLowerCase()
    if (!query) {
      return companies.map((company) => ({
        ...company,
        sites: sites
          .filter((site) => site.company_id === company.company_id)
          .map((site) => ({
            ...site,
            posItems: posList.filter((pos) => pos.site_id === site.site_id)
          }))
      }))
    }

    return companies.reduce((acc, company) => {
      const companySites = sites
        .filter((site) => site.company_id === company.company_id)
        .map((site) => ({
          ...site,
          posItems: posList.filter((pos) => pos.site_id === site.site_id)
        }))

      const companyMatches = [company.name || '', company.country || '']
        .join(' ')
        .toLowerCase()
        .includes(query)

      const filteredSites = companySites.reduce((siteAcc, site) => {
        const siteMatches = [site.name || '']
          .join(' ')
          .toLowerCase()
          .includes(query)
        if (siteMatches) {
          siteAcc.push(site)
          return siteAcc
        }

        const filteredPos = site.posItems.filter((pos) =>
          [pos.pos_id || '', pos.pos_serial || '', pos.pos_label || '']
            .join(' ')
            .toLowerCase()
            .includes(query)
        )
        if (filteredPos.length) {
          siteAcc.push({ ...site, posItems: filteredPos })
        }
        return siteAcc
      }, [])

      if (companyMatches || filteredSites.length) {
        acc.push({ ...company, sites: filteredSites })
      }
      return acc
    }, [])
  }, [companies, sites, posList, organizationSearchTerm])

  const isOrganizationSearchActive = organizationSearchTerm.trim().length > 0

  const filteredTenantUsers = useMemo(() => {
    const query = tenantUsersSearchTerm.trim().toLowerCase()
    if (!query) return tenantUsers
    return tenantUsers.filter((item) =>
      [
        item.name || '',
        item.email || '',
        item.status === 'locked' ? 'gesperrt locked' : (item.status || '')
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [tenantUsers, tenantUsersSearchTerm])

  useEffect(() => {
    if (!expandedTenantUserId) return
    const stillVisible = filteredTenantUsers.some((item) => item.user_id === expandedTenantUserId)
    if (!stillVisible) setExpandedTenantUserId('')
  }, [filteredTenantUsers, expandedTenantUserId])

  const filteredTenantImpersonationPermissions = useMemo(() => {
    const query = tenantImpersonationSearchTerm.trim().toLowerCase()
    if (!query) return tenantImpersonationPermissions
    return tenantImpersonationPermissions.filter((item) =>
      [
        item.helper_name || '',
        item.helper_email || ''
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [tenantImpersonationPermissions, tenantImpersonationSearchTerm])

  useEffect(() => {
    if (!expandedTenantImpersonationPermissionId) return
    const stillVisible = filteredTenantImpersonationPermissions.some(
      (item) => item.permission_id === expandedTenantImpersonationPermissionId
    )
    if (!stillVisible) setExpandedTenantImpersonationPermissionId('')
  }, [filteredTenantImpersonationPermissions, expandedTenantImpersonationPermissionId])

  const filteredResellerAdmins = useMemo(() => {
    const query = resellerSearchTerm.trim().toLowerCase()
    if (!query) return resellerAdmins
    return resellerAdmins.filter((item) =>
      [
        item.name || '',
        item.email || '',
        item.status || ''
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [resellerAdmins, resellerSearchTerm])

  const filteredResellerImpersonationPermissions = useMemo(() => {
    const query = resellerImpersonationSearchTerm.trim().toLowerCase()
    if (!query) return resellerImpersonationPermissions
    const statusText = (statusRaw) => {
      const status = String(statusRaw || '').toLowerCase()
      if (status === 'approved') return 'Freigegeben'
      if (status === 'requested') return 'Anfrage offen'
      return 'Nicht freigegeben'
    }
    return resellerImpersonationPermissions.filter((item) =>
      [
        item.requester_name || '',
        item.requester_email || '',
        statusText(item.status)
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [resellerImpersonationPermissions, resellerImpersonationSearchTerm])

  useEffect(() => {
    if (!expandedPlatformResellerId) return
    const stillVisible = filteredResellerAdmins.some((item) => item.user_id === expandedPlatformResellerId)
    if (!stillVisible) setExpandedPlatformResellerId('')
  }, [filteredResellerAdmins, expandedPlatformResellerId])

  const resellerNameById = useMemo(
    () => Object.fromEntries(resellerAdmins.map((item) => [item.user_id, item.name || item.email || item.user_id])),
    [resellerAdmins]
  )

  const filteredPlatformTenantAdmins = useMemo(() => {
    const query = platformOrgSearchTerm.trim().toLowerCase()
    if (!query) return tenantAdmins
    return tenantAdmins.filter((item) =>
      [
        item.organization_name || '',
        item.name || '',
        item.email || '',
        item.reseller_admin_id ? (resellerNameById[item.reseller_admin_id] || item.reseller_admin_id) : 'Direkt'
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [tenantAdmins, platformOrgSearchTerm, resellerNameById])

  const resellerById = useMemo(
    () => Object.fromEntries(resellerAdmins.map((item) => [item.user_id, item])),
    [resellerAdmins]
  )

  const resellerChildrenByParent = useMemo(
    () => resellerAdmins.reduce((acc, reseller) => {
      const key = reseller.created_by_reseller_admin_id || '__root__'
      const list = acc[key] || []
      list.push(reseller)
      acc[key] = list
      return acc
    }, {}),
    [resellerAdmins]
  )

  const platformOrganizationRows = useMemo(() => {
    if (authUser?.role !== 'platform_admin') return []
    const tenantsByReseller = tenantAdmins.reduce((acc, item) => {
      const key = item.reseller_admin_id || '__direct__'
      const list = acc[key] || []
      list.push(item)
      acc[key] = list
      return acc
    }, {})
    const rows = []
    const directTenants = tenantsByReseller.__direct__ || []
    for (const item of directTenants) rows.push({ type: 'tenant', level: 0, parentResellerId: '', item })
    const walk = (parentId, level) => {
      const children = resellerChildrenByParent[parentId] || []
      for (const reseller of children) {
        const childResellers = resellerChildrenByParent[reseller.user_id] || []
        const tenants = tenantsByReseller[reseller.user_id] || []
        rows.push({
          type: 'reseller',
          level,
          parentResellerId: parentId === '__root__' ? '' : parentId,
          item: reseller,
          hasChildren: Boolean(childResellers.length || tenants.length)
        })
        for (const tenant of tenants) rows.push({ type: 'tenant', level: level + 1, parentResellerId: reseller.user_id, item: tenant })
        walk(reseller.user_id, level + 1)
      }
    }
    walk('__root__', 0)
    return rows
  }, [authUser?.role, resellerChildrenByParent, tenantAdmins])

  const resellerOrganizationRows = useMemo(() => {
    if (authUser?.role !== 'reseller_admin') return []
    const tenantsByReseller = resellerTenantAdmins.reduce((acc, item) => {
      const key = item.reseller_admin_id || ''
      const list = acc[key] || []
      list.push(item)
      acc[key] = list
      return acc
    }, {})
    const rows = []
    const walk = (resellerId, level, parentResellerId = '') => {
      const reseller = resellerById[resellerId]
      if (!reseller) return
      const tenants = tenantsByReseller[resellerId] || []
      const children = resellerChildrenByParent[resellerId] || []
      rows.push({ type: 'reseller', level, parentResellerId, item: reseller, hasChildren: Boolean(children.length || tenants.length) })
      for (const tenant of tenants) rows.push({ type: 'tenant', level: level + 1, parentResellerId: resellerId, item: tenant })
      for (const child of children) walk(child.user_id, level + 1, resellerId)
    }
    if (authUser?.user_id && resellerById[authUser.user_id]) {
      const selfId = authUser.user_id
      const ownTenants = tenantsByReseller[selfId] || []
      const childResellers = resellerChildrenByParent[selfId] || []
      for (const tenant of ownTenants) rows.push({ type: 'tenant', level: 0, parentResellerId: '', item: tenant })
      for (const child of childResellers) walk(child.user_id, 0, '')
    } else {
      const roots = resellerChildrenByParent.__root__ || []
      for (const root of roots) walk(root.user_id, 0, '')
    }
    return rows
  }, [authUser?.role, authUser?.user_id, resellerById, resellerChildrenByParent, resellerTenantAdmins])

  const filteredPlatformOrganizationRows = useMemo(() => {
    const query = platformOrgSearchTerm.trim().toLowerCase()
    if (!query) return platformOrganizationRows
    const toAdminStatusLabel = (statusRaw) => {
      const status = String(statusRaw || '').toLowerCase()
      if (status === 'active') return 'Aktiv'
      if (status === 'locked' || status === 'inactive' || status === 'disabled') return 'Deaktiviert'
      return '-'
    }
    const rowKey = (row) => `${row.type}:${row.item.user_id}`
    const parentByResellerId = {}
    for (const row of platformOrganizationRows) {
      if (row.type !== 'reseller') continue
      const resellerId = row.item.user_id
      const parentId = row.parentResellerId || ''
      parentByResellerId[resellerId] = parentId
    }
    const matches = platformOrganizationRows.filter((row) => {
      if (row.type === 'reseller') {
        const item = row.item
        return [item.name || '', item.contact_name || '', item.email || '', toAdminStatusLabel(item.status)]
          .join(' ')
          .toLowerCase()
          .includes(query)
      }
      const item = row.item
      return [item.organization_name || '', item.name || '', item.email || '', 'Organisation']
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
    if (!matches.length) return []
    const include = new Set(matches.map((row) => rowKey(row)))
    for (const row of matches) {
      let parentId = row.parentResellerId || ''
      while (parentId) {
        include.add(`reseller:${parentId}`)
        parentId = parentByResellerId[parentId] || ''
      }
    }
    return platformOrganizationRows.filter((row) => include.has(rowKey(row)))
  }, [platformOrgSearchTerm, platformOrganizationRows])

  const filteredResellerOrganizationRows = useMemo(() => {
    const query = resellerSearchTerm.trim().toLowerCase()
    if (!query) return resellerOrganizationRows
    const rowKey = (row) => `${row.type}:${row.item.user_id}`
    const parentByResellerId = {}
    for (const row of resellerOrganizationRows) {
      if (row.type !== 'reseller') continue
      const resellerId = row.item.user_id
      const parentId = row.parentResellerId || ''
      parentByResellerId[resellerId] = parentId
    }
    const matches = resellerOrganizationRows.filter((row) => {
      if (row.type === 'reseller') {
        const item = row.item
        return [item.name || '', item.contact_name || '', item.email || '', 'Reseller']
          .join(' ')
          .toLowerCase()
          .includes(query)
      }
      const item = row.item
      return [item.organization_name || '', item.name || '', item.email || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
    if (!matches.length) return []
    const include = new Set(matches.map((row) => rowKey(row)))
    for (const row of matches) {
      let parentId = row.parentResellerId || ''
      while (parentId) {
        include.add(`reseller:${parentId}`)
        parentId = parentByResellerId[parentId] || ''
      }
    }
    return resellerOrganizationRows.filter((row) => include.has(rowKey(row)))
  }, [resellerOrganizationRows, resellerSearchTerm])

  const handleToggleCompanyCreate = () => {
    setShowCompanyCreate((prev) => !prev)
  }

  const handleNavigateToCompanyStructure = () => {
    setActive('Unternehmensstruktur')
    setOrganizationSearchTerm('')
    refreshOrgTree()
  }

  useEffect(() => {
    const keys = platformOrganizationRows
      .filter((row) => row.type === 'reseller' && row.hasChildren)
      .map((row) => row.item.user_id)
    if (!keys.length) {
      setPlatformOrgExpandedResellerIds({})
      return
    }
    setPlatformOrgExpandedResellerIds((prev) => {
      const next = {}
      let changed = false
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(prev, key)) next[key] = prev[key]
        else {
          next[key] = false
          changed = true
        }
      }
      for (const key of Object.keys(prev)) {
        if (!keys.includes(key)) changed = true
      }
      return changed ? next : prev
    })
  }, [platformOrganizationRows])

  useEffect(() => {
    const keys = resellerOrganizationRows
      .filter((row) => row.type === 'reseller' && row.hasChildren)
      .map((row) => row.item.user_id)
    if (!keys.length) {
      setResellerOrgExpandedResellerIds({})
      return
    }
    setResellerOrgExpandedResellerIds((prev) => {
      const next = {}
      let changed = false
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(prev, key)) next[key] = prev[key]
        else {
          next[key] = false
          changed = true
        }
      }
      for (const key of Object.keys(prev)) {
        if (!keys.includes(key)) changed = true
      }
      return changed ? next : prev
    })
  }, [resellerOrganizationRows])

  const visiblePlatformOrganizationRows = useMemo(() => {
    if (platformOrgSearchTerm.trim()) return filteredPlatformOrganizationRows
    const hiddenParents = new Set()
    const visible = []
    for (const row of filteredPlatformOrganizationRows) {
      const parentId = row.parentResellerId || ''
      if (parentId && hiddenParents.has(parentId)) {
        if (row.type === 'reseller') hiddenParents.add(row.item.user_id)
        continue
      }
      if (row.type === 'reseller') {
        visible.push(row)
        if (!platformOrgExpandedResellerIds[row.item.user_id]) hiddenParents.add(row.item.user_id)
        continue
      }
      visible.push(row)
    }
    return visible
  }, [filteredPlatformOrganizationRows, platformOrgExpandedResellerIds, platformOrgSearchTerm])

  const visibleResellerOrganizationRows = useMemo(() => {
    if (resellerSearchTerm.trim()) return filteredResellerOrganizationRows
    const hiddenParents = new Set()
    const visible = []
    for (const row of filteredResellerOrganizationRows) {
      const parentId = row.parentResellerId || ''
      if (parentId && hiddenParents.has(parentId)) {
        if (row.type === 'reseller') hiddenParents.add(row.item.user_id)
        continue
      }
      if (row.type === 'reseller') {
        visible.push(row)
        if (!resellerOrgExpandedResellerIds[row.item.user_id]) hiddenParents.add(row.item.user_id)
        continue
      }
      visible.push(row)
    }
    return visible
  }, [filteredResellerOrganizationRows, resellerOrgExpandedResellerIds, resellerSearchTerm])

  useEffect(() => {
    if (!expandedPlatformOrgId) return
    const stillVisible = filteredPlatformTenantAdmins.some((item) => item.user_id === expandedPlatformOrgId)
    if (!stillVisible) setExpandedPlatformOrgId('')
  }, [filteredPlatformTenantAdmins, expandedPlatformOrgId])

  useEffect(() => {
    if (!expandedPlatformMobileRowId) return
    const stillVisible = visiblePlatformOrganizationRows.some((row) => `${row.type}:${row.item.user_id}` === expandedPlatformMobileRowId)
    if (!stillVisible) setExpandedPlatformMobileRowId('')
  }, [expandedPlatformMobileRowId, visiblePlatformOrganizationRows])

  useEffect(() => {
    if (!expandedResellerTenantId) return
    const stillVisible = resellerTenantAdmins.some((item) => item.user_id === expandedResellerTenantId)
    if (!stillVisible) setExpandedResellerTenantId('')
  }, [resellerTenantAdmins, expandedResellerTenantId])

  useEffect(() => {
    if (!expandedResellerMobileRowId) return
    const stillVisible = visibleResellerOrganizationRows.some((row) => `${row.type}:${row.item.user_id}` === expandedResellerMobileRowId)
    if (!stillVisible) setExpandedResellerMobileRowId('')
  }, [expandedResellerMobileRowId, visibleResellerOrganizationRows])

  const getImpersonationStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'approved') return { key: 'approved', label: 'OK', title: 'Freigegeben' }
    if (status === 'requested') return { key: 'requested', label: 'Pending', title: 'Anfrage offen' }
    return { key: 'revoked', label: 'Stop', title: 'Nicht freigegeben' }
  }

  const getTenantUserStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'locked') return { key: 'locked', label: 'Gesperrt' }
    return { key: 'active', label: 'Aktiv' }
  }

  const getAdminStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'active') return { key: 'active', label: 'Aktiv' }
    if (status === 'locked' || status === 'inactive' || status === 'disabled') return { key: 'disabled', label: 'Deaktiviert' }
    return { key: 'unknown', label: '-' }
  }

  const hasImpersonationApproval = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase()
    return value === true || value === 1 || normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'ja' || normalized === 'approved'
  }

  const isImpersonationApproved = (item) => {
    const status = String(item?.impersonation_status || '').trim().toLowerCase()
    return hasImpersonationApproval(item?.impersonation_allowed) || status === 'approved'
  }

  const getResellerImpersonationStatus = (item) => {
    if (isImpersonationApproved(item)) return 'approved'
    const status = String(item?.impersonation_status || '').trim().toLowerCase()
    if (status === 'requested') return 'requested'
    return 'revoked'
  }

  const activeExport = useMemo(() => {
    const inProgress = exports.find((item) => ['queued', 'running', 'requested', 'pending'].includes((item.status || '').toLowerCase()))
    return inProgress || null
  }, [exports])
  const exportStatusLabel = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase()
    if (status === 'expired') return 'Abgelaufen'
    if (status === 'completed') return 'OK'
    return String(statusRaw || '-')
  }

  const latestExports = useMemo(() => exports.slice(0, 10), [exports])

  const exportTooltipText = (item) => {
    const companyLabels = (item.company_ids || []).map((id) => companyNameById[id] || id)
    const siteLabels = (item.site_ids || []).map((id) => siteNameById[id] || id)
    const posLabels = (item.pos_ids || []).map((id) => posNameById[id] || id)
    const exportCountry = resolveExportCountry(item)
    const period = item.business_time_from && item.business_time_to
      ? formatUiTemplate('range.from_to', {
        from: formatDateDisplay(item.business_time_from),
        to: formatDateDisplay(item.business_time_to)
      })
      : '-'
    const artifactLabel = Array.isArray(item.artifact_types) && item.artifact_types.length
      ? item.artifact_types.map((type) => getArtifactTypeLabel(type, exportCountry)).join(', ')
      : getArtifactTypeLabel(item.artifact_type, exportCountry)
    return [
      formatUiTemplate('export.tooltip.artifact_group', { value: artifactLabel }),
      formatUiTemplate('export.tooltip.company', { value: companyLabels.length ? companyLabels.join(', ') : '-' }),
      formatUiTemplate('export.tooltip.site', { value: siteLabels.length ? siteLabels.join(', ') : '-' }),
      formatUiTemplate('export.tooltip.pos', { value: posLabels.length ? posLabels.join(', ') : '-' }),
      formatUiTemplate('export.tooltip.period', { value: period })
    ].join('\n')
  }

  const exportFilterItems = useMemo(
    () => (exportIncludeDeleted ? archiveItemsWithDeleted : archiveItems),
    [exportIncludeDeleted, archiveItemsWithDeleted, archiveItems]
  )

  const exportCompanyOptions = useMemo(() => {
    const active = companies.map((c) => ({
      company_id: c.company_id,
      name: c.name,
      country: c.country,
      is_deleted: false
    }))
    if (!exportIncludeDeleted) return active
    const activeIds = new Set(active.map((c) => c.company_id))
    const deletedMap = new Map()
    companiesWithDeleted.forEach((item) => {
      if (!item?.company_id || activeIds.has(item.company_id) || !item?.is_deleted) return
      const name = item.name || item.company_id
      deletedMap.set(item.company_id, {
        company_id: item.company_id,
        name: formatUiTemplate('entity.deleted', { name }),
        country: item.country || null,
        is_deleted: true
      })
    })
    exportFilterItems.forEach((item) => {
      if (!item?.company_id || !item?.company_is_deleted || activeIds.has(item.company_id) || deletedMap.has(item.company_id)) return
      if (!deletedMap.has(item.company_id)) {
        const name = item.company_name || item.company_id
        deletedMap.set(item.company_id, {
          company_id: item.company_id,
          name: formatUiTemplate('entity.deleted', { name }),
          country: item.company_country || null,
          is_deleted: true
        })
      }
    })
    return [...active, ...Array.from(deletedMap.values())]
  }, [companies, companiesWithDeleted, exportIncludeDeleted, exportFilterItems])

  const exportSelectedCompanies = useMemo(
    () => exportCompanyOptions.filter((c) => exportCompanyIds.includes(c.company_id)),
    [exportCompanyOptions, exportCompanyIds]
  )

  const exportPosOptions = useMemo(() => {
    if (!exportCompanyIds.length) return []
    if (exportCompanyIds.length > 1) return []
    const allowedCompanyIds = new Set(exportCompanyIds)
    const siteById = new Map(sites.map((s) => [s.site_id, s]))
    const posSource = exportIncludeDeleted ? posListWithDeleted : posList
    const scopedPos = posSource.filter((p) => {
      const companyId = p.company_id || siteById.get(p.site_id)?.company_id
      return companyId && allowedCompanyIds.has(companyId)
    })
    const deletedPosIds = new Set(
      exportFilterItems
        .filter((item) => item?.pos_id && item?.pos_is_deleted)
        .map((item) => item.pos_id)
    )
    const posOptions = scopedPos.map((p) => {
      const siteName = p.site_name || siteById.get(p.site_id)?.name
      const isDeleted = Boolean(p.is_deleted) || deletedPosIds.has(p.pos_id)
      const posLabel = p.pos_label || p.pos_serial
      const baseName = siteName ? `${posLabel} (${siteName})` : posLabel
      return {
        ...p,
        name: isDeleted
          ? formatUiTemplate('entity.deleted_at', {
            name: baseName,
            date: formatDateDisplay(p.deleted_at)
          })
          : baseName
      }
    })

    return posOptions
  }, [posList, posListWithDeleted, sites, exportCompanyIds, exportIncludeDeleted, exportFilterItems])

  const exportProfile = useMemo(() => {
    if (!exportSelectedCompanies.length) return null
    const profileRefs = exportSelectedCompanies.map((company) => {
      const profile = activeProfilesByCountry[company.country]
      return profile?.profile_ref || null
    })
    if (profileRefs.some((ref) => !ref)) return null
    const uniqueRefs = new Set(profileRefs)
    if (uniqueRefs.size !== 1) return null
    const firstCompany = exportSelectedCompanies[0]
    return activeProfilesByCountry[firstCompany.country] || null
  }, [activeProfilesByCountry, exportSelectedCompanies])

  const exportCalendarCells = useMemo(() => buildCalendarCells(exportPickerMonth), [exportPickerMonth])
  const uploadCalendarCells = useMemo(() => buildCalendarCells(uploadPickerMonth), [uploadPickerMonth])
  const exportCategoryOptions = useMemo(
    () => {
      const exportCountry = exportProfile?.country || ''
      return Object.entries(exportPreview?.counts || {}).map(([type, count]) => ({
        value: type,
        label: formatUiTemplate('export.category.count', {
          label: getArtifactTypeLabel(type, exportCountry),
          count
        })
      }))
    },
    [exportPreview, exportProfile]
  )
  const exportEffectiveItems = useMemo(() => {
    const typeSet = new Set(exportSelectedArtifactTypes)
    return (exportPreview?.items || []).filter((item) => {
      const type = item.artifact_type || 'unknown'
      if (!typeSet.has(type)) return false
      return true
    })
  }, [exportPreview, exportSelectedArtifactTypes])
  const _exportEffectiveCounts = useMemo(
    () => exportEffectiveItems.reduce((acc, item) => {
      const key = item.artifact_type || 'unknown'
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {}),
    [exportEffectiveItems]
  )
  const exportSelectedCountTotal = exportEffectiveItems.length

  const toggleCompanyExpand = (companyId) => {
    setExpandedCompanies((prev) => ({ ...prev, [companyId]: !prev[companyId] }))
  }

  const toggleSiteExpand = (siteId) => {
    setExpandedSites((prev) => ({ ...prev, [siteId]: !prev[siteId] }))
  }

  const togglePlatformOrgResellerExpand = (userId) => {
    setPlatformOrgExpandedResellerIds((prev) => ({ ...prev, [userId]: !prev[userId] }))
  }

  const toggleResellerOrgResellerExpand = (userId) => {
    setResellerOrgExpandedResellerIds((prev) => ({ ...prev, [userId]: !prev[userId] }))
  }

  useEffect(() => {
    const validCompanyIds = new Set(companies.map((company) => company.company_id))
    setExpandedCompanies((prev) => {
      const next = {}
      let changed = false
      for (const company of companies) {
        if (Object.prototype.hasOwnProperty.call(prev, company.company_id)) {
          next[company.company_id] = prev[company.company_id]
        } else {
          next[company.company_id] = false
          changed = true
        }
      }
      for (const existingId of Object.keys(prev)) {
        if (!validCompanyIds.has(existingId)) changed = true
      }
      return changed ? next : prev
    })
  }, [companies])

  useEffect(() => {
    const validSiteIds = new Set(sites.map((site) => site.site_id))
    setExpandedSites((prev) => {
      const next = {}
      let changed = false
      for (const site of sites) {
        if (Object.prototype.hasOwnProperty.call(prev, site.site_id)) {
          next[site.site_id] = prev[site.site_id]
        } else {
          next[site.site_id] = false
          changed = true
        }
      }
      for (const existingId of Object.keys(prev)) {
        if (!validSiteIds.has(existingId)) changed = true
      }
      return changed ? next : prev
    })
  }, [sites])

  useEffect(() => {
    if (uploadDateLocked) return
    if (uploadCompanySearchTerm.trim()) return
    if (!uploadCompanyId && companies.length === 1) {
      setUploadCompanyId(companies[0].company_id)
      return
    }
    if (!uploadCompanyId || companies.length <= 1) return
    if (!companies.some((company) => company.company_id === uploadCompanyId)) {
      setUploadCompanyId('')
    }
  }, [companies, uploadCompanyId, uploadCompanySearchTerm, uploadDateLocked])

  useEffect(() => {
    if (companies.length < UI_SEARCH_THRESHOLD && uploadCompanySearchTerm) {
      setUploadCompanySearchTerm('')
    }
  }, [companies.length, uploadCompanySearchTerm])

  useEffect(() => {
    if (uploadDateLocked) return
    if (!uploadCompanySearchTerm.trim()) return
    if (uploadCompanySelectOptions.length > 1) {
      if (uploadCompanyId) setUploadCompanyId('')
      return
    }
    if (uploadCompanySelectOptions.length !== 1) return
    const onlyMatchId = uploadCompanySelectOptions[0]?.company_id || ''
    if (!onlyMatchId || onlyMatchId === uploadCompanyId) return
    setUploadCompanyId(onlyMatchId)
  }, [uploadDateLocked, uploadCompanyId, uploadCompanySearchTerm, uploadCompanySelectOptions])

  useEffect(() => {
    if (!exportCompanyIds.length && exportCompanyOptions.length) {
      setExportCompanyIds([exportCompanyOptions[0].company_id])
    }
  }, [exportCompanyOptions, exportCompanyIds])

  useEffect(() => {
    if (!exportCompanyIds.length) return
    const validCompanyIds = new Set(exportCompanyOptions.map((c) => c.company_id))
    const cleaned = exportCompanyIds.filter((id) => validCompanyIds.has(id))
    if (cleaned.length !== exportCompanyIds.length) {
      setExportCompanyIds(cleaned)
      setExportPosIds([])
    }
  }, [exportCompanyOptions, exportCompanyIds])

  useEffect(() => {
    if (!selectableCountries.length) return
    if (companyCountry && selectableCountries.includes(companyCountry)) return
    if (selectableCountries.includes('DE')) {
      setCompanyCountry('DE')
      return
    }
    setCompanyCountry(selectableCountries[0])
  }, [companyCountry, selectableCountries])

  useEffect(() => {
    if (!uploadPosRefs.length) return
    const validPosIds = new Set(uploadPosOptions.map((p) => p.pos_id))
    const cleaned = uploadPosRefs.filter((id) => validPosIds.has(id)).slice(-1)
    if (cleaned.length !== uploadPosRefs.length || cleaned[0] !== uploadPosRefs[0]) {
      setUploadPosRefs(cleaned)
    }
  }, [uploadPosOptions, uploadPosRefs])

  useEffect(() => {
    if (uploadArtifactType !== 'free') {
      setUploadFreeArtifactLabel('')
    }
  }, [uploadArtifactType])

  useEffect(() => {
    if (uploadDateLocked) {
      setUploadPickerOpen(false)
    }
  }, [uploadDateLocked])

  useEffect(() => {
    if (exportPosIds.length && exportPosIds.some((id) => !exportPosOptions.some((p) => p.pos_id === id))) {
      setExportPosIds([])
    }
  }, [exportPosIds, exportPosOptions])

  const handleExportDatePick = (isoDate) => {
    if (!isoDate) return
    setExportError('')
    setExportSuccess('')
    setExportFilterApplied(false)
    setExportPreview(null)
    if (!exportRangeStart || (exportRangeStart && exportRangeEnd)) {
      setExportRangeStart(isoDate)
      setExportRangeEnd('')
      return
    }
    if (isoDate < exportRangeStart) {
      setExportRangeEnd(exportRangeStart)
      setExportRangeStart(isoDate)
    } else {
      setExportRangeEnd(isoDate)
    }
    setExportPickerOpen(false)
  }

  const handleExportPickerMonth = (delta) => {
    setExportPickerMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const handleExportPickerYear = (deltaYears) => {
    setExportPickerMonth((prev) => new Date(prev.getFullYear() + deltaYears, prev.getMonth(), 1))
  }

  const handleUploadDatePick = (isoDate) => {
    if (!isoDate || uploadDateLocked) return
    setUploadError('')
    setUploadSuccess('')
    if (!uploadFrom || (uploadFrom && uploadTo)) {
      setUploadFrom(isoDate)
      setUploadTo('')
      return
    }
    if (isoDate < uploadFrom) {
      setUploadTo(uploadFrom)
      setUploadFrom(isoDate)
    } else {
      setUploadTo(isoDate)
    }
    setUploadPickerOpen(false)
  }

  const handleUploadPickerMonth = (delta) => {
    if (uploadDateLocked) return
    setUploadPickerMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const handleUploadPickerYear = (deltaYears) => {
    if (uploadDateLocked) return
    setUploadPickerMonth((prev) => new Date(prev.getFullYear() + deltaYears, prev.getMonth(), 1))
  }

  const runExportFilter = async (includeDeletedOverride = exportIncludeDeleted) => {
    setExportError('')
    setExportSuccess('')
    if (!exportCompanyIds.length) {
      setExportError('Bitte mindestens eine Firma wählen.')
      return
    }
    if (!exportRangeStart || !exportRangeEnd) {
      setExportError(TIME_RANGE_REQUIRED_ERROR)
      return
    }
    if (exportRangeEnd < exportRangeStart) {
      setExportError('Zeitraum ungültig: "Bis" liegt vor "Von".')
      return
    }

    let exportSourceItems = includeDeletedOverride ? archiveItemsWithDeleted : archiveItems
    try {
      const url = includeDeletedOverride
        ? `${apiBase}/web/ingest/files?include_deleted=1`
        : `${apiBase}/web/ingest/files`
      const res = await fetch(url, { headers: authHeaders })
      if (!res.ok) {
        setExportError(await parseError(res))
        return
      }
      const data = await res.json()
      exportSourceItems = Array.isArray(data) ? data : []
      if (includeDeletedOverride) {
        setArchiveItemsWithDeleted(exportSourceItems)
      } else {
        setArchiveItems(exportSourceItems)
      }
    } catch {
      setExportError(includeDeletedOverride ? 'Gelöschte Elemente konnten nicht geladen werden.' : 'Archivdaten konnten nicht geladen werden.')
      return
    }

    const filtered = exportSourceItems.filter((item) => {
      const itemFrom = (item.business_time_from || '').slice(0, 10)
      const itemTo = (item.business_time_to || '').slice(0, 10)
      if (!itemFrom || !itemTo) return false
      if (!includeDeletedOverride && item?.company_is_deleted) return false
      if (!includeDeletedOverride && item?.site_is_deleted) return false
      if (!includeDeletedOverride && item?.pos_is_deleted) return false
      if (item.company_id && !exportCompanyIds.includes(item.company_id)) return false
      if (exportPosIds.length > 0) {
        const hasPos = Boolean(item.pos_id)
        const posMatches = hasPos && exportPosIds.includes(item.pos_id)
        const globalUploadMatches = !hasPos
        if (!posMatches && !globalUploadMatches) return false
      }
      // Overlap logic: include if artifact period intersects selected range.
      if (itemTo < exportRangeStart || itemFrom > exportRangeEnd) return false
      return true
    })

    const counts = filtered.reduce((acc, item) => {
      const key = item.artifact_type || 'unknown'
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})

    const initialSelectedTypes = Object.keys(counts)
    setExportSelectedArtifactTypes(initialSelectedTypes)
    setExportPreview({ counts, total: filtered.length, items: filtered })
    setExportFilterApplied(true)
  }

  const handleRunExport = async () => {
    setExportError('')
    setExportSuccess('')
    if (!exportFilterApplied || !exportPreview || exportPreview.total <= 0) {
      setExportError('Bitte zuerst Filter anwenden und Treffer erzeugen.')
      return
    }
    if (!exportProfile?.profile_ref) {
      setExportError('Kein gemeinsames wirksames Profil für die gewählte Firma-Auswahl verfügbar.')
      return
    }
    if (!exportSelectedArtifactTypes.length) {
      setExportError('Bitte mindestens eine Kategorie auswählen.')
      return
    }
    setExportBusy(true)
    try {
      const res = await fetch(`${apiBase}/exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          profile_ref: exportProfile.profile_ref,
          business_time_from: exportRangeStart,
          business_time_to: exportRangeEnd,
          ...(exportCompanyIds.length === 1 ? { company_id: exportCompanyIds[0] } : {}),
          ...(exportCompanyIds.length > 1 ? { company_ids: exportCompanyIds } : {}),
          ...(exportPosIds.length === 1 ? { pos_id: exportPosIds[0] } : {}),
          ...(exportPosIds.length > 1 ? { pos_ids: exportPosIds } : {}),
          artifact_types: exportSelectedArtifactTypes,
          include_deleted: exportIncludeDeleted,
          ...(String(exportAuditorEmail || '').trim() ? { auditor_email: String(exportAuditorEmail).trim() } : {})
        })
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      const data = await res.json()
      setExportSuccess(formatUiTemplate('export.created', { id: data.export_id }))
      const listRes = await fetch(`${apiBase}/exports`, { headers: authHeaders })
      if (listRes.ok) {
        const list = await listRes.json()
        setExports(Array.isArray(list) ? list : [])
      }
    } catch (err) {
      setExportError(err?.message || 'Export fehlgeschlagen.')
    } finally {
      setExportBusy(false)
    }
  }

  const refreshApiKeys = async () => {
    const res = await fetch(`${apiBase}/tenant/api-keys?ts=${Date.now()}`, {
      headers: authHeaders,
      cache: 'no-store'
    })
    if (res.ok) setApiKeys(await res.json())
  }

  const resetApiKeyOnboardingCode = () => {
    setApiKeyOnboardingCode({
      code: '',
      busy: false,
      error: '',
      copied: false
    })
  }

  const handleCreateOnboardingCode = async () => {
    setApiKeyOnboardingCode((prev) => ({
      ...prev,
      busy: true,
      error: '',
      copied: false
    }))
    try {
      const res = await fetch(`${apiBase}/tenant/api-keys/onboarding-code`, {
        method: 'POST',
        headers: authHeaders
      })
      if (!res.ok) {
        const message = await parseError(res)
        const errorMessage = res.status === 401
          ? uiLiteral('Bitte erneut anmelden.')
          : (res.status === 403
              ? (message || uiLiteral('Keine Berechtigung, einen Onboarding-Code zu erzeugen.'))
              : (res.status === 410
                  ? uiLiteral('Mandant wurde gelöscht.')
                  : (message || uiLiteral('Onboarding-Code konnte nicht erzeugt werden.'))))
        throw new Error(errorMessage)
      }
      const data = await res.json()
      setApiKeyOnboardingCode({
        code: String(data?.onboarding_code || ''),
        busy: false,
        error: '',
        copied: false
      })
    } catch (err) {
      const message = String(err?.message || '')
      setApiKeyOnboardingCode((prev) => ({
        ...prev,
        busy: false,
        error: message || uiLiteral('Onboarding-Code konnte nicht erzeugt werden.'),
        copied: false
      }))
    }
  }

  const handleOnboardingCodeGenerationRequest = () => {
    if (
      apiKeyOnboardingCode.code
      && !window.confirm(formatUiTemplate('confirm.onboarding_code.generate_another'))
    ) {
      return
    }
    handleCreateOnboardingCode()
  }

  const handleCopyOnboardingCode = async () => {
    const code = String(apiKeyOnboardingCode.code || '').trim()
    if (!code) return
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code)
        setApiKeyOnboardingCode((prev) => ({ ...prev, copied: true, error: '' }))
        return
      }
      throw new Error('Clipboard nicht verfügbar.')
    } catch {
      setApiKeyOnboardingCode((prev) => ({
        ...prev,
        copied: false,
        error: uiLiteral('Onboarding-Code konnte nicht kopiert werden.')
      }))
    }
  }

  const buildBulkApiKeyApprovalPayload = (item) => {
    const companyName = String(item.reported_company_name || item.company_name || '').trim()
    const companyCountry = String(
      item.reported_company_country ||
      companies.find((company) => String(company.name || '').trim().toLowerCase() === companyName.toLowerCase())?.country ||
      ''
    ).trim().toUpperCase()
    const matchingCompany = companies.find((company) => {
      if (company.is_deleted || String(company.name || '').trim().toLowerCase() !== companyName.toLowerCase()) return false
      return !companyCountry || String(company.country || '').trim().toUpperCase() === companyCountry
    })
    const siteName = String(item.reported_site_name || item.site_name || '').trim()
    if (!companyName || !companyCountry || !siteName) return null
    return {
      ...(matchingCompany
        ? { company_id: matchingCompany.company_id }
        : { company_name: companyName, company_country: companyCountry }),
      site_name: siteName
    }
  }

  const toggleApiKeySelection = (apiKeyId) => {
    const id = String(apiKeyId || '').trim()
    if (!id) return
    setSelectedApiKeyIds((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ))
  }

  const toggleAllPendingApiKeys = () => {
    const pendingIds = pendingApiKeys.map((item) => String(item.api_key_id || '').trim()).filter(Boolean)
    const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedPendingApiKeyIds.has(id))
    setSelectedApiKeyIds(allSelected ? [] : pendingIds)
  }

  const handleApproveSelectedApiKeys = async () => {
    const selectedItems = apiKeys.filter((item) => (
      selectedPendingApiKeyIds.has(String(item.api_key_id || '')) &&
      String(item.status || '').toLowerCase() === 'requested'
    ))
    if (!selectedItems.length || bulkApiKeyApprovalBusy) return
    setBulkApiKeyApprovalBusy(true)
    setBulkApiKeyApprovalStatus('')
    const failed = []
    let approvedCount = 0
    for (const item of selectedItems) {
      const payload = buildBulkApiKeyApprovalPayload(item)
      if (!payload) {
        failed.push(item.api_key_id)
        continue
      }
      try {
        const res = await fetch(`${apiBase}/tenant/api-keys/${item.api_key_id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify(payload)
        })
        if (!res.ok) {
          failed.push(item.api_key_id)
          continue
        }
        approvedCount += 1
        setApiKeys((prev) => prev.map((current) => (
          current.api_key_id === item.api_key_id
            ? { ...current, status: 'approved', config_status: 'ok' }
            : current
        )))
      } catch {
        failed.push(item.api_key_id)
      }
    }
    setSelectedApiKeyIds(failed)
    await refreshOrgTree()
    setBulkApiKeyApprovalStatus(
      formatUiTemplate(
        failed.length ? 'api_keys.bulk.result_failed' : 'api_keys.bulk.result',
        { approved: approvedCount, failed: failed.length }
      )
    )
    setBulkApiKeyApprovalBusy(false)
  }

  const mergeApiKeyStatus = (apiKeyId, nextStatus) => {
    const id = String(apiKeyId || '').trim()
    const status = String(nextStatus || '').trim().toLowerCase()
    if (!id || !status) return
    setApiKeys((prev) => prev.map((item) => (String(item.api_key_id || '') === id ? { ...item, status } : item)))
  }

  const openApiKeyActionDialog = (item, mode) => {
    const mappedPos = posList.find((pos) => String(pos.pos_id || '') === String(item.pos_id || ''))
    const reportedLabel = item.reported_pos_label || ''
    const reportedTseType = item.reported_tse_type || ''
    const reportedTseSerialNumber = item.reported_tse_serial_number || ''
    setApiKeyActionDialog({
      open: true,
      mode,
      apiKey: item.api_key_id || '',
      posId: item.pos_id || '',
      posSerial: item.pos_serial || '',
      companyId: '',
      siteId: '',
      createCompany: false,
      createSite: false,
      newCompanyName: '',
      newCompanyCountry: item.reported_company_country || 'DE',
      newSiteName: '',
      reportedCompanyName: item.reported_company_name || '',
      reportedCompanyCountry: item.reported_company_country || '',
      reportedSiteName: item.reported_site_name || '',
      reportedPosSerial: item.reported_pos_serial || item.pos_serial || '',
      reportedPosLabel: reportedLabel,
      reportedTseType,
      reportedTseSerialNumber,
      labelChanged: !mappedPos || String(mappedPos.pos_label || '') !== String(reportedLabel),
      tseTypeChanged: !mappedPos || normalizePosTseType(mappedPos.tse_type) !== normalizePosTseType(reportedTseType),
      tseSerialNumberChanged: !mappedPos || String(mappedPos.tse_serial_number || '') !== String(reportedTseSerialNumber),
      status: '',
      busy: false
    })
  }

  const closeApiKeyActionDialog = () => {
    setApiKeyActionDialog({
      open: false,
      mode: 'approve',
      apiKey: '',
      posId: '',
      posSerial: '',
      companyId: '',
      siteId: '',
      createCompany: false,
      createSite: false,
      newCompanyName: '',
      newCompanyCountry: 'DE',
      newSiteName: '',
      reportedCompanyName: '',
      reportedCompanyCountry: '',
      reportedSiteName: '',
      reportedPosSerial: '',
      reportedPosLabel: '',
      reportedTseType: '',
      reportedTseSerialNumber: '',
      labelChanged: false,
      tseTypeChanged: false,
      tseSerialNumberChanged: false,
      status: '',
      busy: false
    })
  }

  const handleApproveOrRefreshApiKey = async () => {
    if (apiKeyActionDialog.mode === 'refresh') {
      setApiKeyActionDialog((prev) => ({ ...prev, busy: true, status: '' }))
      try {
        const res = await fetch(`${apiBase}/tenant/api-keys/${apiKeyActionDialog.apiKey}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({})
        })
        if (!res.ok) {
          const msg = await parseError(res)
          setApiKeyActionDialog((prev) => ({ ...prev, status: msg || 'Aktualisierung fehlgeschlagen.' }))
          return
        }
        await refreshApiKeys()
        closeApiKeyActionDialog()
      } finally {
        setApiKeyActionDialog((prev) => ({ ...prev, busy: false }))
      }
      return
    }
    const companyId = String(apiKeyActionDialog.companyId || '').trim()
    const siteId = String(apiKeyActionDialog.siteId || '').trim()
    if (!apiKeyActionDialog.createCompany && !companyId) {
      setApiKeyActionDialog((prev) => ({
        ...prev,
        status: canEditCompanies
          ? 'Bitte Unternehmen waehlen oder neu anlegen.'
          : 'Bitte ein bestehendes Unternehmen waehlen. Die Neuanlage erfordert das Recht Unternehmen verwalten.'
      }))
      return
    }
    if (!apiKeyActionDialog.createSite && !siteId) {
      setApiKeyActionDialog((prev) => ({
        ...prev,
        status: canEditSites
          ? 'Bitte Betriebsstaette waehlen oder neu anlegen.'
          : 'Bitte eine bestehende Betriebsstaette waehlen. Die Neuanlage erfordert das Recht Betriebsstaetten verwalten.'
      }))
      return
    }
    const payload = {}
    if (apiKeyActionDialog.createCompany) {
      const companyName = String(apiKeyActionDialog.newCompanyName || '').trim()
      const companyCountry = String(apiKeyActionDialog.newCompanyCountry || '').trim().toUpperCase()
      if (!companyName || !companyCountry) {
        setApiKeyActionDialog((prev) => ({ ...prev, status: 'Bitte neuen Unternehmensnamen und Land angeben.' }))
        return
      }
      payload.company_name = companyName
      payload.company_country = companyCountry
    } else if (companyId) {
      payload.company_id = companyId
    }
    if (apiKeyActionDialog.createSite) {
      const siteName = String(apiKeyActionDialog.newSiteName || '').trim()
      if (!siteName) {
        setApiKeyActionDialog((prev) => ({ ...prev, status: 'Bitte neuen Betriebsstättennamen angeben.' }))
        return
      }
      payload.site_name = siteName
    } else if (siteId) {
      payload.site_id = siteId
    }
    if ((!payload.company_id && !payload.company_name) || (!payload.site_id && !payload.site_name)) {
      setApiKeyActionDialog((prev) => ({ ...prev, status: 'Bitte Unternehmen/Betriebsstätte wählen oder neu anlegen.' }))
      return
    }
    setApiKeyActionDialog((prev) => ({ ...prev, busy: true, status: '' }))
    try {
      const res = await fetch(`${apiBase}/tenant/api-keys/${apiKeyActionDialog.apiKey}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const msg = await parseError(res)
        setApiKeyActionDialog((prev) => ({ ...prev, busy: false, status: msg || 'Aktion fehlgeschlagen.' }))
        return
      }
      await refreshApiKeys()
      setSelectedApiKeyIds((prev) => prev.filter((id) => id !== apiKeyActionDialog.apiKey))
      closeApiKeyActionDialog()
    } finally {
      setApiKeyActionDialog((prev) => ({ ...prev, busy: false }))
    }
  }

  const handleDeactivateApiKey = async (key) => {
    const apiKey = String(key || '').trim()
    if (!apiKey) return
    // Immediate UI response.
    mergeApiKeyStatus(apiKey, 'deactivated')
    const res = await fetch(`${apiBase}/tenant/api-keys/${apiKey}/deactivate`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) {
      await refreshApiKeys()
      return
    }
    try {
      const data = await res.json()
      mergeApiKeyStatus(data?.api_key_id || apiKey, data?.status || 'deactivated')
    } catch {
      // ignore parse fallback
    }
    window.setTimeout(() => {
      refreshApiKeys()
    }, 300)
  }

  const handleDeleteApiKey = async (key) => {
    await fetch(`${apiBase}/tenant/api-keys/${key}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    await refreshApiKeys()
  }

  const handleReactivateApiKey = async (key) => {
    const apiKey = String(key || '').trim()
    if (!apiKey) return
    // Immediate UI response.
    mergeApiKeyStatus(apiKey, 'approved')
    const res = await fetch(`${apiBase}/tenant/api-keys/${apiKey}/reactivate`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!res.ok) {
      await refreshApiKeys()
      return
    }
    try {
      const data = await res.json()
      mergeApiKeyStatus(data?.api_key_id || apiKey, data?.status || 'approved')
    } catch {
      // ignore parse fallback
    }
    window.setTimeout(() => {
      refreshApiKeys()
    }, 300)
  }

  const renderApiActionIcon = (symbol, title, onClick, options = {}) => (
    <button
      type="button"
      className={`icon-action-btn${options.danger ? ' is-danger' : ''}`}
      data-i18n-live="1"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={Boolean(options.disabled)}
    >
      <span aria-hidden="true">{symbol}</span>
    </button>
  )

  const renderApiKeyActions = (item) => {
    if (!canManageApiKeys) return <span>-</span>
    const isDialogOpen = apiKeyActionDialog.open && apiKeyActionDialog.apiKey === (item.api_key_id || '')
    if (isDialogOpen) return <span className="hint">Dialog geöffnet</span>
    const status = String(item.status || '').toLowerCase()
    const configStatus = String(item.config_status || '').toLowerCase()
    if (status === 'requested') {
      return (
        <>
          {renderApiActionIcon('✓', 'Freigeben', () => openApiKeyActionDialog(item, 'approve'))}
          {renderApiActionIcon('✕', 'Zurückweisen', () => handleDeleteApiKey(item.api_key_id), { danger: true })}
        </>
      )
    }
    if (status === 'approved' && configStatus === 'updated') {
      return (
        <>
          {renderApiActionIcon('↻', 'Aktualisieren', () => openApiKeyActionDialog(item, 'refresh'))}
          {renderApiActionIcon('✕', 'Löschen', () => handleDeleteApiKey(item.api_key_id), { danger: true })}
        </>
      )
    }
    if (status === 'approved') {
      return (
        <>
          {renderApiActionIcon('⏸', 'Deaktivieren', () => handleDeactivateApiKey(item.api_key_id))}
          {renderApiActionIcon('✕', 'Löschen', () => handleDeleteApiKey(item.api_key_id), { danger: true })}
        </>
      )
    }
    if (status === 'deactivated') {
      return (
        <>
          {renderApiActionIcon('▶', 'Reaktivieren', () => handleReactivateApiKey(item.api_key_id))}
          {renderApiActionIcon('✕', 'Löschen', () => handleDeleteApiKey(item.api_key_id), { danger: true })}
        </>
      )
    }
    return <span>-</span>
  }

  const mapApiKeyStateLabel = (item) => {
    const status = String(item?.status || '').toLowerCase()
    const configStatus = String(item?.config_status || '').toLowerCase()
    if (status === 'requested') return 'Neu'
    if (status === 'approved' && configStatus === 'updated') return 'Updated'
    if (status === 'approved') return 'OK'
    if (status === 'deactivated') return 'Deaktiviert'
    return status || '-'
  }

  const handleCreateCompany = async (name, country) => {
    if (!name || !country) return
    await fetch(`${apiBase}/tenant/companies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId, name, country })
    })
    const res = await fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}`, { headers: authHeaders })
    if (res.ok) setCompanies(await res.json())
  }

  const handleCreateSite = async (companyId, name) => {
    if (!companyId || !name) return
    await fetch(`${apiBase}/tenant/sites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId, company_id: companyId, name })
    })
    const res = await fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}`, { headers: authHeaders })
    if (res.ok) setSites(await res.json())
  }

  const openCreatePosDialog = (siteId) => {
    setCreatePosDialog({
      siteId,
      posId: '',
      posLabel: '',
      tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '',
      status: ''
    })
  }

  const resetCreatePosDialog = () => {
    setCreatePosDialog({
      siteId: '',
      posId: '',
      posLabel: '',
      tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '',
      status: ''
    })
  }

  const handleCreatePos = async () => {
    const normalizedPosId = String(createPosDialog.posId || '').trim()
    if (!createPosDialog.siteId || !normalizedPosId) {
      setCreatePosDialog((prev) => ({ ...prev, status: uiLiteral('Bitte POS-Seriennummer angeben.') }))
      return
    }
    const swissbitCloudMode = isSwissbitCloudPosTseType(createPosDialog.tseType)
    const fiskalyCloudMode = isFiskalyCloudPosTseType(createPosDialog.tseType)
    if (fiskalyCloudMode) {
      setCreatePosDialog((prev) => ({ ...prev, status: 'fiskaly Cloud TSE ist aktuell nicht unterstützt.' }))
      return
    }
    const serialNumber = String(createPosDialog.tseSerialNumber || '').trim()
    if (swissbitCloudMode && !serialNumber) {
      setCreatePosDialog((prev) => ({ ...prev, status: 'TSE Seriennummer ist erforderlich.' }))
      return
    }
    const resCreate = await fetch(`${apiBase}/tenant/pos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        tenant_id: tenantId,
        site_id: createPosDialog.siteId,
        pos_serial: normalizedPosId,
        pos_label: createPosDialog.posLabel || '',
        tse_type: normalizePosTseType(createPosDialog.tseType),
        tse_serial_number: serialNumber
      })
    })
    if (!resCreate.ok) {
      const msg = await parseError(resCreate)
      setCreatePosDialog((prev) => ({ ...prev, status: msg || 'POS konnte nicht angelegt werden.' }))
      return
    }
    const res = await fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}`, { headers: authHeaders })
    if (res.ok) setPosList(await res.json())
    resetCreatePosDialog()
  }

  const handleRenameTenant = async () => {
    const name = tenantNameEdit.trim()
    if (!name) return
    const res = await fetch(`${apiBase}/tenant/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name })
    })
    if (!res.ok) return
    const data = await res.json()
    setAuthUser((prev) => (prev ? { ...prev, tenant_name: data.name } : prev))
  }

  const refreshOrgTree = async () => {
    const [companiesRes, sitesRes, posRes, keysRes] = await Promise.all([
      fetch(`${apiBase}/tenant/companies?tenant_id=${tenantId}`, { headers: authHeaders }),
      fetch(`${apiBase}/tenant/sites?tenant_id=${tenantId}`, { headers: authHeaders }),
      fetch(`${apiBase}/tenant/pos?tenant_id=${tenantId}`, { headers: authHeaders }),
      fetch(`${apiBase}/tenant/api-keys`, { headers: authHeaders })
    ])
    if (companiesRes.ok) setCompanies(await companiesRes.json())
    if (sitesRes.ok) setSites(await sitesRes.json())
    if (posRes.ok) setPosList(await posRes.json())
    if (keysRes.ok) setApiKeys(await keysRes.json())
  }

  const handleRenameCompany = async (companyId, name) => {
    if (!companyId || !name.trim()) return
    const res = await fetch(`${apiBase}/tenant/companies/${companyId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: name.trim() })
    })
    if (!res.ok) return
    await refreshOrgTree()
    setEditCompany({ id: '', name: '' })
  }

  const handleRenameSite = async (siteId, name) => {
    if (!siteId || !name.trim()) return
    const res = await fetch(`${apiBase}/tenant/sites/${siteId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: name.trim() })
    })
    if (!res.ok) return
    await refreshOrgTree()
    setEditSite({ id: '', name: '' })
  }

  const refreshAdministration = async () => {
    if (!authToken || !authUser) return
    if (authUser?.role === 'platform_admin') {
      await Promise.all([refreshTenantAdmins(), refreshResellerAdmins()])
      return
    }
    if (authUser?.role === 'reseller_admin') {
      await Promise.all([refreshResellerAdmins(), refreshResellerTenantAdmins(), refreshResellerImpersonationPermissions()])
      return
    }
    const tasks = [refreshOrgTree(), refreshPasskeys()]
    if (canManageUsers) tasks.push(refreshTenantUsers())
    if (authUser?.role === 'tenant_admin') tasks.push(refreshTenantImpersonationPermissions())
    await Promise.all(tasks)
  }

  const handleEditPosOpen = (pos) => {
    const tseType = normalizePosTseType(pos.tse_type)
    const tseSerialNumber = String(pos.tse_serial_number || '')
    setEditPos({
      id: pos.pos_id,
      posSerial: pos.pos_serial || '',
      posLabel: pos.pos_label || '',
      tseType,
      tseSerialNumber,
      status: ''
    })
  }

  const resetEditPos = () => {
    setEditPos({
      id: '',
      posSerial: '',
      posLabel: '',
      tseType: 'swissbit_cloud_tse',
      tseSerialNumber: '',
      status: ''
    })
  }

  const handleSavePosSettings = async () => {
    if (!editPos.id) return
    const swissbitCloudMode = isSwissbitCloudPosTseType(editPos.tseType)
    const fiskalyCloudMode = isFiskalyCloudPosTseType(editPos.tseType)
    if (fiskalyCloudMode) {
      setEditPos((prev) => ({ ...prev, status: 'fiskaly Cloud TSE ist aktuell nicht unterstützt.' }))
      return
    }
    const serialNumber = String(editPos.tseSerialNumber || '').trim()
    if (swissbitCloudMode && !serialNumber) {
      setEditPos((prev) => ({ ...prev, status: 'TSE Seriennummer ist erforderlich.' }))
      return
    }
    const payload = {
      pos_label: editPos.posLabel || '',
      tse_type: normalizePosTseType(editPos.tseType),
      tse_serial_number: serialNumber
    }
    const res = await fetch(`${apiBase}/tenant/pos/${editPos.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const msg = await parseError(res)
      setEditPos((prev) => ({ ...prev, status: msg || 'POS-Einstellungen konnten nicht gespeichert werden.' }))
      return
    }
    await refreshOrgTree()
    resetEditPos()
  }
  const handleDeleteCompany = async (companyId, companyName) => {
    if (!companyId) return
    if (!window.confirm(formatUiTemplate('confirm.delete.company', { label: companyName }))) return
    const res = await fetch(`${apiBase}/tenant/companies/${companyId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) return
    await refreshOrgTree()
  }

  const handleDeleteSite = async (siteId, siteName) => {
    if (!siteId) return
    if (!window.confirm(formatUiTemplate('confirm.delete.site', { label: siteName }))) return
    const res = await fetch(`${apiBase}/tenant/sites/${siteId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) return
    await refreshOrgTree()
  }

  const handleDeletePos = async (posId, posName) => {
    if (!posId) return
    if (!window.confirm(formatUiTemplate('confirm.delete.pos', { label: posName }))) return
    const res = await fetch(`${apiBase}/tenant/pos/${posId}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    if (!res.ok) return
    await refreshOrgTree()
  }

  const handleUploadFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null
    setUploadError('')
    if (nextFile && Number(nextFile.size) > UPLOAD_FILE_LIMIT_BYTES) {
      setUploadFile(null)
      if (uploadFileInputRef.current) {
        uploadFileInputRef.current.value = ''
      }
      setUploadError(`Datei zu groß. Maximal ${UPLOAD_FILE_LIMIT_LABEL} pro Datei.`)
      return
    }
    setUploadFile(nextFile)
  }

  const handleUploadAddFile = async () => {
    setUploadError('')
    setUploadSuccess('')
    if (!uploadCompanyId) {
      setUploadError('Bitte ein Unternehmen wählen.')
      return
    }
    if (!uploadResolvedProfile?.profile_ref) {
      setUploadError('Für das Unternehmensland gibt es kein aktives Profil.')
      return
    }
    if (!uploadFile) {
      setUploadError('Bitte eine Datei auswählen.')
      return
    }
    if (Number(uploadFile.size) > UPLOAD_FILE_LIMIT_BYTES) {
      setUploadError(`Datei zu groß. Maximal ${UPLOAD_FILE_LIMIT_LABEL} pro Datei.`)
      return
    }
    if (!uploadFrom || !uploadTo) {
      setUploadError(TIME_RANGE_REQUIRED_ERROR)
      return
    }
    if (uploadTo < uploadFrom) {
      setUploadError('Zeitraum ungültig: "Bis" liegt vor "Von".')
      return
    }
    if (uploadArtifactType === 'free' && !uploadFreeArtifactLabel.trim()) {
      setUploadError('Bitte Freitext für Artefaktgruppe "Frei" angeben.')
      return
    }

    setUploadBusy(true)
    setUploadTransferProgress({ active: true, loaded: 0, total: Number(uploadFile.size) || 0, percent: 0 })
    try {
      const posNameByIdLocal = Object.fromEntries(
        uploadPosOptions.map((pos) => [pos.pos_id, pos.pos_label || pos.pos_serial])
      )
      const targetPosRef = uploadPosRefs.length ? uploadPosRefs[0] : ''
      const sha256Digest = await sha256DigestHex(uploadFile)
      const fileMeta = {
        artifact_type: uploadArtifactType,
        business_time_from: uploadFrom,
        business_time_to: uploadTo,
        filename: uploadFile.name,
        sha256_digest: sha256Digest,
        company_id: uploadCompanyId
      }
      if (uploadArtifactType === 'free') fileMeta.description = uploadFreeArtifactLabel.trim()
      if (targetPosRef) fileMeta.pos_id = targetPosRef
      const prepareRes = await fetch(`${apiBase}/web/artifacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(fileMeta)
      })
      const fileData = await prepareRes.json().catch(() => ({}))
      if (!prepareRes.ok) {
        throw new Error(normalizeApiError(prepareRes.status, fileData, { uploadFileLimitLabel: UPLOAD_FILE_LIMIT_LABEL }))
      }
      if (!fileData.upload_url || !fileData.upload_headers) {
        throw new Error('Upload-Vorbereitung lieferte keine Upload-URL.')
      }
      try {
        await uploadBlobWithProgress({
          url: fileData.upload_url,
          method: 'PUT',
          headers: fileData.upload_headers || {},
          body: uploadFile,
          onProgress: ({ loaded, total, percent }) => {
            setUploadTransferProgress({ active: true, loaded, total, percent })
          }
        })
      } catch (uploadErr) {
        if (fileData.artifact_id) {
          await fetch(`${apiBase}/web/artifacts/${fileData.artifact_id}`, {
            method: 'DELETE',
            headers: authHeaders
          }).catch(() => {})
        }
        throw uploadErr
      }
      setUploadTransferProgress((prev) => ({
        ...prev,
        active: true,
        loaded: prev.total || prev.loaded,
        percent: 100
      }))
      setUploadBundledFiles((prev) => [...prev, {
        fileId: fileData.artifact_id,
        filename: uploadFile.name,
        artifactType: uploadArtifactType,
        artifactLabel: uploadArtifactType === 'free' ? uploadFreeArtifactLabel.trim() : '',
        posLabel: targetPosRef ? (posNameByIdLocal[targetPosRef] || targetPosRef) : 'Unternehmensweit',
        from: uploadFrom,
        to: uploadTo
      }])
      setUploadLastAddedFile(uploadFile.name)
      setUploadFile(null)
      if (uploadFileInputRef.current) {
        uploadFileInputRef.current.value = ''
      }
    } catch (err) {
      setUploadError(err?.message || 'Upload fehlgeschlagen.')
    } finally {
      setUploadTransferProgress({ active: false, loaded: 0, total: 0, percent: null })
      setUploadBusy(false)
    }
  }

  const handleUploadRemoveFile = async (fileId, filename = 'Datei') => {
    setUploadError('')
    setUploadSuccess('')
    if (!fileId) {
      setUploadError('Kein offener Upload.')
      return
    }
    if (!window.confirm(formatUiTemplate('confirm.delete.upload_file', { label: filename }))) return
    setUploadBusy(true)
    try {
      const res = await fetch(`${apiBase}/web/artifacts/${fileId}`, {
        method: 'DELETE',
        headers: authHeaders
      })
      if (!res.ok) {
        throw new Error(await parseError(res))
      }
      setUploadBundledFiles((prev) => {
        const next = prev.filter((f) => f.fileId !== fileId)
        setUploadLastAddedFile(next.length ? next[next.length - 1].filename : '')
        return next
      })
    } catch (err) {
      setUploadError(err?.message || 'Datei konnte nicht entfernt werden.')
    } finally {
      setUploadBusy(false)
    }
  }

  const handleUploadCommit = async () => {
    setUploadError('')
    setUploadSuccess('')
    if (!uploadBundledFiles.length) {
      setUploadError('Keine Dateien im offenen Upload.')
      return
    }
    const artifactIds = uploadBundledFiles.map((item) => item.fileId).filter(Boolean)
    if (!artifactIds.length) {
      setUploadError('Kein offener Upload. Erst Datei hinzufügen.')
      return
    }

    setUploadBusy(true)
    try {
      const commitRes = await fetch(`${apiBase}/web/artifacts/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          artifact_ids: artifactIds
        })
      })
      if (!commitRes.ok) {
        throw new Error(await parseError(commitRes))
      }
      const commitData = await commitRes.json()

      const archiveRes = await fetch(`${apiBase}/web/ingest/files`, { headers: authHeaders })
      if (archiveRes.ok) {
        const data = await archiveRes.json()
        setArchiveItems(Array.isArray(data) ? data : [])
      }
      setUploadSuccess(formatUiTemplate('upload.committed', { id: commitData?.ingest_id || '' }))
      setUploadBundledFiles([])
      setUploadLastAddedFile('')
    } catch (err) {
      setUploadError(err?.message || 'Commit fehlgeschlagen.')
    } finally {
      setUploadBusy(false)
    }
  }

  const handleUploadResetBundle = async () => {
    setUploadError('')
    setUploadSuccess('')
    const artifactIds = uploadBundledFiles.map((item) => item.fileId).filter(Boolean)
    if (artifactIds.length) {
      setUploadBusy(true)
      try {
        for (const artifactId of artifactIds) {
          const res = await fetch(`${apiBase}/web/artifacts/${artifactId}`, {
            method: 'DELETE',
            headers: authHeaders
          })
          if (!res.ok) {
            throw new Error(await parseError(res))
          }
        }
      } catch (err) {
        setUploadError(err?.message || 'Upload konnte nicht zurückgesetzt werden.')
        setUploadBusy(false)
        return
      }
      setUploadBusy(false)
    }
    setUploadBundledFiles([])
    setUploadLastAddedFile('')
    setUploadFrom(defaultUploadRange.from)
    setUploadTo(defaultUploadRange.to)
    setUploadPickerOpen(false)
    setUploadPickerMonth(monthStart(new Date()))
    setUploadFile(null)
    setUploadTransferProgress({ active: false, loaded: 0, total: 0, percent: null })
    setUploadPosRefs([])
    if (uploadFileInputRef.current) {
      uploadFileInputRef.current.value = ''
    }
  }

  const handlePlayElefantAudio = () => {
    try {
      if (!elefantAudioRef.current) {
        elefantAudioRef.current = new Audio('/ELeFANT.mp3')
      }
      elefantAudioRef.current.currentTime = 0
      const playPromise = elefantAudioRef.current.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    } catch {
      // Optional audio feedback; ignore playback errors.
    }
  }

  if (!authToken || !authUser) {
    if (isPasswordTokenRoute) {
      const showResetInvalidState = resetTokenValid === false && !resetTokenValidating
      return (
        <div className="login-shell sb-scope">
          <form className="login-card password-reset-card" onSubmit={handleResetPassword}>
            <h1 className="login-product-title">ELeFANT</h1>
            <h2 className="login-signin-caption">{resetRouteTitle}</h2>
            {!showResetInvalidState && (
              <div className="form-grid">
                <div>
                  <label>Neues Passwort</label>
                  <input
                    type="password"
                    value={resetPasswordNew}
                    onChange={(e) => setResetPasswordNew(e.target.value)}
                    disabled={resetTokenValidating || resetTokenValid === false}
                    required
                  />
                </div>
                <div>
                  <label>Passwort wiederholen</label>
                  <input
                    type="password"
                    value={resetPasswordRepeat}
                    onChange={(e) => setResetPasswordRepeat(e.target.value)}
                    disabled={resetTokenValidating || resetTokenValid === false}
                    required
                  />
                </div>
              </div>
            )}
            {showResetInvalidState && (
              <div className="hint reset-link-invalid-message" role="alert">
                {resetRouteInvalidMessage}
              </div>
            )}
            <div className="login-actions">
              {!showResetInvalidState && (
                <button className="primary sb-btn sb-btn-primary" type="submit" disabled={resetPasswordBusy || resetTokenValidating || resetTokenValid === false}>
                  {resetTokenValidating ? resetRouteValidating : (resetPasswordBusy ? resetRouteSubmitBusy : resetRouteSubmitIdle)}
                </button>
              )}
              <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => { window.location.href = '/' }}>
                Zurück zum Login
              </button>
            </div>
            {resetPasswordError && !showResetInvalidState && <div className="hint">{resetPasswordError}</div>}
            {resetPasswordSuccess && <div className="hint login-info">{resetPasswordSuccess}</div>}
          </form>
          <LegalBottomBar appVersion={appVersion} />
        </div>
      )
    }
    return (
      <div className="login-shell sb-scope">
        <form className="login-card" onSubmit={handleLogin}>
          <h1 className="login-product-title">ELeFANT</h1>
          <p className="login-product-expansion">
            Verwalten Sie hier alle Ihre Daten rund um die Kasse im Swissbit <br />
              <strong>
                Electronic Ledger for Auditability, Integrity and Traceability
              </strong>
          </p>
          <h2 className="login-signin-caption">Hier einloggen</h2>
          <div className="form-grid">
            <div>
              <label>E-Mail</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Passwort</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="login-actions">
            <button className="primary sb-btn sb-btn-primary" type="submit" disabled={loginBusy || passkeyLoginBusy || forgotPasswordBusy}>
              {loginBusy ? 'Anmeldung…' : 'Einloggen'}
            </button>
            <button
              className="login-forgot-link"
              type="button"
              onClick={handleForgotPassword}
              disabled={loginBusy || passkeyLoginBusy || forgotPasswordBusy}
            >
              {forgotPasswordBusy ? 'Sende Reset-Link…' : 'Passwort vergessen?'}
            </button>
            <button
              className="secondary sb-btn sb-btn-secondary passkey-login-btn"
              type="button"
              disabled={loginBusy || passkeyLoginBusy || forgotPasswordBusy || !passkeySupported}
              onClick={handleLoginWithPasskey}
              title={passkeySupported ? 'Mit Passkey anmelden' : 'Passkey nicht unterstützt'}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 2a3 3 0 0 1 3 3v4h1.5A2.5 2.5 0 0 1 22 11.5v7a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 8 18.5v-7A2.5 2.5 0 0 1 10.5 9H12V5a3 3 0 0 1 3-3zm0 2a1 1 0 0 0-1 1v4h2V5a1 1 0 0 0-1-1zM5 12a3 3 0 1 0 0 6v-2a1 1 0 0 1 0-2v-2z" />
              </svg>
              <span>{passkeyLoginBusy ? 'Warte auf Passkey…' : 'Mit Passkey anmelden'}</span>
            </button>
          </div>
          {loginError && <div className="hint">{loginError}</div>}
          {loginInfo && <div className="hint login-info">{loginInfo}</div>}
          <button className="login-elefant-sound-btn" type="button" onClick={handlePlayElefantAudio} aria-label="ELeFANT Sound abspielen">
            <img className="login-elefant-image" src="/ELeFANT.png" alt="ELeFANT" />
          </button>
        </form>
        <LegalBottomBar appVersion={appVersion} />
      </div>
    )
  }

  return (
    <div className="app-shell sb-scope">
      {mobileNavOpen && <button className="mobile-nav-backdrop" type="button" onClick={() => setMobileNavOpen(false)} aria-label={uiLiteral('Navigation schließen')} />}

      <aside className={`sidebar sb-inverse${mobileNavOpen ? ' is-open' : ''}`}>
        <div className="brand">
          <div className="brand-logo-square">
            <img className="brand-logo-square-img" src="/ELeFANT-white.png" alt="ELeFANT" />
          </div>
          <div className="brand-text">
            <div className="brand-title">ELeFANT</div>
            <div className="brand-subtitle">Electronic Ledger For Auditability, iNtegrity and Traceability</div>
          </div>
        </div>
        <nav className="nav">
          {navItems.map((item) => (
            <button
              key={item.label}
              className={`nav-item${active === item.label ? ' is-active' : ''}`}
              type="button"
              onClick={() => {
                if (item.label === 'API-Keys') refreshApiKeys()
                if (item.label === 'Administration') refreshAdministration()
                if (item.label === 'Unternehmensstruktur') refreshOrgTree()
                setActive(item.label)
                setMobileNavOpen(false)
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className={`topbar${showTopbarOrg ? '' : ' is-org-hidden'}`}>
          <button
            className="mobile-nav-toggle secondary sb-btn sb-btn-secondary"
            type="button"
            onClick={() => setMobileNavOpen((prev) => !prev)}
            aria-label={mobileNavOpen ? uiLiteral('Navigation schließen') : uiLiteral('Navigation öffnen')}
            aria-expanded={mobileNavOpen}
          >
            <span className="mobile-nav-toggle-icon" aria-hidden="true" />
          </button>
          {showTopbarOrg && (
            <div className="context">
              <div className="context-label">{uiLiteral('Organisation')}</div>
              <div className="context-value">{tenantDisplayName}</div>
            </div>
          )}
          <div className="context user">
            <div className="context-label">{topbarUserLabel}</div>
            <div className="context-value user-email">
              {showTopbarUserName && userDisplayName && (
                <span className="user-name-inline">{userDisplayName} · </span>
              )}
              <span>{authUser.email}</span>
              {showTopbarRole && <span>{` · ${roleDisplayLabel || '-'}`}</span>}
            </div>
          </div>
          <div className="user-actions">
            {authUser?.impersonation?.active && (
              <button className="secondary sb-btn sb-btn-secondary user-impersonation" type="button" onClick={handleStopImpersonation}>{uiLiteral('Impersonation beenden')}</button>
            )}
            <button className="secondary user-logout sb-btn sb-btn-secondary" type="button" onClick={handleLogout}>
              <span className="logout-label">{uiLiteral('Logout')}</span>
              <svg className="logout-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14 7l5 5-5 5" />
                <path d="M19 12H9" />
                <path d="M5 4h6a1 1 0 011 1v2" />
                <path d="M12 17v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1h6a1 1 0 011 1v2" />
              </svg>
            </button>
          </div>
        </header>

        <main className="content">
          {active === 'Web-Upload' && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">Web-Upload</div>
                  <h1 className="sb-h1">Daten archivieren</h1>
                </div>
              </section>

              <section className="grid">
                <div className="card reveal upload-summary-card" style={{ animationDelay: '80ms', order: 2 }}>
                  <div className="card-title">Zusammenfassung</div>
                  <div className="upload-summary-panel">
                    <div className="list">
                      <div className="list-row"><span>Länderkürzel</span><span>{uploadDateLocked ? (uploadCompany?.country || '-') : '...'}</span></div>
                      <div className="list-row"><span>Unternehmen</span><span>{uploadDateLocked ? (uploadCompany?.name || '-') : '...'}</span></div>
                      <div className="list-row">
                        <span>POS Scope</span>
                        <span>
                          {uploadDateLocked
                            ? (uploadPosRefs.length ? formatUiTemplate('upload.pos_selected_count', { count: uploadPosRefs.length }) : 'Unternehmensweit')
                            : '...'}
                        </span>
                      </div>
                      <div className="list-row">
                        <span>Zeitraum</span>
                        <span>{uploadDateLocked ? `${formatDateDisplay(uploadFrom)} - ${formatDateDisplay(uploadTo)}` : '...'}</span>
                      </div>
                      <div className="list-row"><span>Anzahl Elemente</span><span>{uploadBundledFiles.length}</span></div>
                    </div>
                  </div>
                  <div className="upload-summary-actions">
                    <button
                      className="primary action-btn sb-btn sb-btn-primary"
                      type="button"
                      onClick={handleUploadCommit}
                      disabled={uploadBusy || !uploadContextReady}
                    >
                      {uploadBusy ? 'Lädt…' : 'Verbindlich archivieren'}
                    </button>
                    <button className="secondary action-btn sb-btn sb-btn-secondary" type="button" onClick={handleUploadResetBundle} disabled={uploadBusy}>
                      Zurücksetzen
                    </button>
                  </div>
                  {!uploadContextReady && (
                    <div className="hint">Upload deaktiviert: Unternehmen mit gültigem Länderprofil auswählen.</div>
                  )}
                  {uploadBundledFiles.length > 0 && (
                    <div>
                      <div className="hint">Dateiliste</div>
                      <div className="table upload-file-table">
                        <div className="table-header">
                          <span>Datei</span><span>Typ</span><span>Aktion</span>
                        </div>
                        {uploadBundledFiles.map((f) => (
                          <div className="table-row" key={f.fileId || `${f.filename}-${f.from}`}>
                            <span>{f.filename}</span>
                            <span>
                              {f.artifactLabel
                                ? `${getArtifactTypeLabel(f.artifactType, uploadCompany?.country || '')} (${f.artifactLabel})`
                                : getArtifactTypeLabel(f.artifactType, uploadCompany?.country || '')}
                              {f.posLabel ? ` · ${f.posLabel}` : ''}
                            </span>
                            <span>
                              <button
                                className="secondary sb-btn sb-btn-secondary"
                                type="button"
                                disabled={uploadBusy}
                                onClick={() => handleUploadRemoveFile(f.fileId, f.filename)}
                              >
                                Entfernen
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {uploadError && <div className="hint">{uploadError}</div>}
                </div>

                <div className={`card reveal${uploadTransferActive ? ' upload-dialog-blocked' : ''}`} style={{ animationDelay: '0ms', order: 1 }} aria-busy={uploadTransferActive}>
                  <div className="card-title">Upload-Details</div>
                  <div className="form-grid">
                    <div className="upload-context-zone">
                      <div className={`upload-context-item${uploadCompanyMissingError ? ' has-error' : ''}`}>
                        <label>Unternehmen</label>
                        {companies.length >= UI_SEARCH_THRESHOLD && (
                          <input
                            type="search"
                            className="multi-select-search upload-company-search"
                            placeholder="Filter..."
                            value={uploadCompanySearchTerm}
                            onChange={(e) => setUploadCompanySearchTerm(e.target.value)}
                            disabled={uploadDateLocked || uploadDialogBlocked}
                          />
                        )}
                        <select
                          value={uploadCompanySelectionVisible ? uploadCompanyId : ''}
                          onChange={(e) => setUploadCompanyId(e.target.value)}
                          disabled={uploadDateLocked || uploadDialogBlocked}
                        >
                          <option value="">Unternehmen wählen</option>
                          {uploadCompanySelectOptions.map((c) => (
                            <option key={c.company_id} value={c.company_id}>
                              {c.name} ({c.country || '-'})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label>POS (optional)</label>
                        <CheckboxMultiSelect
                          options={uploadPosOptions.map((r) => ({ value: r.pos_id, label: r.pos_label || r.pos_serial || '-' }))}
                          value={uploadPosRefs}
                          onChange={(selected) => setUploadPosRefs(selected.slice(-1))}
                          disabled={uploadDateLocked || !uploadContextReady || uploadDialogBlocked}
                          emptyLabel={uploadContextReady ? 'Keine POS verfügbar' : 'Keine Daten'}
                          searchable
                          searchThreshold={UI_SEARCH_THRESHOLD}
                          visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                        />
                        <div className="hint">Ohne POS-Auswahl gilt der Upload unternehmensweit. Es kann maximal eine POS gewählt werden.</div>
                      </div>
                      <div className={`upload-context-item${uploadRangeMissingError ? ' has-error' : ''}`}>
                        <label>Zeitraum</label>
                        <div className={`date-range-wrap${uploadRangeMissingError ? ' has-error' : ''}`}>
                          <button
                            className="date-range-trigger"
                            type="button"
                            onClick={() => !uploadDateLocked && setUploadPickerOpen((v) => !v)}
                            disabled={!uploadContextReady || uploadDateLocked || uploadDialogBlocked}
                          >
                            <span>
                              {uploadFrom
                                ? `${formatDateDisplay(uploadFrom)} - ${uploadTo ? formatDateDisplay(uploadTo) : 'TT.MM.JJJJ'}`
                                : 'TT.MM.JJJJ - TT.MM.JJJJ'}
                            </span>
                            <span className="date-range-icon">📅</span>
                          </button>
                          {uploadPickerOpen && !uploadDateLocked && !uploadDialogBlocked && (
                            <div className="date-range-popover">
                              <div className="date-range-head">
                                <div className="date-range-nav-group">
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerYear(-1)} aria-label="Ein Jahr zurück">«</button>
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerMonth(-1)} aria-label="Ein Monat zurück">‹</button>
                                </div>
                                <div className="date-range-head-label">{uploadPickerMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</div>
                                <div className="date-range-nav-group">
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerMonth(1)} aria-label="Ein Monat vor">›</button>
                                  <button className="date-range-nav-btn" type="button" onClick={() => handleUploadPickerYear(1)} aria-label="Ein Jahr vor">»</button>
                                </div>
                              </div>
                              <div className="date-range-weekdays">
                                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                                  <span key={d}>{d}</span>
                                ))}
                              </div>
                              <div className="date-range-grid">
                                {uploadCalendarCells.map((cell, idx) => {
                                  const inRange = cell && uploadFrom && uploadTo && cell >= uploadFrom && cell <= uploadTo
                                  const isStart = cell && uploadFrom === cell
                                  const isEnd = cell && uploadTo === cell
                                  return (
                                    <button
                                      key={`${cell || 'empty'}-${idx}`}
                                      className={`date-cell${cell ? '' : ' is-empty'}${inRange ? ' is-in-range' : ''}${isStart ? ' is-start' : ''}${isEnd ? ' is-end' : ''}`}
                                      type="button"
                                      disabled={!cell}
                                      onClick={() => handleUploadDatePick(cell)}
                                    >
                                      {cell ? Number(cell.slice(8, 10)) : ''}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="hint">Archivierte Elemente werden in diesem Zeitraum beim Export exportiert.</div>
                      </div>
                      {uploadDateLocked && <div className="hint">Diese Details sind für diesen Upload eingefroren. Zurücksetzen schaltet wieder frei.</div>}
                    </div>
                    <div>
                      <label>Artefaktgruppe</label>
                      <select
                        value={uploadArtifactType}
                        onChange={(e) => setUploadArtifactType(e.target.value)}
                        disabled={!uploadContextReady || uploadDialogBlocked}
                      >
                        {!uploadContextReady ? (
                          <option value="">Kein aktives Länderprofil</option>
                        ) : (
                          <>
                            <option value="tse_tar">{getArtifactTypeLabel('tse_tar', uploadCompany?.country || '')}</option>
                            <option value="dsfinvk">{getArtifactTypeLabel('dsfinvk', uploadCompany?.country || '')}</option>
                            <option value="procedure_doc">{getArtifactTypeLabel('procedure_doc', uploadCompany?.country || '')}</option>
                            <option value="free">Frei</option>
                          </>
                        )}
                      </select>
                    </div>
                    {uploadArtifactType === 'free' && (
                      <div>
                        <label>Freitext-Typ</label>
                        <input
                          type="text"
                          placeholder="z. B. TSE Zertifikat"
                          value={uploadFreeArtifactLabel}
                          onChange={(e) => setUploadFreeArtifactLabel(e.target.value)}
                          disabled={!uploadContextReady || uploadDialogBlocked}
                        />
                      </div>
                    )}
                    <div>
                      <label>Datei</label>
                      <input
                        ref={uploadFileInputRef}
                        type="file"
                        onChange={handleUploadFileChange}
                        disabled={!uploadContextReady || uploadDialogBlocked}
                      />
                      <div className="hint">Mehrere Dateien können in einen offenen Upload gebündelt werden.</div>
                      <button
                        className="secondary upload-add-btn action-btn sb-btn sb-btn-secondary"
                        type="button"
                        onClick={handleUploadAddFile}
                        disabled={uploadBusy || !uploadFile || !uploadContextReady}
                      >
                        {uploadTransferActive
                          ? (uploadTransferPercent == null ? 'Upload läuft…' : `Upload läuft (${uploadTransferPercent} %)…`)
                          : (uploadBusy ? 'Lädt…' : 'Datei hinzufügen')}
                      </button>
                      {uploadTransferActive && (
                        <div className="upload-progress-panel" role="status" aria-live="polite">
                          <div className="upload-progress-copy">{uploadTransferStatusText}</div>
                          <div className={`upload-progress-bar${uploadTransferPercent == null ? ' is-indeterminate' : ''}`}>
                            <span style={uploadTransferPercent == null ? undefined : { width: `${uploadTransferPercent}%` }} />
                          </div>
                        </div>
                      )}
                      {uploadError && <div className="hint">{uploadError}</div>}
                    </div>
                  </div>
                  {!uploadContextReady && (
                    <div className="hint">Aktion erforderlich: Unternehmen wählen.</div>
                  )}
                </div>
              </section>
            </>
          )}

          {active === 'Prüfexport' && canRunExports && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">Prüfexport</div>
                  <h1 className="sb-h1">Prüfexport</h1>
                </div>
                {activeExport && (
                  <div className="hero-card">
                    <div className="hero-title">Aktueller Export</div>
                    <div className="hero-metric">{activeExport.export_id}</div>
                    <div className="hero-sub">{formatUiTemplate('status.value', { value: exportStatusLabel(activeExport.status) })}</div>
                  </div>
                )}
              </section>

              <section className="grid">
                <div className="card export-filter-card">
                  <div className="card-title">Filter</div>
                  <div className="hint">In diesem Dialog können Sie Kriterien auswählen, was im Export enthalten sein soll.</div>
                  <div className="form-grid">
                    <div>
                      <label className="check-item">
                        <span>Gelöschte Einträge zum Filtern anbieten</span>
                        <input
                          type="checkbox"
                          checked={exportIncludeDeleted}
                          onChange={(e) => {
                            const next = e.target.checked
                            setExportIncludeDeleted(next)
                            setExportFilterApplied(false)
                            setExportPreview(null)
                            setExportSelectedArtifactTypes([])
                          }}
                        />
                      </label>
                    </div>
                    <div>
                      <label>Firma</label>
                      <CheckboxMultiSelect
                        options={exportCompanyOptions.map((c) => ({ value: c.company_id, label: c.name }))}
                        value={exportCompanyIds}
                        onChange={(selected) => {
                          setExportCompanyIds(selected)
                          setExportPosIds([])
                          setExportFilterApplied(false)
                          setExportPreview(null)
                        }}
                        emptyLabel="Keine Firmen verfügbar"
                        searchable
                        searchThreshold={UI_SEARCH_THRESHOLD}
                        visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                      />
                      {!exportCompanyOptions.length && (
                        <button
                          className="link sb-link export-empty-action"
                          type="button"
                          onClick={handleNavigateToCompanyStructure}
                        >
                          (+ Unternehmen in der Unternehmensstruktur hinzufügen)
                        </button>
                      )}
                    </div>
                    <div>
                      <label>POS (optional)</label>
                      <CheckboxMultiSelect
                        options={exportPosOptions.map((p) => ({ value: p.pos_id, label: p.name }))}
                        value={exportPosIds}
                        onChange={(selected) => {
                          setExportPosIds(selected)
                          setExportFilterApplied(false)
                          setExportPreview(null)
                        }}
                        disabled={!exportCompanyIds.length || exportCompanyIds.length > 1}
                        emptyLabel={exportCompanyIds.length ? 'Keine POS verfügbar' : 'Keine Daten'}
                        searchable
                        searchThreshold={UI_SEARCH_THRESHOLD}
                        visibleRows={UI_MULTISELECT_VISIBLE_ROWS}
                      />
                      {exportCompanyIds.length > 1 && (
                        <div className="hint">POS ist bei Auswahl mehrerer Firmen deaktiviert.</div>
                      )}
                    </div>
                    <div>
                      <label>Zeitraum</label>
                      <div className={`date-range-wrap${exportRangeMissingError ? ' has-error' : ''}`}>
                        <button className="date-range-trigger" type="button" onClick={() => setExportPickerOpen((v) => !v)}>
                          <span>
                            {exportRangeStart
                              ? `${formatDateDisplay(exportRangeStart)} - ${exportRangeEnd ? formatDateDisplay(exportRangeEnd) : 'TT.MM.JJJJ'}`
                              : 'TT.MM.JJJJ - TT.MM.JJJJ'}
                          </span>
                          <span className="date-range-icon">📅</span>
                        </button>
                        {exportPickerOpen && (
                          <div className="date-range-popover">
                            <div className="date-range-head">
                              <div className="date-range-nav-group">
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerYear(-1)} aria-label="Ein Jahr zurück">«</button>
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerMonth(-1)} aria-label="Ein Monat zurück">‹</button>
                              </div>
                              <div className="date-range-head-label">{exportPickerMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</div>
                              <div className="date-range-nav-group">
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerMonth(1)} aria-label="Ein Monat vor">›</button>
                                <button className="date-range-nav-btn" type="button" onClick={() => handleExportPickerYear(1)} aria-label="Ein Jahr vor">»</button>
                              </div>
                            </div>
                            <div className="date-range-weekdays">
                              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                                <span key={d}>{d}</span>
                              ))}
                            </div>
                            <div className="date-range-grid">
                              {exportCalendarCells.map((cell, idx) => {
                                const inRange = cell && exportRangeStart && exportRangeEnd && cell >= exportRangeStart && cell <= exportRangeEnd
                                const isStart = cell && exportRangeStart === cell
                                const isEnd = cell && exportRangeEnd === cell
                                return (
                                  <button
                                    key={`${cell || 'empty'}-${idx}`}
                                    className={`date-cell${cell ? '' : ' is-empty'}${inRange ? ' is-in-range' : ''}${isStart ? ' is-start' : ''}${isEnd ? ' is-end' : ''}`}
                                    type="button"
                                    disabled={!cell}
                                    onClick={() => handleExportDatePick(cell)}
                                  >
                                    {cell ? Number(cell.slice(8, 10)) : ''}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {exportError && <div className="hint">{exportError}</div>}
                  <button className="secondary export-filter-btn action-btn sb-btn sb-btn-secondary" type="button" onClick={runExportFilter}>Filter anwenden</button>
                </div>

                <div className="card export-preview-card">
                  <div className="card-title">Preview</div>
                  {!exportFilterApplied && <div className="hint">Preview wird nach Klick auf „Filter anwenden“ angezeigt.</div>}
                  {exportFilterApplied && (
                    <>
                      <div>
                        <label>Kategorien</label>
                        <CheckboxMultiSelect
                          options={exportCategoryOptions}
                          value={exportSelectedArtifactTypes}
                          onChange={(selected) => setExportSelectedArtifactTypes(selected)}
                          emptyLabel="Keine Kategorien verfügbar"
                        />
                      </div>
                      <div className="hint">
                        {formatUiTemplate('export.preview.selected_of_total', {
                          selected: exportSelectedCountTotal,
                          total: exportPreview?.total || 0
                        })}
                      </div>
                      {(!exportPreview || exportPreview.total === 0) && <div className="hint">Keine Treffer für die gewählten Filter.</div>}
                    </>
                  )}
                  <div className="export-actions-footer">
                    <div className="export-mail-placeholder">
                      <label>E-Mailversand an (optional, Prüfer/Revision)</label>
                      <input
                        type="email"
                        placeholder="pruefer@beispiel.de"
                        value={exportAuditorEmail}
                        onChange={(e) => setExportAuditorEmail(e.target.value)}
                      />
                      <div className="hint">Der Export steht zum Download für 7 Tage bereit.</div>
                    </div>
                    <button
                      className="primary export-run-btn sb-btn sb-btn-primary"
                      type="button"
                      onClick={handleRunExport}
                      disabled={exportBusy || !exportFilterApplied || !exportPreview || exportSelectedCountTotal === 0}
                    >
                      {exportBusy ? 'Läuft…' : 'Export durchführen'}
                    </button>
                  </div>
                </div>
              </section>

              <section className="card">
                <div className="card-title">Letzte Exporte</div>
                <div className="table export-table">
                  <div className="table-header">
                    <span>Erstellt am</span><span>Status</span><span>Zeitraum</span><span>Export ID</span>
                  </div>
                  {latestExports.map((item) => {
                    const status = String(item.status || '').toLowerCase()
                    const isCompleted = status === 'completed'
                    const isExpired = status === 'expired'
                    return (
                    <div className="table-row" key={item.export_id} title={exportTooltipText(item)}>
                      <span>{formatDateTimeDisplay(item.created_at)}</span>
                      <span className={`badge${isExpired ? ' expired' : ''}`}>{exportStatusLabel(item.status)}</span>
                      <span>
                        {item.business_time_from && item.business_time_to
                          ? `${formatDateDisplay(item.business_time_from)} - ${formatDateDisplay(item.business_time_to)}`
                          : '-'}
                      </span>
                      <span>
                        {isCompleted ? (
                          <a
                            className="link sb-link"
                            href={`${apiBase}/exports/${item.export_id}/download`}
                            title="Export herunterladen"
                          >
                            {item.export_id}
                          </a>
                        ) : isExpired ? (
                          <span className="export-id-disabled" title="Link abgelaufen">{item.export_id}</span>
                        ) : (
                          item.export_id
                        )}
                      </span>
                    </div>
                    )
                  })}
                </div>
              </section>
            </>
          )}

          {active === 'API-Keys' && authUser?.role !== 'platform_admin' && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">API-Keys</div>
                  <h1 className="sb-h1">API-Key Verwaltung</h1>
                </div>
              </section>

              <section className="card">
                <div className="card-title">{uiLiteral('API-Key Verwaltung')}</div>
                {canManageApiKeys && (
                  <div className="onboarding-code-panel">
                    <div className="onboarding-code-header">
                      <div>
                        <div className="onboarding-code-title">{uiLiteral('Onboarding-Code erzeugen')}</div>
                        <div className="hint onboarding-code-description">
                          <p>{uiLiteral('Ein Onboarding-Code erlaubt es einer oder mehreren Kassen, sich automatisch zu registrieren und jeweils einen eigenen API-Key anzufordern. Ein API-Key ist genau einer Kasse zugeordnet. Er ist das Geheimnis, mit dem sich die Kasse gegenüber dem Service identifiziert, und darf nicht zwischen mehreren Kassen geteilt werden. Der Onboarding-Code darf dagegen von mehreren Kassen verwendet werden. Jeder angeforderte API-Key muss anschließend in der API-Key-Verwaltung freigegeben werden.')}</p>
                          <p>{uiLiteral('Onboarding-Codes sind zeitlich unbegrenzt gültig und werden im Portal nur einmal angezeigt. Wenn Sie einen Code verlieren, erzeugen Sie einen neuen; bereits erzeugte Codes bleiben weiterhin gültig.')}</p>
                        </div>
                      </div>
                      <div className="inline-actions onboarding-code-actions">
                        <button className="secondary sb-btn sb-btn-secondary onboarding-code-button" type="button" onClick={handleOnboardingCodeGenerationRequest} disabled={apiKeyOnboardingCode.busy}>
                          {apiKeyOnboardingCode.busy ? uiLiteral('Erzeugt…') : uiLiteral(apiKeyOnboardingCode.code ? 'Weiteren Onboarding-Code erzeugen' : 'Onboarding-Code erzeugen')}
                        </button>
                      </div>
                    </div>
                    {apiKeyOnboardingCode.error && <div className="hint">{apiKeyOnboardingCode.error}</div>}
                    {apiKeyOnboardingCode.code && (
                      <>
                        <div className="table onboarding-code-table" aria-label={uiLiteral('Onboarding-Code')}>
                          <div className="table-header">
                            <span>{uiLiteral('Onboarding-Code')}</span>
                          </div>
                          <div className="table-row">
                            <span className="onboarding-code-cell">
                              <span className="onboarding-code-value">{apiKeyOnboardingCode.code}</span>
                              <button
                                className="link sb-link tenant-icon-btn onboarding-code-copy-button"
                                type="button"
                                title={apiKeyOnboardingCode.copied ? uiLiteral('Kopiert') : uiLiteral('Kopieren')}
                                aria-label={apiKeyOnboardingCode.copied ? uiLiteral('Onboarding-Code kopiert') : uiLiteral('Onboarding-Code kopieren')}
                                onClick={handleCopyOnboardingCode}
                              >
                                {apiKeyOnboardingCode.copied ? (
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M8 7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10h-2V7h-8v10H8V7zm-4 4a2 2 0 0 1 2-2h1v2H6v8h8v-1h2v1a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8z" />
                                  </svg>
                                )}
                              </button>
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="archive-search">
                  <input
                    type="search"
                    placeholder={uiLiteral('Suche (POS, Unternehmen, Betriebsstätte, Status)')}
                    value={apiKeysSearchTerm}
                    onChange={(e) => setApiKeysSearchTerm(e.target.value)}
                  />
                </div>
                {canManageApiKeys && pendingApiKeys.length > 0 && (
                  <div className="api-key-bulk-actions">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        className="api-key-selection-checkbox"
                        checked={pendingApiKeys.every((item) => selectedPendingApiKeyIds.has(String(item.api_key_id || '')))}
                        onChange={toggleAllPendingApiKeys}
                        disabled={bulkApiKeyApprovalBusy}
                      />
                      <span>{uiLiteral('Alle offenen Anfragen auswählen')}</span>
                    </label>
                    <button
                      className="secondary sb-btn sb-btn-secondary"
                      type="button"
                      onClick={handleApproveSelectedApiKeys}
                      disabled={bulkApiKeyApprovalBusy || !pendingApiKeys.some((item) => selectedPendingApiKeyIds.has(String(item.api_key_id || '')))}
                    >
                      {bulkApiKeyApprovalBusy
                        ? uiLiteral('Freigabe läuft ...')
                        : uiLiteral('Ausgewählte freigeben')}
                    </button>
                  </div>
                )}
                {bulkApiKeyApprovalStatus && <div className="hint">{bulkApiKeyApprovalStatus}</div>}
                <div className="table api-keys-table">
                  <div className="table-header">
                    <span aria-label={uiLiteral('Auswahl')}>{uiLiteral('Auswahl')}</span>
                    <span>{uiLiteral('POS-Seriennummer')}</span>
                    <span>{uiLiteral('Unternehmen')}</span>
                    <span>{uiLiteral('Betriebsstätte')}</span>
                    <span>{uiLiteral('Status')}</span>
                    <span>{uiLiteral('Aktionen')}</span>
                  </div>
                  {filteredApiKeys.map((item) => (
                    <div
                      className={`table-row${apiKeyActionDialog.open && apiKeyActionDialog.apiKey === (item.api_key_id || '') ? ' is-editing' : ''}${String(item.status || '').toLowerCase() === 'requested' ? ' is-selectable' : ''}`}
                      key={item.api_key_id || item.pos_id}
                      onClick={(event) => {
                        if (String(item.status || '').toLowerCase() !== 'requested' || event.target.closest('button, input')) return
                        toggleApiKeySelection(item.api_key_id)
                      }}
                    >
                      <span>
                        {String(item.status || '').toLowerCase() === 'requested' && (
                          <input
                            type="checkbox"
                            className="api-key-selection-checkbox"
                            checked={selectedPendingApiKeyIds.has(String(item.api_key_id || ''))}
                            onChange={() => toggleApiKeySelection(item.api_key_id)}
                            disabled={bulkApiKeyApprovalBusy}
                            aria-label={`POS ${item.pos_serial || item.reported_pos_serial || item.api_key_id} auswählen`}
                          />
                        )}
                      </span>
                      <span>{highlightSearchSubstring(item.pos_serial || item.reported_pos_serial || '-', apiKeysSearchTerm)}</span>
                      <span>{highlightSearchSubstring(item.company_name || item.reported_company_name || '-', apiKeysSearchTerm)}</span>
                      <span>{highlightSearchSubstring(item.site_name || item.reported_site_name || '-', apiKeysSearchTerm)}</span>
                      <span className={`badge ${item.status}`} data-i18n-live="1">{highlightSearchSubstring(mapApiKeyStateLabel(item), apiKeysSearchTerm)}</span>
                      <span>
                        {renderApiKeyActions(item)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="api-keys-mobile-list" role="list">
                  {filteredApiKeys.map((item) => {
                    const rowId = item.api_key_id || item.pos_id || ''
                    const isExpanded = expandedApiKeyMobileId === rowId
                    const detailsId = `api-key-mobile-details-${rowId}`
                    return (
                      <div className="api-keys-mobile-item" key={`api-mobile-${rowId}`} role="listitem">
                        <button
                          className="api-keys-mobile-summary"
                          type="button"
                          onClick={() => setExpandedApiKeyMobileId((prev) => (prev === rowId ? '' : rowId))}
                          aria-expanded={isExpanded}
                          aria-controls={detailsId}
                        >
                          <span className={`api-keys-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                          {String(item.status || '').toLowerCase() === 'requested' && (
                            <input
                              type="checkbox"
                              className="api-key-selection-checkbox"
                              checked={selectedPendingApiKeyIds.has(String(item.api_key_id || ''))}
                              onChange={(event) => {
                                event.stopPropagation()
                                toggleApiKeySelection(item.api_key_id)
                              }}
                              disabled={bulkApiKeyApprovalBusy}
                              aria-label={`POS ${item.pos_serial || item.reported_pos_serial || item.api_key_id} auswählen`}
                            />
                          )}
                          <span className="api-keys-mobile-pos">{highlightSearchSubstring(item.pos_serial || item.reported_pos_serial || '-', apiKeysSearchTerm)}</span>
                          <span className="api-keys-mobile-company">{highlightSearchSubstring(item.company_name || item.reported_company_name || '-', apiKeysSearchTerm)}</span>
                          <span className={`badge ${item.status} api-keys-mobile-status`} data-i18n-live="1">{highlightSearchSubstring(mapApiKeyStateLabel(item), apiKeysSearchTerm)}</span>
                        </button>
                        {isExpanded && (
                          <div className="api-keys-mobile-details" id={detailsId}>
                            <div className="api-keys-mobile-actions">
                              {renderApiKeyActions(item)}
                            </div>
                            <div className="api-keys-mobile-meta">
                              <span className="api-keys-mobile-label">Betriebsstätte</span>
                              <span>{highlightSearchSubstring(item.site_name || item.reported_site_name || '-', apiKeysSearchTerm)}</span>
                            </div>
                            <div className="api-keys-mobile-meta">
                              <span className="api-keys-mobile-label">Status</span>
                              <span className={`badge ${item.status}`} data-i18n-live="1">{mapApiKeyStateLabel(item)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                {apiKeyActionDialog.open && (
                  <div className="edit-pos-panel api-key-approval-panel">
                    <div className="card-title">
                      {apiKeyActionDialog.mode === 'approve' ? 'API-Key Freigabe' : 'API-Key Aktualisierung'}
                    </div>
                    <div className={`api-key-approval-grid${apiKeyActionDialog.mode === 'refresh' ? ' is-refresh' : ''}`}>
                      <div className="api-key-approval-card">
                        <div className="api-key-approval-card-title">Gemeldete Eigenschaften</div>
                        <div className="api-key-reported-list">
                          {apiKeyActionDialog.mode === 'approve' ? <>
                            <div><span>Unternehmen</span><strong>{apiKeyActionDialog.reportedCompanyName || '-'}</strong></div>
                            <div><span>Land</span><strong>{apiKeyActionDialog.reportedCompanyCountry || '-'}</strong></div>
                            <div><span>Betriebsstätte</span><strong>{apiKeyActionDialog.reportedSiteName || '-'}</strong></div>
                            {apiKeyActionDialog.reportedPosLabel && <div><span>POS Bezeichnung</span><strong>{apiKeyActionDialog.reportedPosLabel}</strong></div>}
                            <div><span>POS Seriennummer</span><strong>{apiKeyActionDialog.reportedPosSerial || apiKeyActionDialog.posSerial}</strong></div>
                          </> : <>
                            {apiKeyActionDialog.labelChanged && <div><span>POS Bezeichnung</span><strong>{apiKeyActionDialog.reportedPosLabel || '-'}</strong></div>}
                            {apiKeyActionDialog.tseTypeChanged && <div><span>TSE Typ</span><strong>{getPosTseTypeLabel(apiKeyActionDialog.reportedTseType)}</strong></div>}
                            {apiKeyActionDialog.tseSerialNumberChanged && <div><span>TSE Seriennummer</span><strong>{apiKeyActionDialog.reportedTseSerialNumber || '-'}</strong></div>}
                          </>}
                        </div>
                      </div>
                      {apiKeyActionDialog.mode === 'approve' && (
                      <div className="api-key-approval-card">
                        <div className="api-key-approval-card-title">Zuweisung</div>
                        <div className="form-grid">
                          <div>
                            <label htmlFor="ak-company-select">Unternehmen</label>
                            {!apiKeyActionDialog.createCompany ? (
                              <select
                                id="ak-company-select"
                                value={apiKeyActionDialog.companyId}
                                onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, companyId: e.target.value, siteId: '' }))}
                                disabled={apiKeyActionDialog.busy}
                              >
                                <option value="">Bitte wählen</option>
                                {companies.filter((c) => !c.is_deleted).map((company) => (
                                  <option key={company.company_id} value={company.company_id}>{company.name}</option>
                                ))}
                              </select>
                            ) : (
                              <div className="two-col">
                                <input
                                  type="text"
                                  placeholder="Unternehmensname"
                                  value={apiKeyActionDialog.newCompanyName}
                                  onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, newCompanyName: e.target.value }))}
                                  disabled={apiKeyActionDialog.busy}
                                />
                                <input
                                  type="text"
                                  placeholder="Land (ISO-2)"
                                  value={apiKeyActionDialog.newCompanyCountry}
                                  onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, newCompanyCountry: e.target.value }))}
                                  disabled={apiKeyActionDialog.busy}
                                />
                              </div>
                            )}
                            <label className="checkbox-inline api-key-create-toggle">
                              <input
                                type="checkbox"
                                checked={apiKeyActionDialog.createCompany}
                                onChange={(e) =>
                                  setApiKeyActionDialog((prev) => {
                                    const checked = e.target.checked
                                    const hasCompanyName = String(prev.newCompanyName || '').trim().length > 0
                                    const hasCompanyCountry = String(prev.newCompanyCountry || '').trim().length > 0
                                    const reportedName = String(prev.reportedCompanyName || '').trim()
                                    const reportedCountry = String(prev.reportedCompanyCountry || '').trim().toUpperCase()
                                    return {
                                      ...prev,
                                      createCompany: checked,
                                      companyId: '',
                                      siteId: '',
                                      newCompanyName: checked && !hasCompanyName ? reportedName : prev.newCompanyName,
                                      newCompanyCountry: checked && !hasCompanyCountry
                                        ? (reportedCountry || 'DE')
                                        : prev.newCompanyCountry
                                    }
                                  })}
                                disabled={apiKeyActionDialog.busy || !canEditCompanies}
                              />
                              <span>Unternehmen neu anlegen</span>
                            </label>
                            {!canEditCompanies && (
                              <div className="hint api-key-create-toggle-hint">
                                Neuanlage erfordert das Recht Unternehmen verwalten.
                              </div>
                            )}
                          </div>
                          <div>
                            <label htmlFor="ak-site-select">Betriebsstätte</label>
                            {!apiKeyActionDialog.createSite ? (
                              <select
                                id="ak-site-select"
                                value={apiKeyActionDialog.siteId}
                                onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, siteId: e.target.value }))}
                                disabled={apiKeyActionDialog.busy || (!apiKeyActionDialog.companyId && !apiKeyActionDialog.createCompany)}
                              >
                                <option value="">Bitte wählen</option>
                                {sites
                                  .filter((s) => !s.is_deleted && s.company_id === apiKeyActionDialog.companyId)
                                  .map((site) => (
                                    <option key={site.site_id} value={site.site_id}>{site.name}</option>
                                  ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                placeholder="Betriebsstätte"
                                value={apiKeyActionDialog.newSiteName}
                                onChange={(e) => setApiKeyActionDialog((prev) => ({ ...prev, newSiteName: e.target.value }))}
                                disabled={apiKeyActionDialog.busy}
                              />
                            )}
                            <label className="checkbox-inline api-key-create-toggle">
                              <input
                                type="checkbox"
                                checked={apiKeyActionDialog.createSite}
                                onChange={(e) =>
                                  setApiKeyActionDialog((prev) => {
                                    const checked = e.target.checked
                                    const hasCurrentValue = String(prev.newSiteName || '').trim().length > 0
                                    const reportedValue = String(prev.reportedSiteName || '').trim()
                                    return {
                                      ...prev,
                                      createSite: checked,
                                      siteId: '',
                                      newSiteName: checked && !hasCurrentValue ? reportedValue : prev.newSiteName
                                    }
                                  })}
                                disabled={apiKeyActionDialog.busy || !canEditSites}
                              />
                              <span>Betriebsstätte neu anlegen</span>
                            </label>
                            {!canEditSites && (
                              <div className="hint api-key-create-toggle-hint">
                                Neuanlage erfordert das Recht Betriebsstätten verwalten.
                              </div>
                            )}
                          </div>
                          <div className="muted">
                            Die POS-Seriennummer wird aus den gemeldeten Eigenschaften übernommen und nicht manuell bearbeitet.
                          </div>
                        </div>
                      </div>
                      )}
                      <div className="inline-actions api-key-dialog-actions">
                        <button className="primary sb-btn sb-btn-secondary" type="button" onClick={handleApproveOrRefreshApiKey} disabled={apiKeyActionDialog.busy}>
                          {apiKeyActionDialog.mode === 'approve' ? 'Zuweisen' : 'Aktualisieren'}
                        </button>
                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={closeApiKeyActionDialog} disabled={apiKeyActionDialog.busy}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                    {apiKeyActionDialog.status && <div className="hint">{apiKeyActionDialog.status}</div>}
                  </div>
                )}
                {!apiKeys.length && <div className="hint">Keine API-Keys definiert</div>}
                {apiKeys.length > 0 && !filteredApiKeys.length && <div className="hint">Keine passenden API-Keys gefunden.</div>}
                {!canManageApiKeys && <div className="hint">Nur Leserechte: API-Keys können nicht bearbeitet werden.</div>}
              </section>
            </>
          )}

          {active === 'API-Keys' && authUser?.role === 'platform_admin' && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">API-Keys</div>
                  <h1 className="sb-h1">API-Key Verwaltung</h1>
                </div>
              </section>
              <section className="card">
                <div className="hint">API-Key Verwaltung ist im Hauptnutzer-Kontext verfügbar.</div>
              </section>
            </>
          )}

          {(active === 'Administration' || active === 'Unternehmensstruktur') && (
            <>
              <section className="hero">
                <div>
                  <div className="eyebrow">{isStructureNavigation ? 'Unternehmensstruktur' : 'Administration'}</div>
                  <h1 className="sb-h1">{isStructureNavigation ? 'Unternehmensstruktur' : 'Administration'}</h1>
                </div>
              </section>

              {!isStructureNavigation && (
              <section className="grid admin-security-grid">
                <section className="card admin-compact-card">
                  <div className="card-title">Passwort ändern</div>
                  <button
                    className="secondary sb-btn sb-btn-secondary admin-compact-toggle"
                    type="button"
                    onClick={() => setShowPasswordChange((prev) => !prev)}
                  >
                    {showPasswordChange ? 'Passwortänderung ausblenden' : 'Passwort ändern'}
                  </button>
                  {showPasswordChange && (
                    <div className="tenant-create-wrap">
                    <div className="form-grid">
                      <div>
                        <label>Neues Passwort</label>
                        <input
                            type="password"
                            value={passwordNew}
                            onChange={(e) => setPasswordNew(e.target.value)}
                          />
                        </div>
                        <div>
                          <label>Passwort wiederholen</label>
                          <input
                            type="password"
                            value={passwordRepeat}
                            onChange={(e) => setPasswordRepeat(e.target.value)}
                          />
                        </div>
                      </div>
                      <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleChangePassword} disabled={passwordBusy}>
                        {passwordBusy ? 'Speichert…' : 'Passwort speichern'}
                      </button>
                    </div>
                  )}
                  {passwordError && <div className="hint">{passwordError}</div>}
                  {passwordSuccess && <div className="hint">{passwordSuccess}</div>}
                </section>

                <section className="card admin-compact-card">
                  <div className="card-title">Einstellungen</div>
                  <button
                    className="secondary sb-btn sb-btn-secondary admin-compact-toggle"
                    type="button"
                    onClick={() => setShowUserSettings((prev) => !prev)}
                  >
                    {showUserSettings ? 'Einstellungen ausblenden' : 'Einstellungen'}
                  </button>
                  {showUserSettings && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div>
                          <label>Sprache</label>
                          <select value={preferredLanguageDraft} onChange={(e) => setPreferredLanguageDraft(String(e.target.value || 'en').toLowerCase() === 'de' ? 'de' : 'en')}>
                            <option value="de">Deutsch</option>
                            <option value="en">English</option>
                          </select>
                        </div>
                      </div>
                      <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleSaveUserSettings} disabled={settingsBusy}>
                        {settingsBusy ? 'Speichert…' : 'Einstellungen speichern'}
                      </button>
                    </div>
                  )}
                  {settingsError && <div className="hint">{settingsError}</div>}
                  {settingsSuccess && <div className="hint">{settingsSuccess}</div>}
                </section>

                <section className="card passkey-manage-card">
                  <div className="card-title">Passkeys verwalten</div>
                  {!passkeySupported && <div className="hint">Passkeys werden von diesem Browser nicht unterstützt.</div>}
                  <button
                    className="secondary sb-btn sb-btn-secondary passkey-manage-toggle"
                    type="button"
                    onClick={() => {
                      setShowPasskeyPanel((prev) => !prev)
                      if (showPasskeyPanel) {
                        setShowPasskeyCreate(false)
                        setEditPasskey({ passkeyId: '', name: '' })
                      }
                    }}
                    disabled={!passkeySupported || passkeyBusy}
                  >
                    {showPasskeyPanel ? 'Passkey-Dialog ausblenden' : 'Passkeys verwalten'}
                  </button>
                  {showPasskeyPanel && (
                    <>
                      <div className="table passkey-table">
                        <div className="table-header">
                          <span>Name</span><span>Zuletzt verwendet</span><span>Aktionen</span>
                        </div>
                        {!passkeys.length && (
                          <div className="table-row">
                            <span>-</span>
                            <span>-</span>
                            <span>-</span>
                          </div>
                        )}
                        {passkeys.map((item) => {
                          const isEditing = editPasskey.passkeyId === item.passkey_id
                          return (
                            <div className="table-row" key={item.passkey_id}>
                              {isEditing ? (
                                <>
                                  <span>
                                    <input
                                      className="tenant-user-edit-input"
                                      type="text"
                                      value={editPasskey.name}
                                      onChange={(e) => setEditPasskey((prev) => ({ ...prev, name: e.target.value }))}
                                    />
                                  </span>
                                  <span>{formatDateTimeDisplay(item.last_used_at || item.updated_at || item.created_at)}</span>
                                  <span className="tenant-user-row-actions is-edit">
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSavePasskeyName} disabled={passkeyBusy}>Speichern</button>
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditPasskey({ passkeyId: '', name: '' })} disabled={passkeyBusy}>Abbrechen</button>
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span>{item.name || '-'}</span>
                                  <span>{formatDateTimeDisplay(item.last_used_at || item.updated_at || item.created_at)}</span>
                                  <span className="tenant-user-row-actions">
                                    <button
                                      className="link sb-link tenant-icon-btn"
                                      type="button"
                                      title="Editieren"
                                      aria-label="Passkey editieren"
                                      onClick={() => setEditPasskey({ passkeyId: item.passkey_id, name: item.name || '' })}
                                    >
                                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4zm16.7-11.3a1 1 0 0 0 0-1.4l-2-2a1 1 0 0 0-1.4 0l-1.6 1.6 4 4 1-1.2z" /></svg>
                                    </button>
                                    <button
                                      className="link sb-link tenant-icon-btn danger"
                                      type="button"
                                      title="Löschen"
                                      aria-label="Passkey löschen"
                                      onClick={() => handleDeletePasskey(item.passkey_id, item.name || 'Passkey')}
                                    >
                                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21a2 2 0 0 1-2-2V7h14v12a2 2 0 0 1-2 2H7zm3-3h2V10h-2v8zm4 0h2V10h-2v8zM9 4h6l1 2h4v2H4V6h4l1-2z" /></svg>
                                    </button>
                                  </span>
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <button
                        className="secondary sb-btn sb-btn-secondary"
                        type="button"
                        onClick={() => {
                          setShowPasskeyCreate((prev) => !prev)
                          if (!showPasskeyCreate && !passkeyName.trim()) setPasskeyName(PASSKEY_DEFAULT_NAME)
                        }}
                        disabled={!passkeySupported || passkeyBusy}
                      >
                        {showPasskeyCreate ? 'Passkey-Anlage ausblenden' : 'Passkey hinzufügen'}
                      </button>
                      {showPasskeyCreate && (
                        <div className="tenant-create-wrap">
                          <div className="form-grid">
                            <div>
                              <label>Name</label>
                              <input
                                type="text"
                                placeholder={PASSKEY_DEFAULT_NAME}
                                value={passkeyName}
                                onChange={(e) => setPasskeyName(e.target.value)}
                              />
                            </div>
                          </div>
                          <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleCreatePasskey} disabled={passkeyBusy || !passkeySupported}>
                            {passkeyBusy ? 'Warte auf Passkey…' : 'Passkey registrieren'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {passkeyError && <div className="hint">{passkeyError}</div>}
                  {passkeySuccess && <div className="hint">{passkeySuccess}</div>}
                </section>
              </section>
              )}

              {!isStructureNavigation && authUser?.role === 'platform_admin' && (
                <section className="card platform-org-panel" style={{ order: 1 }}>
                  <div className="card-title">Organisationsverwaltung</div>
                  <div className="archive-search">
                    <input
                      type="search"
                      placeholder="Suche (Organisation, Ansprechpartner, E-Mail, Reseller)"
                      value={platformOrgSearchTerm}
                      onChange={(e) => setPlatformOrgSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="table tenant-admin-table platform-org-table organization-admin-scroll-window">
                    <div className="table-header">
                      <span>Name</span><span>Ansprechpartner</span><span>E-Mail</span><span>Typ</span><span>Aktionen</span>
                    </div>
                    {visiblePlatformOrganizationRows.map((row) => {
                      if (row.type === 'reseller') {
                        const item = row.item
                        const isEditing = editResellerAdmin.userId === item.user_id
                        const isExpanded = Boolean(platformOrgExpandedResellerIds[item.user_id])
                        return (
                          <div
                            className={`table-row org-group-row level-${row.level}${row.hasChildren ? ' is-collapsible' : ''}${isEditing ? ' is-editing' : ''}`}
                            key={`platform-reseller-${item.user_id}`}
                            onClick={(!isEditing && row.hasChildren && !platformOrgSearchTerm.trim()) ? () => togglePlatformOrgResellerExpand(item.user_id) : undefined}
                          >
                            {isEditing ? (
                              <>
                                <span>
                                  <input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                </span>
                                <span>
                                  <input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} />
                                </span>
                                <span>
                                  <input type="email" value={editResellerAdmin.email} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, email: e.target.value }))} />
                                </span>
                                <span>
                                  <span className="org-type-icon" title="Reseller" aria-label="Reseller">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v2H3V7zm0 4h18v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z" /></svg>
                                  </span>
                                </span>
                                <span className="tenant-admin-actions">
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveResellerAdminEdit}>Speichern</button>
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })}>Abbrechen</button>
                                </span>
                              </>
                            ) : (
                              <>
                                <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                  <span className="org-row-main">
                                    {row.hasChildren ? (
                                      <button
                                        className="tree-toggle"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (platformOrgSearchTerm.trim()) return
                                          togglePlatformOrgResellerExpand(item.user_id)
                                        }}
                                        aria-label={isExpanded ? 'Zuklappen' : 'Aufklappen'}
                                        aria-expanded={isExpanded}
                                        disabled={Boolean(platformOrgSearchTerm.trim())}
                                      >
                                        <span className="tree-toggle-icon" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                                      </button>
                                    ) : (
                                      <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                    )}
                                    <span>{highlightSearchSubstring(item.name || '-', platformOrgSearchTerm)}</span>
                                  </span>
                                </span>
                                <span>{highlightSearchSubstring(item.contact_name || '-', platformOrgSearchTerm)}</span>
                                <span>{highlightSearchSubstring(item.email || '-', platformOrgSearchTerm)}</span>
                                <span>
                                  <span className="org-type-icon" title="Reseller" aria-label="Reseller">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v2H3V7zm0 4h18v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z" /></svg>
                                  </span>
                                </span>
                                <span className="tenant-user-row-actions" onClick={(e) => e.stopPropagation()}>
                                  <button className="link sb-link tenant-icon-btn" type="button" title="Editieren" aria-label="Reseller editieren" onClick={() => setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' })}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4zm16.7-11.3a1 1 0 0 0 0-1.4l-2-2a1 1 0 0 0-1.4 0l-1.6 1.6 4 4 1-1.2z" /></svg></button>
                                  {!authUser?.impersonation?.active && (
                                    <button
                                      className={`link sb-link tenant-icon-btn${String(item.status || '').toLowerCase() === 'active' ? '' : ' is-disabled'}`}
                                      type="button"
                                      title={String(item.status || '').toLowerCase() === 'active' ? 'Impersonieren' : 'Impersonieren (inaktiv)'}
                                      aria-label="Reseller impersonieren"
                                      onClick={() => handleImpersonateUser(item.user_id)}
                                      disabled={String(item.status || '').toLowerCase() !== 'active'}
                                    >
                                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c-5 0-9 5-9 7s4 7 9 7 9-5 9-7-4-7-9-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" /></svg>
                                    </button>
                                  )}
                                  <button className="link sb-link tenant-icon-btn danger" type="button" data-i18n-live="1" title={String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'} aria-label="Reseller aktivieren oder deaktivieren" onClick={() => handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller')}>{String(item.status || '').toLowerCase() === 'active' ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21a2 2 0 0 1-2-2V7h14v12a2 2 0 0 1-2 2H7zm3-3h2V10h-2v8zm4 0h2V10h-2v8zM9 4h6l1 2h4v2H4V6h4l1-2z" /></svg> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14h-2V8h2z" /></svg>}</button>
                                </span>
                              </>
                            )}
                          </div>
                        )
                      }
                      const item = row.item
                      const isEditing = editTenantAdmin.userId === item.user_id
                      return (
                        <div className={`table-row level-${row.level}${isEditing ? ' is-editing' : ''}`} key={`platform-tenant-${item.user_id}`}>
                          {isEditing ? (
                            <>
                              <span><input type="text" value={editTenantAdmin.organizationName} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} /></span>
                              <span><input type="text" value={editTenantAdmin.name} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} /></span>
                              <span><input type="email" value={editTenantAdmin.email} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, email: e.target.value }))} /></span>
                              <span>
                                <span className="org-type-icon" title="Organisation" aria-label="Organisation">
                                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 21h16v-2h-1V9h-4v10h-2V5H7v14H4v2zm5-2V7h2v12H9zm8 0v-8h1v8h-1z" /></svg>
                                </span>
                              </span>
                              <span className="tenant-admin-actions">
                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveTenantAdminEdit}>Speichern</button>
                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })}>Abbrechen</button>
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                <span className="org-row-main">
                                  <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                  <span>{highlightSearchSubstring(item.organization_name || '-', platformOrgSearchTerm)}</span>
                                </span>
                              </span>
                              <span>{highlightSearchSubstring(item.name || '-', platformOrgSearchTerm)}</span>
                              <span>{highlightSearchSubstring(item.email || '-', platformOrgSearchTerm)}</span>
                              <span>
                                <span className="org-type-icon" title="Organisation" aria-label="Organisation">
                                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 21h16v-2h-1V9h-4v10h-2V5H7v14H4v2zm5-2V7h2v12H9zm8 0v-8h1v8h-1z" /></svg>
                                </span>
                              </span>
                              <span className="tenant-user-row-actions" onClick={(e) => e.stopPropagation()}>
                                <button className="link sb-link tenant-icon-btn" type="button" title="Editieren" aria-label="Organisation editieren" onClick={() => setEditTenantAdmin({ userId: item.user_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' })}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4zm16.7-11.3a1 1 0 0 0 0-1.4l-2-2a1 1 0 0 0-1.4 0l-1.6 1.6 4 4 1-1.2z" /></svg></button>
                                {!authUser?.impersonation?.active && (
                                  <button
                                    className={`link sb-link tenant-icon-btn${String(item.status || '').toLowerCase() === 'active' ? '' : ' is-disabled'}`}
                                    type="button"
                                    title={String(item.status || '').toLowerCase() === 'active' ? 'Impersonieren' : 'Impersonieren (inaktiv)'}
                                    aria-label="Organisation impersonieren"
                                    onClick={() => handleImpersonateUser(item.user_id)}
                                    disabled={String(item.status || '').toLowerCase() !== 'active'}
                                  >
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c-5 0-9 5-9 7s4 7 9 7 9-5 9-7-4-7-9-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" /></svg>
                                  </button>
                                )}
                                <button className="link sb-link tenant-icon-btn danger" type="button" data-i18n-live="1" title={String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'} aria-label="Organisation aktivieren oder deaktivieren" onClick={() => handleTogglePlatformTenantAdminStatus(item)}>{String(item.status || '').toLowerCase() === 'active' ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21a2 2 0 0 1-2-2V7h14v12a2 2 0 0 1-2 2H7zm3-3h2V10h-2v8zm4 0h2V10h-2v8zM9 4h6l1 2h4v2H4V6h4l1-2z" /></svg> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14h-2V8h2z" /></svg>}</button>
                              </span>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="platform-mobile-list" role="list">
                    {visiblePlatformOrganizationRows.map((row) => {
                      const rowKey = `${row.type}:${row.item.user_id}`
                      const isExpanded = expandedPlatformMobileRowId === rowKey
                      const isReseller = row.type === 'reseller'
                      const item = row.item
                      const isEditing = isReseller ? editResellerAdmin.userId === item.user_id : editTenantAdmin.userId === item.user_id
                      const canToggleChildren = isReseller && row.hasChildren && !platformOrgSearchTerm.trim()
                      return (
                        <div className="platform-mobile-item" key={`platform-mobile-${rowKey}`} role="listitem">
                          <button
                            className="platform-mobile-summary"
                            type="button"
                            onClick={() => {
                              if (canToggleChildren) togglePlatformOrgResellerExpand(item.user_id)
                              setExpandedPlatformMobileRowId((prev) => (prev === rowKey ? '' : rowKey))
                            }}
                            aria-expanded={isExpanded}
                          >
                            <span className={`platform-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                            <span className="platform-mobile-name" style={{ paddingLeft: `${row.level * 14}px` }}>
                              {isReseller ? highlightSearchSubstring(item.name || '-', platformOrgSearchTerm) : highlightSearchSubstring(item.organization_name || '-', platformOrgSearchTerm)}
                            </span>
                            <span className="platform-mobile-email">{highlightSearchSubstring(item.email || '-', platformOrgSearchTerm)}</span>
                            <span className={`tenant-user-status-badge platform-mobile-status is-${(isReseller ? getAdminStatusMeta(item.status).key : 'active')}`} data-i18n-live="1">
                              {isReseller ? highlightSearchSubstring(getAdminStatusMeta(item.status).label, platformOrgSearchTerm) : highlightSearchSubstring('Organisation', platformOrgSearchTerm)}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="platform-mobile-details">
                              {isEditing ? (
                                <div className="platform-mobile-edit">
                                  {isReseller ? (
                                    <>
                                      <input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} />
                                      <input type="email" value={editResellerAdmin.email} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, email: e.target.value }))} />
                                      <div className="platform-mobile-actions">
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveResellerAdminEdit}>Speichern</button>
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })}>Abbrechen</button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <input type="text" value={editTenantAdmin.organizationName} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} />
                                      <input type="text" value={editTenantAdmin.name} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <input type="email" value={editTenantAdmin.email} onChange={(e) => setEditTenantAdmin((prev) => ({ ...prev, email: e.target.value }))} />
                                      <div className="platform-mobile-actions">
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveTenantAdminEdit}>Speichern</button>
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditTenantAdmin({ userId: '', organizationName: '', name: '', email: '' })}>Abbrechen</button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="platform-mobile-actions">
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => (isReseller ? setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' }) : setEditTenantAdmin({ userId: item.user_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }))}>Editieren</button>
                                  {!authUser?.impersonation?.active && (
                                    <button
                                      className="secondary sb-btn sb-btn-secondary"
                                      type="button"
                                      onClick={() => (isReseller ? handleImpersonateUser(item.user_id) : handleImpersonateUser(item.user_id))}
                                      disabled={String(item.status || '').toLowerCase() !== 'active'}
                                    >
                                      Impersonieren
                                    </button>
                                  )}
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" data-i18n-live="1" onClick={() => (isReseller ? handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller') : handleTogglePlatformTenantAdminStatus(item))}>
                                    {String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {!visiblePlatformOrganizationRows.length && (
                    <div className="hint">
                      {platformOrgSearchTerm.trim() ? 'Keine passende Organisation gefunden.' : 'Keine Einträge vorhanden.'}
                    </div>
                  )}
                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setShowTenantCreate((prev) => !prev)}>
                    {showTenantCreate ? 'Anlegen ausblenden' : 'Neue Organisation anlegen'}
                  </button>
                  {showTenantCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div><label>Organisationsname</label><input type="text" placeholder="Organisation" value={tenantAdminOrgName} onChange={(e) => setTenantAdminOrgName(e.target.value)} /></div>
                        <div><label>Name Hauptnutzer</label><input type="text" placeholder="Klartext Name" value={tenantAdminName} onChange={(e) => setTenantAdminName(e.target.value)} /></div>
                        <div><label>E-Mail Hauptnutzer</label><input type="email" placeholder="admin@tenant.tld" value={tenantAdminEmail} onChange={(e) => setTenantAdminEmail(e.target.value)} /></div>
                      </div>
                      <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleCreateTenantAdmin}>Anlegen</button>
                    </div>
                  )}
                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setShowResellerCreate((prev) => !prev)}>
                    {showResellerCreate ? 'Anlegen ausblenden' : 'Reseller anlegen'}
                  </button>
                  {showResellerCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div><label>Reseller Name (optional)</label><input type="text" placeholder="Reseller Name" value={resellerAdminName} onChange={(e) => setResellerAdminName(e.target.value)} /></div>
                        <div><label>Name Reseller-Admin (optional)</label><input type="text" placeholder="Klartext Name" value={resellerAdminContactName} onChange={(e) => setResellerAdminContactName(e.target.value)} /></div>
                        <div><label>E-Mail</label><input type="email" placeholder="reseller@partner.tld" value={resellerAdminEmail} onChange={(e) => setResellerAdminEmail(e.target.value)} /></div>
                      </div>
                      <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleCreateResellerAdmin}>Anlegen</button>
                    </div>
                  )}
                  {platformError && <div className="hint">{platformError}</div>}
                </section>
              )}

              {!isStructureNavigation && authUser?.role === 'reseller_admin' && (
                <section className="card reseller-org-panel">
                  <div className="card-title">Organisationsverwaltung</div>
                  <div className="archive-search">
                    <input
                      type="search"
                      placeholder="Suche (Reseller, Organisation, Ansprechpartner, E-Mail, Status)"
                      value={resellerSearchTerm}
                      onChange={(e) => setResellerSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="table tenant-admin-table reseller-org-table organization-admin-scroll-window">
                    <div className="table-header">
                      <span>Name</span><span>Ansprechpartner</span><span>E-Mail</span><span>Impersonation</span><span>Aktionen</span>
                    </div>
                    {visibleResellerOrganizationRows.map((row) => {
                      if (row.type === 'reseller') {
                        const item = row.item
                        const isEditing = editResellerAdmin.userId === item.user_id
                        const isExpanded = Boolean(resellerOrgExpandedResellerIds[item.user_id])
                        const canManageResellerNode = Boolean(authUser?.user_id) && item.created_by_reseller_admin_id === authUser.user_id
                        return (
                          <div
                            className={`table-row org-group-row level-${row.level}${row.hasChildren ? ' is-collapsible' : ''}${isEditing ? ' is-editing' : ''}`}
                            key={`reseller-admin-${item.user_id}`}
                            onClick={(!isEditing && row.hasChildren && !resellerSearchTerm.trim()) ? () => toggleResellerOrgResellerExpand(item.user_id) : undefined}
                          >
                            {isEditing ? (
                              <>
                                <span><input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} /></span>
                                <span><input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} /></span>
                                <span><input type="email" value={editResellerAdmin.email} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, email: e.target.value }))} /></span>
                                <span>-</span>
                                <span className="tenant-admin-actions">
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveResellerAdminEdit}>Speichern</button>
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })}>Abbrechen</button>
                                </span>
                              </>
                            ) : (
                              <>
                                <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                  <span className="org-row-main">
                                    {row.hasChildren ? (
                                      <button
                                        className="tree-toggle"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (resellerSearchTerm.trim()) return
                                          toggleResellerOrgResellerExpand(item.user_id)
                                        }}
                                        aria-label={isExpanded ? 'Zuklappen' : 'Aufklappen'}
                                        aria-expanded={isExpanded}
                                        disabled={Boolean(resellerSearchTerm.trim())}
                                      >
                                        <span className="tree-toggle-icon" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                                      </button>
                                    ) : (
                                      <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                    )}
                                    <span>{highlightSearchSubstring(item.name || '-', resellerSearchTerm)}</span>
                                  </span>
                                </span>
                                <span>{highlightSearchSubstring(item.contact_name || '-', resellerSearchTerm)}</span>
                                <span>{highlightSearchSubstring(item.email || '-', resellerSearchTerm)}</span>
                                <span><span className={`impersonation-status-icon is-${getImpersonationStatusMeta(getResellerImpersonationStatus(item)).key}`} title={getImpersonationStatusMeta(getResellerImpersonationStatus(item)).title} /></span>
                                <span className="tenant-user-row-actions" onClick={(e) => e.stopPropagation()}>
                                  {canManageResellerNode && (
                                    <button className="link sb-link tenant-icon-btn" type="button" title="Editieren" aria-label="Reseller editieren" onClick={() => setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' })}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4zm16.7-11.3a1 1 0 0 0 0-1.4l-2-2a1 1 0 0 0-1.4 0l-1.6 1.6 4 4 1-1.2z" /></svg></button>
                                  )}
                                  {Boolean(item.impersonation_allowed) && !authUser?.impersonation?.active && item.user_id !== authUser?.user_id && (
                                    <button
                                      className={`link sb-link tenant-icon-btn${String(item.status || '').toLowerCase() === 'active' ? '' : ' is-disabled'}`}
                                      type="button"
                                      title={String(item.status || '').toLowerCase() === 'active' ? 'Impersonieren' : 'Impersonieren (inaktiv)'}
                                      aria-label="Reseller impersonieren"
                                      onClick={() => handleImpersonateResellerUser(item.user_id)}
                                      disabled={String(item.status || '').toLowerCase() !== 'active'}
                                    >
                                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c-5 0-9 5-9 7s4 7 9 7 9-5 9-7-4-7-9-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" /></svg>
                                    </button>
                                  )}
                                  {canManageResellerNode && (
                                    <button className="link sb-link tenant-icon-btn danger" type="button" data-i18n-live="1" title={String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'} aria-label="Reseller aktivieren oder deaktivieren" onClick={() => handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller')}>{String(item.status || '').toLowerCase() === 'active' ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21a2 2 0 0 1-2-2V7h14v12a2 2 0 0 1-2 2H7zm3-3h2V10h-2v8zm4 0h2V10h-2v8zM9 4h6l1 2h4v2H4V6h4l1-2z" /></svg> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14h-2V8h2z" /></svg>}</button>
                                  )}
                                </span>
                              </>
                            )}
                          </div>
                        )
                      }
                      const item = row.item
                      const isEditing = editResellerTenantAdmin.userId === item.user_id
                      const tenantCreatorId = item.created_by_user_id || item.reseller_admin_id || row.parentResellerId
                      const canManageTenantNode = Boolean(authUser?.user_id) && tenantCreatorId === authUser.user_id
                      const resellerTenantImpersonationStatus = getResellerImpersonationStatus(item)
                      const canStartTenantImpersonation = resellerTenantImpersonationStatus === 'approved' && !authUser?.impersonation?.active
                      const canChangeTenantImpersonation = Boolean(authUser?.user_id) && item.reseller_admin_id === authUser.user_id && !authUser?.impersonation?.active
                      return (
                        <div className={`table-row level-${row.level}${isEditing ? ' is-editing' : ''}`} key={`reseller-tenant-${item.user_id}`}>
                          {isEditing ? (
                            <>
                              <span><input type="text" value={editResellerTenantAdmin.organizationName} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} /></span>
                              <span><input type="text" value={editResellerTenantAdmin.name} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} /></span>
                              <span><input type="email" value={editResellerTenantAdmin.email} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, email: e.target.value }))} /></span>
                              <span>-</span>
                              <span className="tenant-admin-actions tenant-admin-actions-inline">
                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveResellerTenantAdminEdit}>Speichern</button>
                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => { setEditResellerTenantAdmin({ userId: '', tenantId: '', organizationName: '', name: '', email: '' }) }}>Abbrechen</button>
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ paddingLeft: `${row.level * 18}px` }}>
                                <span className="org-row-main">
                                  <span className="tree-toggle tree-toggle-spacer" aria-hidden="true" />
                                  <span>{highlightSearchSubstring(item.organization_name || '-', resellerSearchTerm)}</span>
                                </span>
                              </span>
                              <span>{highlightSearchSubstring(item.name || '-', resellerSearchTerm)}</span>
                              <span>{highlightSearchSubstring(item.email || '-', resellerSearchTerm)}</span>
                              <span><span className={`impersonation-status-icon is-${getImpersonationStatusMeta(getResellerImpersonationStatus(item)).key}`} title={getImpersonationStatusMeta(getResellerImpersonationStatus(item)).title} /></span>
                              <span className="tenant-user-row-actions" onClick={(e) => e.stopPropagation()}>
                                {canStartTenantImpersonation && (
                                  <button
                                    className={`link sb-link tenant-icon-btn${String(item.status || '').toLowerCase() === 'active' ? '' : ' is-disabled'}`}
                                    type="button"
                                    title={String(item.status || '').toLowerCase() === 'active' ? 'Impersonieren' : 'Impersonieren (inaktiv)'}
                                    aria-label="Organisation impersonieren"
                                    onClick={() => handleImpersonateTenant(item.tenant_id)}
                                    disabled={String(item.status || '').toLowerCase() !== 'active'}
                                  >
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c-5 0-9 5-9 7s4 7 9 7 9-5 9-7-4-7-9-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" /></svg>
                                  </button>
                                )}
                                {canManageTenantNode && (
                                  <button className="link sb-link tenant-icon-btn" type="button" title="Editieren" aria-label="Organisation editieren" onClick={() => { setEditResellerTenantAdmin({ userId: item.user_id, tenantId: item.tenant_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }) }}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4zm16.7-11.3a1 1 0 0 0 0-1.4l-2-2a1 1 0 0 0-1.4 0l-1.6 1.6 4 4 1-1.2z" /></svg></button>
                                )}
                                {canChangeTenantImpersonation && resellerTenantImpersonationStatus === 'revoked' && (
                                  <button
                                    className="link sb-link tenant-icon-btn"
                                    type="button"
                                    title="Impersonation anfragen"
                                    aria-label="Impersonation anfragen"
                                    onClick={() => handleRequestResellerTenantImpersonation(item.tenant_id)}
                                  >
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4.4 0-8 2.2-8 5v2h11.2a6 6 0 0 1-.2-1.5A5.5 5.5 0 0 1 18.5 13H10zm9.5 1v3h3v2h-3v3h-2v-3h-3v-2h3v-3h2z" /></svg>
                                  </button>
                                )}
                                {canChangeTenantImpersonation && resellerTenantImpersonationStatus !== 'revoked' && (
                                  <button
                                    className="link sb-link tenant-icon-btn"
                                    type="button"
                                    title={resellerTenantImpersonationStatus === 'approved' ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen'}
                                    aria-label={resellerTenantImpersonationStatus === 'approved' ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen'}
                                    onClick={() => handleWithdrawResellerTenantImpersonation(item.tenant_id, resellerTenantImpersonationStatus, item.organization_name || item.name || item.email)}
                                  >
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4.4 0-8 2.2-8 5v2h11.2a6 6 0 0 1-.2-1.5A5.5 5.5 0 0 1 18.5 13H10zm4.5 4h8v2h-8v-2z" /></svg>
                                  </button>
                                )}
                                {canManageTenantNode && (
                                  <button className="link sb-link tenant-icon-btn danger" type="button" data-i18n-live="1" title={String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'} aria-label="Organisation aktivieren oder deaktivieren" onClick={() => handleToggleResellerTenantStatus(item)}>{String(item.status || '').toLowerCase() === 'active' ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21a2 2 0 0 1-2-2V7h14v12a2 2 0 0 1-2 2H7zm3-3h2V10h-2v8zm4 0h2V10h-2v8zM9 4h6l1 2h4v2H4V6h4l1-2z" /></svg> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14h-2V8h2z" /></svg>}</button>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="platform-mobile-list" role="list">
                    {visibleResellerOrganizationRows.map((row) => {
                      const rowKey = `${row.type}:${row.item.user_id}`
                      const item = row.item
                      const isReseller = row.type === 'reseller'
                      const isExpanded = expandedResellerMobileRowId === rowKey
                      const isEditing = isReseller ? editResellerAdmin.userId === item.user_id : editResellerTenantAdmin.userId === item.user_id
                      const canManageRow = isReseller
                        ? (Boolean(authUser?.user_id) && item.created_by_reseller_admin_id === authUser.user_id)
                        : (Boolean(authUser?.user_id) && (item.created_by_user_id || item.reseller_admin_id || row.parentResellerId) === authUser.user_id)
                      const resellerMobileTenantImpersonationStatus = getResellerImpersonationStatus(item)
                      const canStartMobileTenantImpersonation = !isReseller && resellerMobileTenantImpersonationStatus === 'approved' && !authUser?.impersonation?.active
                      const canChangeMobileTenantImpersonation = Boolean(authUser?.user_id) && !isReseller && item.reseller_admin_id === authUser.user_id && !authUser?.impersonation?.active
                      const canToggleChildren = isReseller && row.hasChildren && !resellerSearchTerm.trim()
                      return (
                        <div className="platform-mobile-item" key={`reseller-mobile-${rowKey}`} role="listitem">
                          <button
                            className="platform-mobile-summary"
                            type="button"
                            onClick={() => {
                              if (canToggleChildren) toggleResellerOrgResellerExpand(item.user_id)
                              setExpandedResellerMobileRowId((prev) => (prev === rowKey ? '' : rowKey))
                            }}
                            aria-expanded={isExpanded}
                          >
                            <span className={`platform-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                            <span className="platform-mobile-name" style={{ paddingLeft: `${row.level * 14}px` }}>
                              {isReseller ? highlightSearchSubstring(item.name || '-', resellerSearchTerm) : highlightSearchSubstring(item.organization_name || '-', resellerSearchTerm)}
                            </span>
                            <span className="platform-mobile-email">{highlightSearchSubstring(item.email || '-', resellerSearchTerm)}</span>
                            <span className={`tenant-user-status-badge platform-mobile-status is-${(isReseller ? getAdminStatusMeta(item.status).key : getImpersonationStatusMeta(getResellerImpersonationStatus(item)).key)}`} data-i18n-live="1">
                              {isReseller ? highlightSearchSubstring(getAdminStatusMeta(item.status).label, resellerSearchTerm) : highlightSearchSubstring(getImpersonationStatusMeta(getResellerImpersonationStatus(item)).label, resellerSearchTerm)}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="platform-mobile-details">
                              {isEditing ? (
                                <div className="platform-mobile-edit">
                                  {isReseller ? (
                                    <>
                                      <input type="text" value={editResellerAdmin.name} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <input type="text" value={editResellerAdmin.contactName} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, contactName: e.target.value }))} />
                                      <input type="email" value={editResellerAdmin.email} onChange={(e) => setEditResellerAdmin((prev) => ({ ...prev, email: e.target.value }))} />
                                      <div className="platform-mobile-actions">
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveResellerAdminEdit}>Speichern</button>
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditResellerAdmin({ userId: '', name: '', contactName: '', email: '' })}>Abbrechen</button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <input type="text" value={editResellerTenantAdmin.organizationName} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, organizationName: e.target.value }))} />
                                      <input type="text" value={editResellerTenantAdmin.name} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, name: e.target.value }))} />
                                      <input type="email" value={editResellerTenantAdmin.email} onChange={(e) => setEditResellerTenantAdmin((prev) => ({ ...prev, email: e.target.value }))} />
                                      <div className="platform-mobile-actions">
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveResellerTenantAdminEdit}>Speichern</button>
                                        <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => { setEditResellerTenantAdmin({ userId: '', tenantId: '', organizationName: '', name: '', email: '' }) }}>Abbrechen</button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="platform-mobile-actions">
                                  {canManageRow && (
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => (isReseller ? setEditResellerAdmin({ userId: item.user_id, name: item.name || '', contactName: item.contact_name || '', email: item.email || '' }) : setEditResellerTenantAdmin({ userId: item.user_id, tenantId: item.tenant_id, organizationName: item.organization_name || '', name: item.name || '', email: item.email || '' }))}>Editieren</button>
                                  )}
                                  {!authUser?.impersonation?.active && isReseller && Boolean(item.impersonation_allowed) && item.user_id !== authUser?.user_id && (
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleImpersonateResellerUser(item.user_id)} disabled={String(item.status || '').toLowerCase() !== 'active'}>Impersonieren</button>
                                  )}
                                  {canStartMobileTenantImpersonation && (
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleImpersonateTenant(item.tenant_id)} disabled={String(item.status || '').toLowerCase() !== 'active'}>Impersonieren</button>
                                  )}
                                  {canChangeMobileTenantImpersonation && resellerMobileTenantImpersonationStatus === 'revoked' && (
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleRequestResellerTenantImpersonation(item.tenant_id)}>
                                      Impersonation anfragen
                                    </button>
                                  )}
                                  {canChangeMobileTenantImpersonation && resellerMobileTenantImpersonationStatus !== 'revoked' && (
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleWithdrawResellerTenantImpersonation(item.tenant_id, resellerMobileTenantImpersonationStatus, item.organization_name || item.name || item.email)}>
                                      {resellerMobileTenantImpersonationStatus === 'approved' ? 'Impersonation-Freigabe zurückziehen' : 'Impersonation-Anfrage zurückziehen'}
                                    </button>
                                  )}
                                  {canManageRow && (
                                    <button className="secondary sb-btn sb-btn-secondary" type="button" data-i18n-live="1" onClick={() => (isReseller ? handleDeleteResellerAdmin(item.user_id, item.name || item.email || 'Reseller') : handleToggleResellerTenantStatus(item))}>
                                      {String(item.status || '').toLowerCase() === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {!resellerOrganizationRows.length && <div className="hint">Noch keine Einträge vorhanden.</div>}
                  {!visibleResellerOrganizationRows.length && (
                    <div className="hint">
                      {resellerSearchTerm.trim() ? 'Keine passenden Einträge gefunden.' : 'Noch keine Einträge vorhanden.'}
                    </div>
                  )}
                  <button
                    className="secondary sb-btn sb-btn-secondary"
                    type="button"
                    onClick={() => setShowResellerTenantCreate((prev) => !prev)}
                  >
                    {showResellerTenantCreate ? 'Anlegen ausblenden' : 'Kundenorganisation anlegen'}
                  </button>
                  {showResellerTenantCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div>
                          <label>Organisationsname</label>
                          <input
                            type="text"
                            placeholder="Organisation"
                            value={resellerTenantOrgName}
                            onChange={(e) => setResellerTenantOrgName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label>Name Hauptnutzer</label>
                          <input
                            type="text"
                            placeholder="Klartext Name"
                            value={resellerTenantName}
                            onChange={(e) => setResellerTenantName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label>E-Mail Hauptnutzer</label>
                          <input
                            type="email"
                            placeholder="admin@tenant.tld"
                            value={resellerTenantEmail}
                            onChange={(e) => setResellerTenantEmail(e.target.value)}
                          />
                        </div>
                        <label className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={resellerTenantRequestImpersonation}
                            onChange={(e) => setResellerTenantRequestImpersonation(e.target.checked)}
                          />
                          <span>Impersonation anfragen</span>
                        </label>
                      </div>
                      <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleCreateResellerTenantAdmin}>
                        Anlegen
                      </button>
                    </div>
                  )}
                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setShowResellerSubCreate((prev) => !prev)}>
                    {showResellerSubCreate ? 'Anlegen ausblenden' : 'Reseller anlegen'}
                  </button>
                  {showResellerSubCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div><label>Reseller Name (optional)</label><input type="text" placeholder="Reseller Name" value={resellerAdminName} onChange={(e) => setResellerAdminName(e.target.value)} /></div>
                        <div><label>Name Reseller-Admin (optional)</label><input type="text" placeholder="Klartext Name" value={resellerAdminContactName} onChange={(e) => setResellerAdminContactName(e.target.value)} /></div>
                        <div><label>E-Mail</label><input type="email" placeholder="reseller@partner.tld" value={resellerAdminEmail} onChange={(e) => setResellerAdminEmail(e.target.value)} /></div>
                        <label className="multi-select-option">
                          <input type="checkbox" checked={resellerSubRequestImpersonation} onChange={(e) => setResellerSubRequestImpersonation(e.target.checked)} />
                          <span>Impersonation anfragen</span>
                        </label>
                      </div>
                      <button className="secondary tenant-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleCreateResellerAdmin}>Anlegen</button>
                    </div>
                  )}
                  <div className="card-title">Impersonation-Anfragen</div>
                  <div className="archive-search">
                    <input
                      type="search"
                      placeholder="Suche (Name, E-Mail)"
                      value={resellerImpersonationSearchTerm}
                      onChange={(e) => setResellerImpersonationSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="organization-admin-scroll-window reseller-impersonation-scroll-window">
                    <div className="table tenant-admin-table reseller-impersonation-table">
                      <div className="table-header">
                        <span>Anfrager</span><span>E-Mail</span><span>Status</span><span>Aktionen</span>
                      </div>
                      {filteredResellerImpersonationPermissions.map((item) => {
                        const meta = getImpersonationStatusMeta(item.status)
                        const status = String(item.status || '').toLowerCase()
                        const nextStatus = status === 'approved' ? 'revoked' : 'approved'
                        const actionLabel = status === 'approved' ? 'Widerrufen' : 'Freigeben'
                        return (
                          <div className="table-row" key={item.permission_id}>
                            <span>{highlightSearchSubstring(item.requester_name || '-', resellerImpersonationSearchTerm)}</span>
                            <span>{highlightSearchSubstring(item.requester_email || '-', resellerImpersonationSearchTerm)}</span>
                            <span><span className={`impersonation-status-badge is-${meta.key}`} title={meta.title}>{meta.label}</span></span>
                            <span className="tenant-admin-actions tenant-admin-actions-inline">
                              <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleUpdateResellerImpersonationPermissionStatus(item.permission_id, nextStatus)}>{actionLabel}</button>
                            </span>
                          </div>
                        )
                      })}
                      {!resellerImpersonationPermissions.length && (
                        <div className="table-row"><span>-</span><span>-</span><span>Keine Anfragen</span><span>-</span></div>
                      )}
                    </div>
                    <div className="reseller-impersonation-mobile-list" role="list">
                      {filteredResellerImpersonationPermissions.map((item) => {
                        const meta = getImpersonationStatusMeta(item.status)
                        const status = String(item.status || '').toLowerCase()
                        const nextStatus = status === 'approved' ? 'revoked' : 'approved'
                        const actionLabel = status === 'approved' ? 'Widerrufen' : 'Freigeben'
                        return (
                          <div className="reseller-impersonation-mobile-item" key={`mobile-${item.permission_id}`} role="listitem">
                            <div className="reseller-impersonation-mobile-row">
                              <span className="reseller-impersonation-mobile-label">Anfrager</span>
                              <strong>{highlightSearchSubstring(item.requester_name || '-', resellerImpersonationSearchTerm)}</strong>
                            </div>
                            <div className="reseller-impersonation-mobile-row">
                              <span className="reseller-impersonation-mobile-label">E-Mail</span>
                              <span>{highlightSearchSubstring(item.requester_email || '-', resellerImpersonationSearchTerm)}</span>
                            </div>
                            <div className="reseller-impersonation-mobile-row">
                              <span className="reseller-impersonation-mobile-label">Status</span>
                              <span className={`impersonation-status-badge is-${meta.key}`} title={meta.title}>{meta.label}</span>
                            </div>
                            <button
                              className="secondary sb-btn sb-btn-secondary"
                              type="button"
                              onClick={() => handleUpdateResellerImpersonationPermissionStatus(item.permission_id, nextStatus)}
                            >
                              {actionLabel}
                            </button>
                          </div>
                        )
                      })}
                      {!resellerImpersonationPermissions.length && (
                        <div className="reseller-impersonation-mobile-item is-empty" role="listitem">
                          <span>Keine Anfragen</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {resellerImpersonationPermissions.length > 0 && !filteredResellerImpersonationPermissions.length && <div className="hint">Keine passenden Anfragen gefunden.</div>}
                  {platformError && <div className="hint">{platformError}</div>}
                </section>
              )}

              {(isTenantAdmin || isTenantUser) && (
              <>
              <section className={`grid tenant-admin-layout-grid${isStructureNavigation ? ' is-structure-view' : ''}`}>
                {!isStructureNavigation && (
                <div className="card tenant-organization-panel">
                  <div className="card-title">Organisation</div>
                  <button
                    className="secondary sb-btn sb-btn-secondary admin-compact-toggle"
                    type="button"
                    onClick={() => setShowTenantOrganizationPanel((prev) => !prev)}
                  >
                    {showTenantOrganizationPanel ? 'Organisation ausblenden' : 'Organisation bearbeiten'}
                  </button>
                  {showTenantOrganizationPanel && (
                  <div className="organization-tenant-row">
                    {!editTenant && <span className="organization-tenant-name">{tenantDisplayName}</span>}
                    {canRenameOrganization && editTenant ? (
                      <div className="organization-tenant-edit">
                        <input
                          type="text"
                          value={tenantNameEdit}
                          onChange={(e) => setTenantNameEdit(e.target.value)}
                        />
                        <button
                          className="secondary sb-btn sb-btn-secondary"
                          type="button"
                          onClick={async () => {
                            await handleRenameTenant()
                            setEditTenant(false)
                          }}
                        >
                          Speichern
                        </button>
                        <button
                          className="link sb-link"
                          type="button"
                          onClick={() => {
                            setTenantNameEdit(tenantDisplayName === '-' ? '' : tenantDisplayName)
                            setEditTenant(false)
                          }}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      canRenameOrganization && (
                        <span className="organization-tenant-actions">
                          <button
                            className="link sb-link"
                            type="button"
                            onClick={() => setEditTenant(true)}
                          >
                            Umbenennen
                          </button>
                        </span>
                      )
                    )}
                  </div>
                  )}
                </div>
                )}
                {isStructureNavigation && (
                <div className="card tenant-structure-panel">
                  <div className="card-title">Unternehmensstruktur</div>
                  <div className="archive-search">
                    <input
                      type="search"
                      placeholder="Suche (Unternehmen, Betriebsstätte, POS)"
                      value={organizationSearchTerm}
                      onChange={(e) => setOrganizationSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="organization-structure-header" aria-hidden="true">
                    <span>Name</span>
                    <span>Aktionen</span>
                  </div>
                  {companies.length === 0 && (
                    <>
                      <div className="hint">Keine Unternehmen vorhanden.</div>
                      {canEditCompanies && (
                        <button
                          className="link sb-link organization-empty-create-action"
                          type="button"
                          onClick={handleToggleCompanyCreate}
                        >
                          + Erstes Unternehmen anlegen
                        </button>
                      )}
                    </>
                  )}
                  {companies.length > 0 && filteredOrganizationCompanies.length === 0 && (
                    <div className="hint">Nicht gefunden</div>
                  )}
                  {filteredOrganizationCompanies.length > 0 && (
                    <div className={`tree organization-tree-scroll-window${editPos.id ? ' is-pos-editing' : ''}`}>
                      {filteredOrganizationCompanies.map((company) => {
                        const companySites = company.sites || []
                        const companyExpanded = isOrganizationSearchActive
                          ? companySites.length > 0
                          : (expandedCompanies[company.company_id] ?? false)
                                  const hasNoSites = companySites.length === 0
                                  const showCompanyCreateHint = canEditSites && !isOrganizationSearchActive && !companyExpanded && hasNoSites
                        return (
                          <div className="tree-node" key={company.company_id}>
                            <div
                              className="tree-row tree-company"
                              onClick={editCompany.id === company.company_id ? undefined : () => toggleCompanyExpand(company.company_id)}
                            >
                              {editCompany.id === company.company_id ? (
                                <div className="tree-edit-line">
                                  <input
                                    type="text"
                                    value={editCompany.name}
                                    onChange={(e) => setEditCompany({ id: company.company_id, name: e.target.value })}
                                  />
                                  <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleRenameCompany(company.company_id, editCompany.name)}>Speichern</button>
                                  <button className="link sb-link" type="button" onClick={() => setEditCompany({ id: '', name: '' })}>Abbrechen</button>
                                </div>
                              ) : (
                                <>
                                  <span className={`tree-row-main${showCompanyCreateHint ? ' has-create-hint' : ''}`}>
                                    <span className="tree-row-main-top">
                                      <button
                                        className="tree-toggle"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          toggleCompanyExpand(company.company_id)
                                        }}
                                        aria-label={companyExpanded
                                          ? formatUiTemplate('aria.company.collapse', { name: company.name })
                                          : formatUiTemplate('aria.company.expand', { name: company.name })}
                                        aria-expanded={companyExpanded}
                                      >
                                        <span className="tree-toggle-icon" aria-hidden="true">{companyExpanded ? '▾' : '▸'}</span>
                                      </button>
                                      <span>{highlightSearchSubstring(company.name, organizationSearchTerm)}</span>
                                    </span>
                                    {showCompanyCreateHint && (
                                      <span className="tree-row-create-hint">(+ Erste Betriebsstätte anlegen)</span>
                                    )}
                                  </span>
                                  {canEditCompanies && (
                                    <span className="tree-row-actions">
                                      <button
                                        className="link sb-link"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setEditCompany({ id: company.company_id, name: company.name })
                                        }}
                                      >
                                        Umbenennen
                                      </button>
                                      <button
                                        className="link sb-link"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleDeleteCompany(company.company_id, company.name)
                                        }}
                                      >
                                        Löschen
                                      </button>
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            {companyExpanded && (
                              <>
                                {canEditSites && !isOrganizationSearchActive && (
                                  <button
                                    className="link tree-action tree-site sb-link"
                                    type="button"
                                    onClick={() => {
                                      setSelectedCompany((prev) => (prev === company.company_id ? '' : company.company_id))
                                      setSiteName('')
                                    }}
                                  >
                                    {hasNoSites ? '+ Erste Betriebsstätte anlegen' : '+ Betriebsstätte anlegen'}
                                  </button>
                                )}
                                {canEditSites && !isOrganizationSearchActive && selectedCompany === company.company_id && (
                                  <div className="tree-inline-form tree-site">
                                    <input
                                      type="text"
                                      placeholder="Betriebsstätte"
                                      value={siteName}
                                      onChange={(e) => setSiteName(e.target.value)}
                                    />
                                    <button
                                      className="secondary sb-btn sb-btn-secondary"
                                      type="button"
                                      onClick={() => {
                                        handleCreateSite(company.company_id, siteName)
                                        setSiteName('')
                                        setSelectedCompany('')
                                      }}
                                    >
                                      Anlegen
                                    </button>
                                  </div>
                                )}
                                {companySites.length === 0 && (
                                  canEditSites && !isOrganizationSearchActive ? (
                                    <button
                                      className="link tree-row tree-empty tree-empty-action sb-link"
                                      type="button"
                                      onClick={() => {
                                        setSelectedCompany(company.company_id)
                                        setSiteName('')
                                      }}
                                    >
                                      Keine Betriebsstätten
                                    </button>
                                  ) : (
                                    <div className="tree-row tree-empty">Keine Betriebsstätten</div>
                                  )
                                )}
                                {companySites.map((site) => {
                                  const sitePos = site.posItems || []
                                  const hasNoPos = sitePos.length === 0
                                  const siteExpanded = isOrganizationSearchActive
                                    ? sitePos.length > 0
                                    : (expandedSites[site.site_id] ?? true)
                                  const showSiteCreateHint = canEditPos && !isOrganizationSearchActive && !siteExpanded && hasNoPos
                                  return (
                                    <div className="tree-node" key={site.site_id}>
                                      <div
                                        className="tree-row tree-site"
                                        onClick={editSite.id === site.site_id ? undefined : () => toggleSiteExpand(site.site_id)}
                                      >
                                        {editSite.id === site.site_id ? (
                                          <div className="tree-edit-line">
                                            <input
                                              type="text"
                                              value={editSite.name}
                                              onChange={(e) => setEditSite({ id: site.site_id, name: e.target.value })}
                                            />
                                            <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => handleRenameSite(site.site_id, editSite.name)}>Speichern</button>
                                            <button className="link sb-link" type="button" onClick={() => setEditSite({ id: '', name: '' })}>Abbrechen</button>
                                          </div>
                                        ) : (
                                          <>
                                            <span className={`tree-row-main${showSiteCreateHint ? ' has-create-hint' : ''}`}>
                                              <span className="tree-row-main-top">
                                                <button
                                                  className="tree-toggle"
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    toggleSiteExpand(site.site_id)
                                                  }}
                                                  aria-label={siteExpanded
                                                    ? formatUiTemplate('aria.site.collapse', { name: site.name })
                                                    : formatUiTemplate('aria.site.expand', { name: site.name })}
                                                  aria-expanded={siteExpanded}
                                                >
                                                  <span className="tree-toggle-icon" aria-hidden="true">{siteExpanded ? '▾' : '▸'}</span>
                                                </button>
                                                <span>{highlightSearchSubstring(site.name, organizationSearchTerm)}</span>
                                              </span>
                                              {showSiteCreateHint && (
                                                <span className="tree-row-create-hint">(+ Erste POS anlegen)</span>
                                              )}
                                            </span>
                                            {canEditSites && (
                                              <span className="tree-row-actions">
                                                <button
                                                  className="link sb-link"
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    setEditSite({ id: site.site_id, name: site.name })
                                                  }}
                                                >
                                                  Umbenennen
                                                </button>
                                                <button
                                                  className="link sb-link"
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleDeleteSite(site.site_id, site.name)
                                                  }}
                                                >
                                                  Löschen
                                                </button>
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      {siteExpanded && (
                                        <>
                                          {canEditPos && !isOrganizationSearchActive && (
                                            <button
                                              className="link tree-action tree-pos sb-link"
                                              type="button"
                                              onClick={() => openCreatePosDialog(createPosDialog.siteId === site.site_id ? '' : site.site_id)}
                                            >
                                              {hasNoPos ? '+ Erste POS anlegen' : '+ POS anlegen'}
                                            </button>
                                          )}
                                          {canEditPos && !isOrganizationSearchActive && createPosDialog.siteId === site.site_id && (
                                            <div className="tree-pos-settings">
                                              <label>POS Seriennummer</label>
                                              <input
                                                type="text"
                                                value={createPosDialog.posId}
                                                onChange={(e) =>
                                                  setCreatePosDialog((prev) => ({
                                                    ...prev,
                                                    posId: e.target.value,
                                                    status: ''
                                                  }))
                                                }
                                                placeholder="POS Seriennummer"
                                              />
                                              <label>POS Bezeichnung (optional)</label>
                                              <input
                                                type="text"
                                                value={createPosDialog.posLabel}
                                                onChange={(e) => setCreatePosDialog((prev) => ({ ...prev, posLabel: e.target.value, status: '' }))}
                                                placeholder="POS Bezeichnung"
                                              />
                                              <label>TSE Typ</label>
                                              <select
                                                value={createPosDialog.tseType}
                                                onChange={(e) =>
                                                  setCreatePosDialog((prev) => ({
                                                    ...prev,
                                                    tseType: e.target.value,
                                                    status: ''
                                                  }))
                                                }
                                              >
                                                {POS_TSE_TYPE_OPTIONS.map((option) => (
                                                  <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                              </select>
                                              {isSwissbitCloudPosTseType(createPosDialog.tseType) ? (
                                                <>
                                                  <label>TSE Seriennummer</label>
                                                  <input
                                                    type="text"
                                                    value={createPosDialog.tseSerialNumber}
                                                    onChange={(e) =>
                                                      setCreatePosDialog((prev) => ({
                                                        ...prev,
                                                        tseSerialNumber: e.target.value,
                                                        status: ''
                                                      }))
                                                    }
                                                    placeholder="TSE Seriennummer"
                                                  />
                                                </>
                                              ) : isFiskalyCloudPosTseType(createPosDialog.tseType) ? (
                                                <div className="hint">fiskaly Cloud TSE ist aktuell nicht unterstützt.</div>
                                              ) : (
                                                <>
                                                  <label>TSE Seriennummer (optional)</label>
                                                  <input
                                                    type="text"
                                                    value={createPosDialog.tseSerialNumber}
                                                    onChange={(e) =>
                                                      setCreatePosDialog((prev) => ({
                                                        ...prev,
                                                        tseSerialNumber: e.target.value,
                                                        status: ''
                                                      }))
                                                    }
                                                    placeholder="TSE Seriennummer"
                                                  />
                                                </>
                                              )}
                                              {createPosDialog.status && <div className="hint">{createPosDialog.status}</div>}
                                              <div className="tree-pos-settings-actions">
                                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleCreatePos}>Anlegen</button>
                                                <button className="link sb-link" type="button" onClick={resetCreatePosDialog}>Abbrechen</button>
                                              </div>
                                            </div>
                                          )}
                                          {sitePos.length === 0 && (
                                            canEditPos && !isOrganizationSearchActive ? (
                                              <button
                                                className="link tree-row tree-empty tree-empty-action sb-link"
                                                type="button"
                                                onClick={() => openCreatePosDialog(site.site_id)}
                                              >
                                                Keine POS
                                              </button>
                                            ) : (
                                              <div className="tree-row tree-empty">Keine POS</div>
                                            )
                                          )}
                                          {sitePos.map((pos) => (
                                            <div className="tree-node" key={pos.pos_id}>
                                              <div
                                                className={`tree-row tree-pos${canEditPos && editPos.id !== pos.pos_id ? ' tree-pos-row-clickable' : ''}`}
                                                onClick={canEditPos && editPos.id !== pos.pos_id ? () => handleEditPosOpen(pos) : undefined}
                                              >
                                                <span>{highlightSearchSubstring(pos.pos_label || pos.pos_serial, organizationSearchTerm)}</span>
                                                {canEditPos && editPos.id !== pos.pos_id && (
                                                  <span className="tree-row-actions">
                                                    <button
                                                      className="link sb-link"
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        if (editPos.id === pos.pos_id) {
                                                          resetEditPos()
                                                          return
                                                        }
                                                        handleEditPosOpen(pos)
                                                      }}
                                                    >
                                                      Bearbeiten
                                                    </button>
                                                    <button
                                                      className="link sb-link"
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleDeletePos(pos.pos_id, pos.pos_label || pos.pos_serial)
                                                      }}
                                                    >
                                                      Löschen
                                                    </button>
                                                  </span>
                                                )}
                                              </div>
                                              {canEditPos && editPos.id === pos.pos_id && (
                                                <div className="tree-pos-settings">
                                                  <label>POS Seriennummer</label>
                                                  <div className="tree-pos-settings-readonly">{editPos.posSerial}</div>
                                                  <label>POS Bezeichnung (optional)</label>
                                                  <input
                                                    type="text"
                                                    value={editPos.posLabel}
                                                    onChange={(e) => setEditPos((prev) => ({ ...prev, posLabel: e.target.value, status: '' }))}
                                                    placeholder="POS Bezeichnung"
                                                  />
                                                  <label>TSE Typ</label>
                                                  <select
                                                    value={editPos.tseType}
                                                    onChange={(e) =>
                                                      setEditPos((prev) => ({
                                                        ...prev,
                                                        tseType: e.target.value,
                                                        status: ''
                                                      }))
                                                    }
                                                  >
                                                    {POS_TSE_TYPE_OPTIONS.map((option) => (
                                                      <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                  </select>
                                                  {isSwissbitCloudPosTseType(editPos.tseType) ? (
                                                    <>
                                                      <label>TSE Seriennummer</label>
                                                      <input
                                                        type="text"
                                                        value={editPos.tseSerialNumber}
                                                        onChange={(e) =>
                                                          setEditPos((prev) => ({
                                                            ...prev,
                                                            tseSerialNumber: e.target.value,
                                                            status: ''
                                                          }))
                                                        }
                                                        placeholder="TSE Seriennummer"
                                                      />
                                                    </>
                                                  ) : isFiskalyCloudPosTseType(editPos.tseType) ? (
                                                    <div className="hint">fiskaly Cloud TSE ist aktuell nicht unterstützt.</div>
                                                  ) : (
                                                    <>
                                                      <label>TSE Seriennummer (optional)</label>
                                                      <input
                                                        type="text"
                                                        value={editPos.tseSerialNumber}
                                                        onChange={(e) =>
                                                          setEditPos((prev) => ({
                                                            ...prev,
                                                            tseSerialNumber: e.target.value,
                                                            status: ''
                                                          }))
                                                        }
                                                        placeholder="TSE Seriennummer"
                                                      />
                                                    </>
                                                  )}
                                                  {editPos.status && <div className="hint">{editPos.status}</div>}
                                                  <div className="tree-pos-settings-actions">
                                                    <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSavePosSettings}>Speichern</button>
                                                    <button className="link sb-link" type="button" onClick={resetEditPos}>Abbrechen</button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  )
                                })}
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                  <div className="tenant-structure-actions">
                    {canEditCompanies && (
                    <button
                      className="secondary sb-btn sb-btn-secondary tenant-structure-toggle-btn"
                      type="button"
                      onClick={handleToggleCompanyCreate}
                    >
                      Unternehmen anlegen
                    </button>
                    )}
                    {canEditCompanies && showCompanyCreate && (
                      <div className="tree-inline-form">
                        <input
                          type="text"
                          placeholder="Firmenname"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                        />
                        <select
                          value={companyCountry}
                          onChange={(e) => setCompanyCountry(e.target.value)}
                          disabled={!selectableCountries.length}
                        >
                          {!selectableCountries.length && <option value="">Kein Land mit aktivem Profil</option>}
                          {selectableCountries.map((country) => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                        <button
                          className="secondary sb-btn sb-btn-secondary"
                          type="button"
                          onClick={() => {
                            handleCreateCompany(companyName, companyCountry)
                            setCompanyName('')
                            setShowCompanyCreate(false)
                          }}
                        >
                          Anlegen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                )}
                {!isStructureNavigation && canManageUsers && (
                <div className="card tenant-user-panel">
                  <div className="card-title">Nutzerverwaltung</div>
                  <button
                    className="secondary sb-btn sb-btn-secondary admin-compact-toggle"
                    type="button"
                    onClick={() => setShowTenantUsersPanel((prev) => !prev)}
                  >
                    {showTenantUsersPanel ? 'Nutzerverwaltung ausblenden' : 'Nutzerverwaltung öffnen'}
                  </button>
                  {showTenantUsersPanel && (
                  <>
                  <div className="archive-search">
                    <input
                      type="search"
                      placeholder="Suche (Name, E-Mail, Status)"
                      value={tenantUsersSearchTerm}
                      onChange={(e) => setTenantUsersSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="tenant-user-content">
                    <div className={`table tenant-user-table tenant-user-scroll-window${filteredTenantUsers.length ? '' : ' is-empty'}`}>
                      <div className="table-header">
                        <span>Name</span><span>E-Mail</span><span>Status</span><span>Aktionen</span>
                      </div>
                      {filteredTenantUsers.map((item) => (
                        <div className="table-row" key={item.user_id}>
                          {editTenantUser.userId === item.user_id ? (
                            <>
                              <span>
                                <input
                                  className="tenant-user-edit-input"
                                  type="text"
                                  value={editTenantUser.name}
                                  onChange={(e) => setEditTenantUser((prev) => ({ ...prev, name: e.target.value }))}
                                />
                              </span>
                              <span>
                                <input
                                  className="tenant-user-edit-input tenant-user-edit-input-readonly"
                                  type="email"
                                  value={editTenantUser.email}
                                  readOnly
                                  disabled
                                />
                              </span>
                              <span className="tenant-user-status-hidden" aria-hidden="true" />
                              <span className="tenant-user-row-actions is-edit">
                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveTenantUserEdit}>Speichern</button>
                                <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={() => setEditTenantUser({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })}>Abbrechen</button>
                              </span>
                            </>
                          ) : (
                            <>
                              {/*
                                Status-sensitive action rendering:
                                locked -> show unlock icon/action, active -> show lock icon/action.
                              */}
                              <span>{highlightSearchSubstring(item.name || '-', tenantUsersSearchTerm)}</span>
                              <span>{highlightSearchSubstring(item.email || '-', tenantUsersSearchTerm)}</span>
                              <span>
                                <span className={`tenant-user-status-badge is-${getTenantUserStatusMeta(item.status).key}`} data-i18n-live="1">
                                  {highlightSearchSubstring(getTenantUserStatusMeta(item.status).label, tenantUsersSearchTerm)}
                                </span>
                              </span>
                              <span className="tenant-user-row-actions">
                                <button
                                  className="link sb-link tenant-icon-btn"
                                  type="button"
                                  title="Editieren"
                                  aria-label="Nutzer editieren"
                                  onClick={() => setEditTenantUser({ userId: item.user_id, name: item.name || '', email: item.email || '', rights: normalizeTenantUserRights(item.rights) })}
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M4 20h4l10-10-4-4L4 16v4zm16.7-11.3a1 1 0 0 0 0-1.4l-2-2a1 1 0 0 0-1.4 0l-1.6 1.6 4 4 1-1.2z" />
                                  </svg>
                                </button>
                                <button
                                  className="link sb-link tenant-icon-btn"
                                  type="button"
                                  title="Rechte"
                                  aria-label="Nutzerrechte bearbeiten"
                                  onClick={() => openTenantUserRightsDialog('edit', { userId: item.user_id, label: item.email || item.name || 'Nutzer', rights: item.rights })}
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M3 13h2v-2H3v2zm16 0h2v-2h-2v2zM9 13h6v-2H9v2zm0-6h12V5H9v2zm0 12h12v-2H9v2zM3 7h2V5H3v2zm0 12h2v-2H3v2z" />
                                  </svg>
                                </button>
                                <button
                                  className="link sb-link tenant-icon-btn danger"
                                  type="button"
                                  title={item.status === 'locked' ? 'Entsperren' : 'Sperren'}
                                  aria-label={item.status === 'locked' ? 'Nutzer entsperren' : 'Nutzer sperren'}
                                  onClick={() => handleToggleTenantUserLock(item.user_id, item.email || item.name || 'Nutzer', item.status === 'locked')}
                                >
                                  {item.status === 'locked' ? (
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                      <path d="M19 9h-8V7a3 3 0 1 1 6 0h2a5 5 0 1 0-10 0v2H5v12h14V9z" />
                                    </svg>
                                  ) : (
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                      <path d="M17 9V7a5 5 0 0 0-10 0v2H5v12h14V9h-2zm-8 0V7a3 3 0 0 1 6 0v2H9z" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  className="link sb-link tenant-icon-btn danger"
                                  type="button"
                                  title="Löschen"
                                  aria-label="Nutzer löschen"
                                  onClick={() => handleDeleteTenantUser(item.user_id, item.email || item.name || 'Nutzer')}
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M7 21a2 2 0 0 1-2-2V7h14v12a2 2 0 0 1-2 2H7zm3-3h2V10h-2v8zm4 0h2V10h-2v8zM9 4h6l1 2h4v2H4V6h4l1-2z" />
                                  </svg>
                                </button>
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="tenant-users-mobile-list" role="list">
                      {filteredTenantUsers.map((item) => {
                        const statusMeta = getTenantUserStatusMeta(item.status)
                        const isEditing = editTenantUser.userId === item.user_id
                        const isExpanded = isEditing || expandedTenantUserId === item.user_id
                        const detailsId = `tenant-user-mobile-details-${item.user_id}`
                        return (
                          <div className="tenant-users-mobile-item" key={`mobile-user-${item.user_id}`} role="listitem">
                            <button
                              className="tenant-users-mobile-summary"
                              type="button"
                              onClick={() => !isEditing && setExpandedTenantUserId((prev) => (prev === item.user_id ? '' : item.user_id))}
                              aria-expanded={isExpanded}
                              aria-controls={detailsId}
                            >
                              <span className={`tenant-users-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                              <span className="tenant-users-mobile-name">{highlightSearchSubstring(item.name || '-', tenantUsersSearchTerm)}</span>
                              <span className="tenant-users-mobile-email">{highlightSearchSubstring(item.email || '-', tenantUsersSearchTerm)}</span>
                              <span className={`tenant-user-status-badge tenant-users-mobile-status is-${statusMeta.key}`} data-i18n-live="1">{highlightSearchSubstring(statusMeta.label, tenantUsersSearchTerm)}</span>
                            </button>
                            {isExpanded && (
                              <div className="tenant-users-mobile-details" id={detailsId}>
                                {isEditing ? (
                                  <div className="tenant-users-mobile-edit">
                                    <input
                                      className="tenant-user-edit-input"
                                      type="text"
                                      value={editTenantUser.name}
                                      onChange={(e) => setEditTenantUser((prev) => ({ ...prev, name: e.target.value }))}
                                    />
                                    <input
                                      className="tenant-user-edit-input tenant-user-edit-input-readonly"
                                      type="email"
                                      value={editTenantUser.email}
                                      readOnly
                                      disabled
                                    />
                                    <div className="tenant-users-mobile-actions">
                                      <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={handleSaveTenantUserEdit}>Speichern</button>
                                      <button
                                        className="secondary sb-btn sb-btn-secondary"
                                        type="button"
                                        onClick={() => setEditTenantUser({ userId: '', name: '', email: '', rights: TENANT_USER_RIGHTS_DEFAULT })}
                                      >
                                        Abbrechen
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="tenant-users-mobile-actions">
                                    <button
                                      className="secondary sb-btn sb-btn-secondary"
                                      type="button"
                                      onClick={() => setEditTenantUser({ userId: item.user_id, name: item.name || '', email: item.email || '', rights: normalizeTenantUserRights(item.rights) })}
                                    >
                                      Editieren
                                    </button>
                                    <button
                                      className="secondary sb-btn sb-btn-secondary"
                                      type="button"
                                      onClick={() => openTenantUserRightsDialog('edit', { userId: item.user_id, label: item.email || item.name || 'Nutzer', rights: item.rights })}
                                    >
                                      Rechte
                                    </button>
                                    <button
                                      className="secondary sb-btn sb-btn-secondary"
                                      type="button"
                                      onClick={() => handleToggleTenantUserLock(item.user_id, item.email || item.name || 'Nutzer', item.status === 'locked')}
                                    >
                                      {item.status === 'locked' ? 'Entsperren' : 'Sperren'}
                                    </button>
                                    <button
                                      className="secondary sb-btn sb-btn-secondary"
                                      type="button"
                                      onClick={() => handleDeleteTenantUser(item.user_id, item.email || item.name || 'Nutzer')}
                                    >
                                      Löschen
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {!tenantUsers.length && <div className="hint">Keine Nutzer vorhanden.</div>}
                    {tenantUsers.length > 0 && !filteredTenantUsers.length && <div className="hint">Keine passenden Nutzer gefunden.</div>}
                  </div>
                  <div className="tenant-user-actions">
                    <button
                      className="secondary sb-btn sb-btn-secondary tenant-user-toggle-btn"
                      type="button"
                      onClick={() => setShowTenantUserCreate((prev) => !prev)}
                    >
                      {showTenantUserCreate ? 'Anlegen ausblenden' : 'Nutzer anlegen'}
                    </button>
                    {showTenantUserCreate && (
                      <div className="tenant-create-wrap">
                        <div className="form-grid">
                          <div>
                            <label>Name</label>
                            <input
                              type="text"
                              placeholder="Vorname Nachname"
                              value={tenantUserName}
                              onChange={(e) => setTenantUserName(e.target.value)}
                            />
                          </div>
                          <div>
                            <label>E-Mail</label>
                            <input
                              type="email"
                              placeholder="user@tenant.tld"
                              value={tenantUserEmail}
                              onChange={(e) => setTenantUserEmail(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="tenant-user-create-actions">
                          <button className="secondary tenant-user-create-submit sb-btn sb-btn-secondary" type="button" onClick={handleCreateTenantUser}>Anlegen</button>
                          <button
                            className="secondary sb-btn sb-btn-secondary"
                            type="button"
                            onClick={() => openTenantUserRightsDialog('create', { rights: tenantUserRights })}
                          >
                            Rechte bearbeiten
                          </button>
                        </div>
                      </div>
                    )}
                    {tenantUserError && <div className="hint">{tenantUserError}</div>}
                    {tenantUserSuccess && <div className="hint">{tenantUserSuccess}</div>}
                  </div>
                  {rightsDialog.open && (
                    <div className="dialog-backdrop" role="presentation" onClick={closeTenantUserRightsDialog}>
                      <div className="dialog" role="dialog" aria-modal="true" aria-label="Nutzerrechte" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-title">Nutzerrechte</div>
                        {rightsDialog.mode === 'edit' && (
                          <div className="hint">Nutzer: {rightsDialog.label || '-'}</div>
                        )}
                        <div className="dialog-rights-list">
                          {TENANT_USER_RIGHTS_OPTIONS.map((opt) => (
                            <label key={opt.key} className="multi-select-option">
                              <input
                                type="checkbox"
                                checked={Boolean(rightsDraft[opt.key])}
                                onChange={() => toggleTenantUserRight(opt.key)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="dialog-actions">
                          <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={saveTenantUserRightsDialog}>
                            Speichern
                          </button>
                          <button className="secondary sb-btn sb-btn-secondary" type="button" onClick={closeTenantUserRightsDialog}>
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </div>
                )}
              {!isStructureNavigation && canManageImpersonation && (
              <div className="card tenant-user-panel tenant-impersonation-panel">
                <div className="card-title">{uiLiteral('Vollzugriff')}</div>
                <button
                  className="secondary sb-btn sb-btn-secondary admin-compact-toggle"
                  type="button"
                  onClick={() => setShowTenantFullAccessPanel((prev) => !prev)}
                >
                  {showTenantFullAccessPanel
                    ? uiLiteral('Vollzugriff ausblenden')
                    : uiLiteral('Vollzugriff öffnen')}
                </button>
                {showTenantFullAccessPanel && (
                <>
                <div className="archive-search">
                  <input
                    type="search"
                    placeholder="Suche (Name, E-Mail)"
                    value={tenantImpersonationSearchTerm}
                    onChange={(e) => setTenantImpersonationSearchTerm(e.target.value)}
                  />
                </div>
                <div className="tenant-user-content">
                  <div className={`table tenant-user-table tenant-impersonation-table${filteredTenantImpersonationPermissions.length ? '' : ' is-empty'}`}>
                    <div className="table-header">
                      <span>Name</span><span>E-Mail</span><span>Status</span><span>Aktionen</span>
                    </div>
                    {filteredTenantImpersonationPermissions.map((item) => {
                      const meta = getImpersonationStatusMeta(item.status)
                      const status = String(item.status || '').toLowerCase()
                      return (
                        <div className="table-row" key={item.permission_id}>
                          <span>{highlightSearchSubstring(item.helper_name || '-', tenantImpersonationSearchTerm)}</span>
                          <span>{highlightSearchSubstring(item.helper_email || '-', tenantImpersonationSearchTerm)}</span>
                          <span>
                            <span className={`impersonation-status-badge is-${meta.key}`} title={meta.title}>{meta.label}</span>
                          </span>
                          <span className="tenant-user-row-actions tenant-impersonation-row-actions">
                            {status === 'requested' && (
                              <button
                                className="secondary sb-btn sb-btn-secondary"
                                type="button"
                                onClick={() => handleUpdateTenantImpersonationPermissionStatus(item.permission_id, 'approved')}
                              >
                                Genehmigen
                              </button>
                            )}
                            {status === 'approved' && (
                              <button
                                className="secondary sb-btn sb-btn-secondary"
                                type="button"
                                onClick={() => handleUpdateTenantImpersonationPermissionStatus(item.permission_id, 'revoked', item.helper_name || item.helper_email)}
                              >
                                Widerrufen
                              </button>
                            )}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  <div className="tenant-impersonation-mobile-list" role="list">
                    {filteredTenantImpersonationPermissions.map((item) => {
                      const meta = getImpersonationStatusMeta(item.status)
                      const status = String(item.status || '').toLowerCase()
                      const isExpanded = expandedTenantImpersonationPermissionId === item.permission_id
                      const detailsId = `tenant-impersonation-details-${item.permission_id}`
                      return (
                        <div className="tenant-impersonation-mobile-item" key={`mobile-${item.permission_id}`} role="listitem">
                          <button
                            className="tenant-impersonation-mobile-summary"
                            type="button"
                            onClick={() => setExpandedTenantImpersonationPermissionId((prev) => (prev === item.permission_id ? '' : item.permission_id))}
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                          >
                            <span className={`tenant-impersonation-mobile-chevron${isExpanded ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                            <span className="tenant-impersonation-mobile-name">{highlightSearchSubstring(item.helper_name || '-', tenantImpersonationSearchTerm)}</span>
                            <span className="tenant-impersonation-mobile-email">{highlightSearchSubstring(item.helper_email || '-', tenantImpersonationSearchTerm)}</span>
                            <span className={`impersonation-status-badge tenant-impersonation-mobile-status is-${meta.key}`} title={meta.title}>{meta.label}</span>
                          </button>
                          {isExpanded && (
                            <div className="tenant-impersonation-mobile-details" id={detailsId}>
                              <div className="tenant-impersonation-mobile-meta">
                                <span className="tenant-impersonation-mobile-label">Impersonation</span>
                                <span className={`impersonation-status-badge is-${meta.key}`} title={meta.title}>{meta.label}</span>
                              </div>
                              <div className="tenant-impersonation-mobile-actions">
                                {status === 'requested' && (
                                  <button
                                    className="secondary sb-btn sb-btn-secondary impersonation-mobile-action-btn"
                                    type="button"
                                    onClick={() => handleUpdateTenantImpersonationPermissionStatus(item.permission_id, 'approved')}
                                  >
                                    Genehmigen
                                  </button>
                                )}
                                {status === 'approved' && (
                                  <button
                                    className="secondary sb-btn sb-btn-secondary impersonation-mobile-action-btn"
                                    type="button"
                                    onClick={() => handleUpdateTenantImpersonationPermissionStatus(item.permission_id, 'revoked', item.helper_name || item.helper_email)}
                                  >
                                    Widerrufen
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {!tenantImpersonationPermissions.length && <div className="hint">Keine Helfer vorhanden.</div>}
                  {tenantImpersonationPermissions.length > 0 && !filteredTenantImpersonationPermissions.length && <div className="hint">Keine passenden Helfer gefunden.</div>}
                </div>
                <div className="tenant-user-actions">
                  <button
                    className="secondary sb-btn sb-btn-secondary tenant-user-toggle-btn"
                    type="button"
                    onClick={() => setShowTenantImpersonationCreate((prev) => !prev)}
                  >
                    {showTenantImpersonationCreate ? 'Dialog schließen' : 'Anfrage stellen'}
                  </button>
                  {showTenantImpersonationCreate && (
                    <div className="tenant-create-wrap">
                      <div className="form-grid">
                        <div>
                          <label>Helfer per E-Mail hinzufügen</label>
                          <input
                            type="email"
                            placeholder="user@example.tld"
                            value={tenantImpersonationEmail}
                            onChange={(e) => setTenantImpersonationEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      <button
                        className="secondary tenant-user-create-submit sb-btn sb-btn-secondary"
                        type="button"
                        onClick={handleCreateTenantImpersonationPermission}
                      >
                        Anfrage erstellen
                      </button>
                    </div>
                  )}
                  {tenantImpersonationError && <div className="hint">{tenantImpersonationError}</div>}
                </div>
                </>
                )}
              </div>
              )}
              </section>
              </>
              )}
            </>
          )}
        </main>
      </div>
      <LegalBottomBar showTerms appVersion={appVersion} />
    </div>
  )
}

export default App
